import { useState, useMemo, useEffect, useRef, Fragment } from 'react';
import QRCode from 'qrcode';
import { useDevices, useDeviceAction, useToast, useStats } from './hooks/useNanoMDM';
import { query, auth } from './api';
import DeviceModal from './components/DeviceModal';
import NotifyModal from './components/NotifyModal';
import DevicePhoto from './components/DevicePhoto';
import Toast from './components/Toast';
import { DialogHost, confirmVN, promptVN } from './components/Dialog';
import LedgerEvidence, { LedgerTxnModal, PendingEvidence, uploadPending, EVIDENCE_CSS } from './components/LedgerEvidence';
import LedgerChart, { LEDGER_CHART_CSS } from './components/LedgerChart';
import { dmy, dmyTime } from './dates';
import { calcMonthly, totalWithInterest, interestAmount } from './lib/installment';
import { weekdayLabel } from './lib/rental';
import { passwordErr, PASSWORD_HINT, MIN_PASSWORD_LEN } from './lib/password';
import { appleModelName } from './lib/appleModels';
import { LEGAL_GROUPS, legalGroupFields, legalForm, legalPayload, legalMissing } from './lib/legalParty';

// Ô ngày cho bộ lọc: HIỂN THỊ dd/mm/yyyy (input date thật trong suốt đè lên), CLICK
// bất kỳ đâu mở lịch (showPicker). value/onChange dùng ISO 'yyyy-mm-dd'.
function FilterDate({ value, onChange, min, max }) {
  const ref = useRef(null);
  const open = () => { try { ref.current?.showPicker(); } catch { ref.current?.focus(); } };
  return (
    <div className="fdate" onClick={open} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}>
      {/* Ô HIỂN THỊ là input TEXT readOnly -> chỉ vẽ đúng chữ mình đưa, KHÔNG có UI date của trình duyệt */}
      <input className="fdate-disp" type="text" readOnly placeholder="dd/mm/yyyy"
        value={value ? dmy(value) : ''} tabIndex={-1} />
      <svg className="fdate-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
      </svg>
      {/* input date THẬT: ẩn hẳn (1px), chỉ để mở lịch (showPicker) + giữ giá trị */}
      <input ref={ref} className="fdate-native" type="date" value={value || ''} min={min || undefined} max={max || undefined}
        tabIndex={-1} aria-hidden="true" onChange={e => onChange(e.target.value)} />
    </div>
  );
}

const ROLE_LABEL = { admin: 'Quản trị viên', operator: 'Điều hành', manager: 'Quản lý cửa hàng', staff: 'Nhân viên', kho: 'Kho', customer: 'Khách hàng' };
// Màu ticket cho từng vai trò (dễ phân biệt trong danh sách).
const ROLE_BADGE = {
  admin:    { bg: 'var(--red-bg)',    fg: 'var(--red)' },
  operator: { bg: 'var(--blue-bg)',   fg: 'var(--blue)' },
  manager:  { bg: 'var(--green-bg)',  fg: 'var(--green)' },
  staff:    { bg: 'var(--bg4)',       fg: 'var(--text2)' },
  kho:      { bg: 'var(--amber-bg)',  fg: 'var(--amber)' },
};
function RoleBadge({ role }) {
  const b = ROLE_BADGE[role] || { bg: 'var(--bg4)', fg: 'var(--text2)' };
  return <span className="badge" style={{ background: b.bg, color: b.fg, whiteSpace: 'nowrap' }}>{ROLE_LABEL[role] || role}</span>;
}
// "Điều hành" trở lên (rộng hơn manager: báo cáo/nhật ký/quản lý user…)
const isOpsRole = (role) => role === 'admin' || role === 'operator';
// Được thao tác/điều khiển thiết bị (admin+operator+manager)
// Hợp đồng thuê dùng CHUNG cột tiền với trả góp, nên nhãn phải đổi theo hình thức:
//  - trả góp        -> "/tháng", "Ngày 15"
//  - thuê theo tháng -> "/kỳ",    "Ngày 15"
//  - thuê theo tuần  -> "/kỳ",    "Thứ 4"   (installment_day lúc này là THỨ, không phải ngày)
const isRental = (d) => d.contract_mode === 'rental';
const perLabel = (d) => (isRental(d) ? '/kỳ' : '/tháng');
const payDayLabel = (d) => {
  if (!d.installment_day) return null;
  if (isRental(d) && d.rent_unit === 'week') return weekdayLabel(d.installment_day);
  return `Ngày ${d.installment_day}`;
};

const canManageRole = (role) => role === 'admin' || role === 'operator' || role === 'manager';

// storeChoicesFor: danh sách cửa hàng/kho mà user được chọn — CÙNG RULE với form thêm
// người dùng. Phạm vi theo vai trò user: admin=tất cả; điều hành=nhóm của mình; còn lại=cửa
// hàng mình. `want`: 'store' = chỉ cửa hàng bán (ẩn kho), 'kho' = chỉ kho, 'all' = cả hai.
// Dùng cho MỌI dropdown search/lọc theo cửa hàng để load danh sách nhất quán.
function storeChoicesFor(me, locations = [], want = 'all') {
  const base = me?.role === 'admin' ? locations
    : locations.filter(l => me?.role === 'operator' ? (me.location_ids || []).includes(l.id) : l.id === me?.location_id);
  if (want === 'store') return base.filter(l => !l.is_warehouse);
  if (want === 'kho') return base.filter(l => l.is_warehouse);
  return base;
}

// KHÁCH HÀNG (thuê bao) CỦA MỘT TÀI KHOẢN — suy từ cửa hàng được gán, KHÔNG lưu riêng
// ở app_users. Cửa hàng mới là nơi giữ liên kết (locations.customer_id), nên nhân viên
// đổi cửa hàng là quan hệ này tự đúng theo, không có cột thứ hai để lệch.
//
// Điều hành ôm được nhiều cửa hàng nên có thể ra NHIỀU khách — trả về mảng chứ không
// phải một giá trị, đúng như mô hình cho phép.
function customersOfUser(u, locations = []) {
  const ids = u?.role === 'operator'
    ? (u.location_ids || [])
    : (u?.location_id != null && u.location_id !== '' ? [Number(u.location_id)] : []);
  const names = [];
  for (const id of ids) {
    const name = (locations.find(l => l.id === id) || {}).customer_name;
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}

// customerLabel: chuỗi gọn cho bảng/thẻ. Nhiều khách thì rút lại, tooltip xem đủ.
const customerLabel = (names) =>
  !names.length ? '—' : names.length === 1 ? names[0] : `${names[0]} +${names.length - 1} khách nữa`;

// groupStoresByCustomer: gom cửa hàng theo khách hàng để form người dùng nhìn là biết
// tài khoản đang phục vụ công ty nào. Cửa hàng chưa gán khách xếp cuối.
function groupStoresByCustomer(stores = []) {
  const groups = new Map();
  for (const l of stores) {
    const key = l.customer_name || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(l);
  }
  const named = [...groups.entries()].filter(([k]) => k).sort((a, b) => a[0].localeCompare(b[0], 'vi'));
  const orphan = groups.get('') || [];
  return orphan.length ? [...named, ['', orphan]] : named;
}

// ── Cổng xác thực: chưa đăng nhập -> Login; đã đăng nhập -> Shell ──────────────
export default function App() {
  const [session, setSession] = useState(undefined); // undefined=đang tải, null=chưa login
  useEffect(() => { auth.me().then(setSession).catch(() => setSession(null)); }, []);

  if (session === undefined) {
    return <div className="splash">Đang tải…<style>{GLOBAL_CSS}</style></div>;
  }
  if (!session) return <Login onLogin={setSession} />;
  // Bị cấp trên yêu cầu 2FA nhưng chưa bật -> buộc thiết lập trước khi vào app.
  if (session.user.totp_required && !session.user.totp_enabled) {
    return <ForceTOTP
      onDone={() => auth.me().then(setSession)}
      onLogout={async () => { try { await auth.logout(); } catch { /* */ } setSession(null); }} />;
  }
  return <Shell session={session} setSession={setSession} onLogout={() => setSession(null)} />;
}

// Màn hình BẮT BUỘC thiết lập 2FA (khi cấp trên yêu cầu). Không cho bỏ qua.
function ForceTOTP({ onDone, onLogout }) {
  const [setup, setSetup] = useState(null);
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');

  async function start() {
    setErr('');
    try {
      const s = await auth.totpSetup();
      setSetup(s);
      setQr(await QRCode.toDataURL(s.otpauth_uri, { margin: 1, width: 200 }));
    } catch (ex) { setErr(ex.message); }
  }
  useEffect(() => { start(); }, []);

  async function enable() {
    setErr('');
    try { await auth.totpEnable(code.trim()); onDone(); }
    catch (ex) { setErr((ex.body && ex.body.error) || 'Mã không đúng'); }
  }

  return (
    <div className="login-wrap">
      <div className="login-card" style={{ width: 360 }}>
        <div className="login-title">Bắt buộc bật 2FA</div>
        <div className="login-sub">Quản trị viên yêu cầu tài khoản này dùng xác thực 2 lớp. Vui lòng thiết lập để tiếp tục.</div>
        {qr && <img src={qr} alt="QR" style={{ background: '#fff', padding: 8, borderRadius: 8, alignSelf: 'center' }} />}
        {setup && (
          <div className="info-cell">
            <div className="info-cell-label">Khoá thủ công</div>
            <div className="info-cell-val mono" style={{ wordBreak: 'break-all' }}>{setup.secret}</div>
          </div>
        )}
        <label className="fld"><span>Nhập mã 6 số từ Google Authenticator</span>
          <input inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} /></label>
        {err && <div className="login-err">{err}</div>}
        <button className="btn primary login-btn" onClick={enable}>Xác nhận & Bật</button>
        <button className="btn" onClick={onLogout} style={{ justifyContent: 'center' }}>Đăng xuất</button>
      </div>
      <style>{GLOBAL_CSS}</style>
    </div>
  );
}

function navFor(role, me) {
  // KHÁCH HÀNG (cổng khách portal.zentratech.vn): CHỈ máy của họ + máy tính kỳ hạn.
  // Mọi mục quản lý/tài chính/hệ thống của ZENTRA KHÔNG hiện (backend portal cũng
  // không có route tương ứng).
  //
  // Gói CHUỖI (chuoi_300 / chuoi_vo_han / doanh_nghiep) mở thêm mục quản trị nội bộ
  // chuỗi. Cờ do BACKEND quyết định (/auth/me trả chain_mgmt + can_manage), FE chỉ
  // hiện/ẩn — giấu menu không phải là bảo vệ, mọi route đều tự chặn lại theo gói.
  if (role === 'customer') {
    const items = [
      { icon: 'device', label: 'Thiết bị' },
      { icon: 'calc', label: 'Máy tính kỳ hạn' },
      // Mọi vai trò và MọI GÓI đều thấy — khách gói 1 cửa hàng càng cần biết ngày hết hạn.
      // Backend tự cắt bớt khối liên hệ/pháp nhân với cấp dưới chủ chuỗi.
      { icon: 'customer', label: 'Doanh nghiệp' },
    ];
    const nav = [{ section: 'Cửa hàng của bạn', items }];
    if (me?.chain_mgmt) {
      const chain = [];
      if (me.can_manage) chain.push({ icon: 'users', label: 'Nhân viên' });
      if (me.can_manage) chain.push({ icon: 'report', label: 'Báo cáo chuỗi' });
      chain.push({ icon: 'log', label: 'Nhật ký' });
      nav.push({ section: 'Quản lý chuỗi', items: chain });
    }
    return nav;
  }
  // NV kho: tối giản — chỉ xem tồn kho + tài khoản.
  if (role === 'kho') {
    return [
      { section: 'Kho', items: [{ icon: 'box', label: 'Kho' }] },
      { section: 'Hệ thống', items: [{ icon: 'account', label: 'Tài khoản' }] },
    ];
  }
  const nav = [{ section: 'Tổng quan', items: [
    { icon: 'device', label: 'Thiết bị' },
    { icon: 'calc', label: 'Máy tính kỳ hạn' }, // ai cũng dùng được
  ] }];
  const manage = [];
  if (canManageRole(role)) manage.push({ icon: 'box', label: 'Kho' }); // xem tồn kho + kéo về cửa hàng
  // "Lịch sử máy": manager+ xem được, TRỪ tài khoản bị admin khoá (no_device_history).
  // Ẩn menu chỉ là giao diện — backend vẫn chặn GET /devices?finalized=1 (403).
  if (canManageRole(role) && !me?.no_device_history) manage.push({ icon: 'archive', label: 'Lịch sử máy' }); // máy đã gỡ MDM + hoàn tất
  if (canManageRole(role)) manage.push({ icon: 'report', label: 'Báo cáo' });
  if (canManageRole(role)) manage.push({ icon: 'log', label: 'Nhật ký' });
  if (canManageRole(role)) manage.push({ icon: 'users', label: 'Người dùng' });
  if (role === 'admin') manage.push({ icon: 'store', label: 'Cửa hàng' });
  if (role === 'admin') manage.push({ icon: 'customer', label: 'Khách hàng' });
  if (role === 'admin') manage.push({ icon: 'ctv', label: 'Cộng tác viên' });
  if (role === 'admin') manage.push({ icon: 'ledger', label: 'Sổ thu/chi' });
  if (manage.length) nav.push({ section: 'Quản lý', items: manage });
  const sys = [{ icon: 'account', label: 'Tài khoản' }];
  // Tài khoản quét ABM đứng ngay dưới màn ABM: hai thứ luôn dùng cùng một lúc (mở tài
  // khoản → quét máy → xem máy về ABM → tắt tài khoản).
  if (role === 'admin') sys.unshift({ icon: 'key', label: 'Tài khoản quét ABM' });
  if (role === 'admin') sys.unshift({ icon: 'abm', label: 'Máy từ Apple (ABM)' });
  // "Chứng chỉ APNs" + "Log hệ thống" KHÔNG hiện ngoài menu — truy cập qua URL riêng
  // có mật khẩu (basic_auth): /certs/ (chứng chỉ) và /logs/ (log hệ thống).
  nav.push({ section: 'Hệ thống', items: sys });
  return nav;
}

// Icon SVG nét mảnh cho menu (viewBox 24, stroke currentColor).
function NavIcon({ name }) {
  const p = {
    device:  <><rect x="6" y="2" width="12" height="20" rx="3"/><line x1="10" y1="18" x2="14" y2="18"/></>,
    users:   <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    store:   <><path d="M3 7l1.6-3h14.8L21 7"/><path d="M4 7v13h16V7"/><path d="M3 7h18"/><path d="M9 20v-5h6v5"/></>,
    customer: <><path d="M3 21V8l9-5 9 5v13"/><path d="M3 21h18"/><path d="M9 21v-6h6v6"/><path d="M9 11h.01"/><path d="M15 11h.01"/></>,
    ledger: <><rect x='3' y='4' width='18' height='16' rx='2'/><path d='M3 9h18'/><path d='M8 13h5'/><path d='M8 16h8'/></>,
    ctv: <><circle cx="9" cy="8" r="3.2"/><path d="M3.5 20a5.5 5.5 0 0 1 11 0"/><path d="M17 4.5l1 2 2.2.3-1.6 1.5.4 2.2-2-1-2 1 .4-2.2L14 6.8l2.2-.3z"/></>,
    cert:    <><path d="M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z"/><path d="M9 12l2 2 4-4"/></>,
    key:     <><circle cx="7.5" cy="15.5" r="4"/><path d="M10.4 12.6 21 2"/><path d="m16 6 3 3"/><path d="m13.5 8.5 3 3"/></>,
    account: <><circle cx="12" cy="7.5" r="4"/><path d="M5 21a7 7 0 0 1 14 0"/></>,
    report:  <><path d="M3 3v18h18"/><rect x="7" y="11" width="3" height="6"/><rect x="12" y="7" width="3" height="10"/><rect x="17" y="13" width="3" height="4"/></>,
    log:     <><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="3.5" cy="6" r="1"/><circle cx="3.5" cy="12" r="1"/><circle cx="3.5" cy="18" r="1"/></>,
    terminal: <><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 9l3 3-3 3"/><line x1="12" y1="15" x2="16" y2="15"/></>,
    abm: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.27 6.96 12 12l8.73-5.04"/><line x1="12" y1="22" x2="12" y2="12"/></>,
    calc: <><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><path d="M8 11h2M14 11h2M8 15h2M14 15h2M8 19h2M14 19h2"/></>,
    box: <><path d="M21 8V16a2 2 0 0 1-1 1.73l-7 4a2 2 0 0 1-2 0l-7-4A2 2 0 0 1 3 16V8"/><path d="M3.3 7 12 12l8.7-5"/><path d="M12 22V12"/><path d="m7.5 4.3 9 5.2"/></>,
    archive: <><rect x="3" y="4" width="18" height="4" rx="1"/><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8"/><path d="M10 12h4"/></>,
  }[name];
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">{p}</svg>;
}

function Shell({ session, setSession, onLogout }) {
  const role = session.user.role;
  const isCustomer = role === 'customer';   // cổng khách (portal.zentratech.vn)
  const canManage = canManageRole(role);
  const NAV = navFor(role, session.user);

  const { devices, loading, error, lastUpdated, reload, updateDevice, removeDevice } = useDevices();
  const { run, pending } = useDeviceAction(updateDevice, removeDevice, isCustomer);
  const { toast, show: showToast } = useToast();

  // Hồ sơ doanh nghiệp của khách: nạp MỘT LẦN ở đây vì dùng ở hai nơi — dải chỉ số trên
  // topbar (mọi trang) và màn Doanh nghiệp. Để mỗi nơi tự gọi thì vừa thừa request vừa có
  // lúc hai chỗ hiện số khác nhau.
  const [company, setCompany] = useState(null);
  const [companyErr, setCompanyErr] = useState(null);
  useEffect(() => {
    if (!isCustomer) { setCompany(null); return; }
    query.getCompany().then(c => { setCompany(c); setCompanyErr(null); })
      .catch(e => setCompanyErr((e.body && e.body.error) || e.message));
  }, [isCustomer]);

  // Tính một lần cho dải chỉ số; null khi chưa nạp xong (dải cũng chưa vẽ).
  const companyLeft = company ? subDaysLeft(company.days_left) : null;

  const [locations, setLocations] = useState([]);
  // Cổng khách: backend portal KHÔNG có /locations -> bỏ qua (tên cửa hàng đã có sẵn
  // trong từng device row). Tránh 404 thừa.
  //
  // Gói CHUỖI là ngoại lệ: cần danh sách cửa hàng để chuyển máy giữa các cửa hàng.
  // Lấy từ /stores của cổng khách (chỉ đọc, đã kẹp customer_id), KHÔNG phải /locations.
  useEffect(() => {
    if (isCustomer) {
      if (session.user?.chain_mgmt) query.listChainStores().then(setLocations).catch(() => {});
      return;
    }
    query.listLocations().then(setLocations).catch(() => {});
  }, [isCustomer, session.user?.chain_mgmt]);

  // Số máy đang trong kho -> badge menu "Kho". Chỉ role thấy được kho mới nạp; refetch
  // theo `devices` (kéo về / trả về kho làm reload danh sách chính -> cập nhật badge).
  const [khoCount, setKhoCount] = useState(0);
  const [finalizedCount, setFinalizedCount] = useState(0);

  const [search, setSearch]         = useState('');
  const [filter, setFilter]         = useState('all');
  // Bộ lọc nâng cao (client-side): khoảng ngày enroll, người tạo, cửa hàng, khách hàng.
  const [fromDate, setFromDate]     = useState('');
  const [toDate, setToDate]         = useState('');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [storeFilter, setStoreFilter]     = useState('');
  const [custSearch, setCustSearch]        = useState('');
  const [showAdvanced, setShowAdvanced]    = useState(false); // ẩn/hiện bộ lọc nâng cao (phễu)
  // Tổng quan (4 block + tình trạng đội): thu gọn được; mobile mặc định GỌN.
  const [summaryOpen, setSummaryOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth > 768);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [notifyAllOpen, setNotifyAllOpen] = useState(false); // modal gửi thông báo hàng loạt
  // URL riêng /certs/ (sau basic_auth Caddy) -> mở thẳng trang Chứng chỉ APNs (ẩn khỏi menu).
  const atCertsURL = typeof window !== 'undefined' && window.location.pathname.startsWith('/certs');
  const [activeNav, setActiveNav]   = useState(atCertsURL ? 'Chứng chỉ APNs' : role === 'kho' ? 'Kho' : 'Thiết bị');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);

  async function doLogout() {
    try { await auth.logout(); } catch { /* ignore */ }
    onLogout();
  }

  // "DD/MM/YYYY HH:MM" (created_at = ngày enroll) -> "YYYY-MM-DD" để so khoảng ngày.
  const createdToYMD = (s) => {
    const m = String(s || '').match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
  };
  // Danh sách "người tạo" (enrolled_by) có trong dữ liệu -> đổ vào dropdown.
  const creators = useMemo(
    () => [...new Set(devices.map(d => d.enrolled_by).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [devices]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const cq = custSearch.trim().toLowerCase();
    return devices.filter(d => {
      const matchSearch = !q || [d.name, d.serial, d.udid, d.location_name, d.model]
        .some(v => (v || '').toLowerCase().includes(q));
      const matchFilter = filter === 'all' || d.status === filter;
      const matchCust = !cq || [d.owner_name, d.owner_phone].some(v => (v || '').toLowerCase().includes(cq));
      const matchCreator = !creatorFilter || (d.enrolled_by || '') === creatorFilter;
      const matchStore = !storeFilter || String(d.location_id ?? '') === storeFilter;
      const ymd = createdToYMD(d.created_at);
      const matchFrom = !fromDate || (ymd && ymd >= fromDate);
      const matchTo = !toDate || (ymd && ymd <= toDate);
      return matchSearch && matchFilter && matchCust && matchCreator && matchStore && matchFrom && matchTo;
    });
  }, [devices, search, filter, custSearch, creatorFilter, storeFilter, fromDate, toDate]);

  // Giữ thiết bị đang MỞ đồng bộ với danh sách mới nhất: sau khi gán người cài đặt /
  // cửa hàng… (gọi reload), modal phải hiện dữ liệu mới thay vì object cũ đã chụp lúc mở.
  useEffect(() => {
    if (!selectedDevice) return;
    const fresh = devices.find(x => x.udid === selectedDevice.udid);
    if (fresh && fresh !== selectedDevice) setSelectedDevice(fresh);
  }, [devices, selectedDevice]);

  // Đếm máy trong kho cho badge menu (chỉ role thấy kho); refetch khi danh sách chính reload.
  useEffect(() => {
    if (role !== 'kho' && !canManageRole(role)) { setKhoCount(0); return; }
    query.getWarehouse().then(list => setKhoCount(Array.isArray(list) ? list.length : 0)).catch(() => {});
  }, [role, devices]);

  // Đếm máy trong Lịch sử máy đã quản lý cho badge menu (manager+); refetch khi danh sách chính reload.
  useEffect(() => {
    if (!canManageRole(role) || session.user?.no_device_history) { setFinalizedCount(0); return; }
    query.getFinalizedDevices().then(list => setFinalizedCount(Array.isArray(list) ? list.length : 0)).catch(() => {});
  }, [role, devices]);

  const stats = useStats(devices);
  const isDashboard = activeNav === 'Thiết bị';
  const overdueDevices = devices.filter(d => d.due_status === 'overdue');
  // Cảnh báo thiết bị: đã gỡ MDM (enabled=false = CheckOut) + mất tín hiệu (còn quản lý nhưng lâu không báo).
  const removedDevices = devices.filter(d => !d.enabled);
  const staleDevices = devices.filter(d => d.enabled && staleDaysOf(d.last_seen) >= STALE_DAYS);
  // Máy còn trong ABM nhưng đã gỡ hẳn MDM (biến mất khỏi enrollment) — chỉ admin, chống thất thoát máy supervised.
  const [abmOrphans, setAbmOrphans] = useState([]);
  useEffect(() => {
    if (session.user.role !== 'admin') return;
    let alive = true;
    query.getAbmOrphans().then(o => { if (alive) setAbmOrphans(o || []); }).catch(() => {});
    return () => { alive = false; };
  }, [session.user.role, devices.length]);
  const alertCount = overdueDevices.length + removedDevices.length + staleDevices.length + abmOrphans.length;

  async function handleAction(action, udid, opts = {}) {
    // Lệnh MDM là BẤT ĐỒNG BỘ + qua APNs (APNs vẫn nhận push cho máy offline rồi
    // gửi khi máy online). Không thể biết chắc máy đã thực thi lúc bấm → nhãn dùng
    // "Đã gửi lệnh …" (đã xếp), KHÔNG dùng "Đã xong". Máy áp dụng khi online/nhận lệnh.
    const actionLabels = {
      checkin: ['Đã đánh thức máy — chờ máy báo về', '🔄'],
      lock:    ['Đã gửi lệnh khoá — máy nhận khi có mạng', '🔒'],
      unlock:  ['Đã gửi lệnh mở khoá — máy nhận khi có mạng', '🔓'],
      restart: ['Đã gửi lệnh khởi động lại — chạy khi máy có mạng', '🔄'],
      shutdown:['Đã gửi lệnh tắt nguồn — chạy khi máy có mạng', '⏻'],
      sound:   ['Đã gửi lệnh phát chuông tìm máy', '🔊'],
      os_update: ['Đã lên lịch cập nhật iOS', '📲'],
      install_profile: ['Đã gửi lệnh cài cấu hình', '📋'],
      remove_profile:  ['Đã gửi lệnh gỡ cấu hình', '📋'],
      install_app:     ['Đã gửi lệnh cài ứng dụng', '📦'],
      remove_app:      ['Đã gửi lệnh gỡ ứng dụng', '🗑'],
      rename:          ['Đã gửi lệnh đổi tên — áp dụng khi máy có mạng', '✏️'],
      lost_mode_on:    ['Đã gửi lệnh khoá điện thoại', '📍'],
      lost_mode_off:   ['Đã gửi lệnh mở khoá điện thoại', '✅'],
      locate:          ['Đã gửi lệnh định vị — chờ máy báo về', '📍'],
    };
    const result = await run(action, udid, opts);
    const [msg, icon] = actionLabels[action] || ['Đã gửi lệnh', '✅'];
    if (result.ok) {
      if (result.warn) showToast(result.warn, 'info', '⏳');
      else showToast(msg, 'success', icon);
    } else {
      showToast(`Lỗi: ${result.error}`, 'error', '❌');
    }
    return result;
  }

  return (
    <div className="app">
      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <div className="logo-mark"><img src="/logo.svg" alt="Zentra" /></div>
          <div>
            <div className="logo-name">{isCustomer ? 'Zentra' : 'Zentra MDM'}</div>
            <div className="logo-url">{isCustomer ? 'Cổng khách hàng' : 'mdm.zentratech.vn'}</div>
          </div>
        </div>
        <nav className="sidebar-nav">
          {NAV.map(group => (
            <div key={group.section}>
              <div className="nav-section">{group.section}</div>
              {group.items.map(item => item.href ? (
                <a
                  key={item.label}
                  className="nav-item"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setSidebarOpen(false)}
                >
                  <NavIcon name={item.icon} /> <span style={{ flex: 1 }}>{item.label}</span>
                  <span className="nav-ext">↗</span>
                </a>
              ) : (
                <div
                  key={item.label}
                  className={`nav-item${activeNav === item.label ? ' active' : ''}`}
                  onClick={() => { setActiveNav(item.label); setSidebarOpen(false); }}
                >
                  <NavIcon name={item.icon} /> <span style={{ flex: 1 }}>{item.label}</span>
                  {item.label === 'Thiết bị' && devices.length > 0 && <span className="nav-count">{devices.length}</span>}
                  {item.label === 'Kho' && khoCount > 0 && <span className="nav-count">{khoCount}</span>}
                  {item.label === 'Lịch sử máy' && finalizedCount > 0 && <span className="nav-count">{finalizedCount}</span>}
                </div>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-user">
          <div className="user-avatar">{session.user.username.slice(0, 2).toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="user-name">{session.user.username}</div>
            <div className="user-role">
              {ROLE_LABEL[role] || role}{session.location_name ? ` · ${session.location_name}` : ''}
            </div>
          </div>
          <button className="icon-btn" title="Đăng xuất" onClick={doLogout}>⎋</button>
        </div>
      </aside>

      {/* Backdrop khi mở drawer trên mobile */}
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <main className="main">
        {/* Topbar */}
        <div className="topbar">
          <button className="btn menu-btn" aria-label="Mở menu" onClick={() => setSidebarOpen(true)}>☰</button>
          <div style={{ minWidth: 0 }}>
            <div className="topbar-title">{isDashboard ? 'Bảng điều khiển' : activeNav}</div>
            {isDashboard && <div className="topbar-sub">Toàn bộ iPhone của {session.org_name || 'Zentra'}</div>}
          </div>

          {/* CỔNG KHÁCH: chỉ số thuê bao luôn hiện, không phải vào màn Doanh nghiệp mới biết
              còn bao nhiêu ngày. Backend trả những ô này cho MỌI vai trò cổng khách. */}
          {isCustomer && company && (
            <div className="topbar-chips">
              {/* HẾT HẠN: KHÔNG điều kiện nào cả — hiện với mọi gói, mọi vai trò, và hiện
                  ngay cả khi còn rất xa ngày hết hạn. Đứng đầu và không mang class tchip-opt
                  nên không bị nhường chỗ khi thanh chật. Màu chỉ đổi theo mức cấp bách
                  (subDaysLeft), KHÔNG quyết định ẩn/hiện. */}
              <span className="tchip" style={companyLeft.color ? { color: companyLeft.color, fontWeight: 600 } : undefined}>
                <b>Hết hạn</b>{company.expires_at || '—'}{' · '}{companyLeft.text}
              </span>
              {company.status !== 'active' && (
                <span className="tchip" style={{ color: 'var(--amber, #d97706)', fontWeight: 600 }}>
                  {SUB_STATUS[company.status] || company.status}
                </span>
              )}
              <span className="tchip tchip-opt"><b>Gói</b>{company.plan_label}</span>
              <span className="tchip tchip-opt"><b>Máy</b>{company.device_used}/{capText(company.device_cap)}</span>
              <span className="tchip tchip-opt"><b>Cửa hàng</b>{company.store_used}/{capText(company.store_cap)}</span>
            </div>
          )}

          <div className="bell-wrap" style={{ marginLeft: 'auto' }}>
            <button className="bell-btn" aria-label="Cảnh báo" onClick={() => setBellOpen(o => !o)}>
              🔔{alertCount > 0 && <span className="bell-badge">{alertCount}</span>}
            </button>
            {bellOpen && (<>
              <div className="bell-backdrop" onClick={() => setBellOpen(false)} />
              <div className="bell-menu">
                {alertCount === 0 && <div className="bell-empty">🎉 Không có cảnh báo</div>}
                {abmOrphans.length > 0 && <>
                  <div className="bell-head">🚨 Đã gỡ quản lý · vẫn còn trong ABM ({abmOrphans.length})</div>
                  {abmOrphans.slice(0, 10).map(d => (
                    <div className="bell-item" key={'a' + d.serial_number}>
                      <span className="pd-red" />
                      <div style={{ minWidth: 0 }}>
                        <div className="bell-name">{d.model || 'iPhone'} · {d.serial_number}</div>
                        <div className="bell-sub">Máy giám sát đã bị gỡ quản lý — xoá trắng bằng Apple Configurator/ABM rồi cài lại</div>
                      </div>
                    </div>
                  ))}
                </>}
                {removedDevices.length > 0 && <>
                  <div className="bell-head">🚫 Đã gỡ quản lý ({removedDevices.length})</div>
                  {removedDevices.slice(0, 10).map(d => (
                    <div className="bell-item" key={'r' + d.udid} onClick={() => { setSelectedDevice(d); setBellOpen(false); }}>
                      <span className="pd-red" />
                      <div style={{ minWidth: 0 }}>
                        <div className="bell-name">{d.name}</div>
                        <div className="bell-sub">{d.owner_name || d.location_name || 'Chưa gán cửa hàng'}</div>
                      </div>
                    </div>
                  ))}
                </>}
                {staleDevices.length > 0 && <>
                  <div className="bell-head">📡 Mất tín hiệu ({staleDevices.length})</div>
                  {staleDevices.slice(0, 10).map(d => (
                    <div className="bell-item" key={'s' + d.udid} onClick={() => { setSelectedDevice(d); setBellOpen(false); }}>
                      <span className="pd-amber" />
                      <div style={{ minWidth: 0 }}>
                        <div className="bell-name">{d.name}</div>
                        <div className="bell-sub">Mất tín hiệu {staleDaysOf(d.last_seen)} ngày · {d.location_name || '—'}</div>
                      </div>
                    </div>
                  ))}
                </>}
                {overdueDevices.length > 0 && <>
                  <div className="bell-head">💰 Hợp đồng trễ hạn ({overdueDevices.length})</div>
                  {overdueDevices.slice(0, 10).map(d => (
                    <div className="bell-item" key={'o' + d.udid} onClick={() => { setSelectedDevice(d); setBellOpen(false); }}>
                      <span className="pd-red" />
                      <div style={{ minWidth: 0 }}>
                        <div className="bell-name">{d.name}</div>
                        <div className="bell-sub">{d.owner_name || d.location_name || 'Chưa gán cửa hàng'}</div>
                      </div>
                    </div>
                  ))}
                </>}
              </div>
            </>)}
          </div>
        </div>

        {activeNav === 'Kho'             && <WarehouseView me={session.user} locations={locations} showToast={showToast} onOpenDevice={d => setSelectedDevice(d)} onReloadDevices={reload} />}
        {activeNav === 'Lịch sử máy'     && <FinalizedView showToast={showToast} onOpenDevice={d => setSelectedDevice(d)} refreshKey={selectedDevice} />}
        {activeNav === 'Máy tính kỳ hạn' && <InstallmentCalcView />}
        {activeNav === 'Chứng chỉ APNs' && <PushCertView />}
        {activeNav === 'Máy từ Apple (ABM)' && <AbmView showToast={showToast} />}
        {activeNav === 'Tài khoản quét ABM' && <ScanAccountsView showToast={showToast} />}
        {activeNav === 'Báo cáo'         && <ReportsView showToast={showToast} onOpenDevice={udid => { const d = devices.find(x => x.udid === udid); if (d) setSelectedDevice(d); }} />}
        {activeNav === 'Nhật ký'         && <AuditView onOpenDevice={udid => { const d = devices.find(x => x.udid === udid); if (d) setSelectedDevice(d); }} />}
        {activeNav === 'Người dùng'      && <UsersView me={session.user} locations={locations} showToast={showToast} />}
        {activeNav === 'Cửa hàng'        && <LocationsView showToast={showToast} onChange={() => query.listLocations().then(setLocations)} />}
        {activeNav === 'Khách hàng'      && <CustomersView showToast={showToast} />}
        {activeNav === 'Cộng tác viên'   && <CollaboratorsView showToast={showToast} />}
        {activeNav === 'Sổ thu/chi'      && <LedgerView showToast={showToast} />}
        {activeNav === 'Nhân viên'       && <ChainStaffView me={session.user} showToast={showToast} />}
        {activeNav === 'Báo cáo chuỗi'   && <ChainReportView showToast={showToast} />}
        {activeNav === 'Doanh nghiệp'    && <CompanyView company={company} error={companyErr} />}
        {activeNav === 'Tài khoản'       && <AccountView me={session.user} showToast={showToast} refreshSession={() => auth.me().then(setSession)} />}

        {isDashboard && <>
        {/* Tổng quan — thu gọn/mở rộng; mobile mặc định thu gọn */}
        <button className="summary-toggle" onClick={() => setSummaryOpen(o => !o)} aria-expanded={summaryOpen}>
          <span>📊 Tổng quan</span>
          <span className="summary-chev">{summaryOpen ? 'Thu gọn ▾' : 'Mở rộng ▸'}</span>
        </button>
        {summaryOpen && (
          <div className="summary-wrap">
            {/* Stats */}
            <div className="stats">
              <StatCard label="Tổng thiết bị" value={stats.total}   color="blue"  ico="📱" sub="Đang được quản lý" />
              <StatCard label="Đang hoạt động" value={stats.active}  color="green" ico="✓" subClass="up"
                sub={stats.total ? `▲ ${Math.round(stats.active / stats.total * 100)}% đang kết nối` : 'Đang kết nối'} />
              <StatCard label="Bị khoá"       value={stats.locked}  color="amber" ico="🔒" sub="Chờ mở khoá" />
              <StatCard label="Đã gỡ quản lý" value={stats.offline} color="red"   ico="⚠" subClass="down" sub="Không điều khiển được nữa" />
            </div>
            {/* Fleet health */}
            <FleetBar stats={stats} />
          </div>
        )}

        {/* Tìm kiếm + bộ lọc — gom 1 khối cho dễ nhận biết */}
        <div className="list-search">
          {(() => {
            const advCount = [fromDate, toDate, creatorFilter, storeFilter, custSearch].filter(Boolean).length;
            return (
          <div className="filters-card">
          <div className="filters-card-title">🔎 Tìm kiếm &amp; bộ lọc</div>
          <div className="search-row">
            <div className="search-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Tìm tên máy, số serial, người dùng…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            {/* Phễu -> Tìm kiếm nâng cao (ẩn/hiện bộ lọc theo ngày/người tạo/cửa hàng/khách) */}
            <button type="button" className={`adv-toggle${showAdvanced ? ' on' : ''}${advCount ? ' has' : ''}`}
              aria-expanded={showAdvanced} onClick={() => setShowAdvanced(v => !v)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              <span className="adv-toggle-label">Tìm kiếm nâng cao</span>
              {advCount > 0 && <span className="adv-count">{advCount}</span>}
              <span className="adv-chev">{showAdvanced ? '▲' : '▾'}</span>
            </button>
          </div>
          {showAdvanced && (
          <div className="filter-bar">
            <label className="filt"><span>Từ ngày (cài máy)</span>
              <FilterDate value={fromDate} max={toDate} onChange={setFromDate} /></label>
            <label className="filt"><span>Đến ngày</span>
              <FilterDate value={toDate} min={fromDate} onChange={setToDate} /></label>
            <label className="filt"><span>Người tạo</span>
              <select value={creatorFilter} onChange={e => setCreatorFilter(e.target.value)}>
                <option value="">Tất cả</option>
                {creators.map(c => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label className="filt"><span>Cửa hàng</span>
              <select value={storeFilter} onChange={e => setStoreFilter(e.target.value)}>
                <option value="">Tất cả</option>
                {/* Chỉ CỬA HÀNG BÁN (ẩn kho — tab Thiết bị không có máy kho), theo phạm vi user */}
                {storeChoicesFor(session.user, locations, 'store').map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
              </select></label>
            <label className="filt grow"><span>Khách hàng (tên / SĐT)</span>
              <input type="text" placeholder="Tên hoặc số điện thoại khách…" value={custSearch} onChange={e => setCustSearch(e.target.value)} /></label>
            {advCount > 0 && (
              <button className="btn" style={{ alignSelf: 'flex-end' }}
                onClick={() => { setFromDate(''); setToDate(''); setCreatorFilter(''); setStoreFilter(''); setCustSearch(''); }}>
                ✕ Xóa lọc
              </button>
            )}
          </div>
          )}
          </div>
            );
          })()}
        </div>

        {/* Table */}
        <div className="content">
          <div className="table-card">
            <div className="table-head">
              <span className="table-head-title">Thiết bị</span>
              <div className="filter-btns">
                {['all','active','locked','offline'].map(f => (
                  <button key={f} className={`chip${filter === f ? ' on' : ''}`} onClick={() => setFilter(f)}>
                    {{ all:'Tất cả', active:'Hoạt động', locked:'Bị khoá', offline:'Đã gỡ quản lý' }[f]}
                  </button>
                ))}
              </div>
              <div className="th-actions">
                <button className="btn" onClick={reload} disabled={loading}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width:13, height:13, ...(loading ? { animation:'spin 1s linear infinite' } : {}) }}>
                    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>{loading ? '' : 'Làm mới'}
                </button>
                <button className="btn" onClick={() => exportCSV(filtered)}>Xuất CSV</button>
                {canManageRole(role) && (
                  <button className="btn" onClick={async () => {
                    if (!(await confirmVN('Gọi TẤT CẢ máy trong phạm vi báo về ngay? Máy còn kết nối sẽ báo về trong ít phút; máy mất kết nối sẽ hiện ở "📡 Mất tín hiệu".', { title: 'Đánh thức tất cả máy?', okText: 'Đánh thức' }))) return;
                    try { const r = await query.checkinAllDevices(); showToast(`Đã đánh thức ${r.pushed ?? 0} máy — chờ ít phút rồi bấm Làm mới`, 'success', '🔄'); }
                    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
                  }}>🔄 Đánh thức tất cả máy</button>
                )}
                {canManageRole(role) && (
                  <button className="btn" onClick={() => setNotifyAllOpen(true)}>📢 Gửi thông báo</button>
                )}
              </div>
            </div>
            <div className="tbl-wrap dev-table">
              <table>
                <colgroup>
                  <col style={{width:44}}/><col style={{width:236}}/><col style={{width:150}}/>
                  <col style={{width:120}}/><col style={{width:120}}/><col style={{width:118}}/>
                  <col style={{width:118}}/><col style={{width:82}}/><col style={{width:140}}/>
                  <col style={{width:98}}/><col style={{width:120}}/>
                </colgroup>
                <thead>
                  <tr>
                    <th><input type="checkbox" onChange={e => {
                      document.querySelectorAll('#tbody input[type=checkbox]')
                        .forEach(c => { c.checked = e.target.checked; });
                    }}/></th>
                    <th>Thiết bị</th><th>Khách hàng</th><th>Cửa hàng</th><th>Trạng thái</th>
                    <th className="num">Thanh toán/tháng</th><th className="num">Còn lại</th><th>Ngày trả</th><th>Người tạo</th>
                    <th>Pin</th><th>Lần cuối kết nối</th>
                  </tr>
                </thead>
                <tbody id="tbody">
                  {filtered.length === 0 ? (
                    <tr><td colSpan={11} style={{textAlign:'center',padding:40,color:'var(--text3)'}}>
                      Không tìm thấy thiết bị nào
                    </td></tr>
                  ) : filtered.map(d => (
                    <DeviceRow
                      key={d.udid}
                      device={d}
                      isPending={pending === d.udid}
                      onClick={() => setSelectedDevice(d)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {/* Danh sách dạng THẺ — ưu tiên mobile (CSS ẩn ở desktop, hiện <=768px) */}
            <div className="dev-cards">
              {filtered.length === 0
                ? <div className="dev-cards-empty">Không tìm thấy thiết bị nào</div>
                : filtered.map(d => (
                  <DeviceCard key={d.udid} device={d} isPending={pending === d.udid} onClick={() => setSelectedDevice(d)} />
                ))}
            </div>
          </div>
        </div>
        </>}
      </main>

      {/* Modal */}
      {selectedDevice && (
        <DeviceModal
          device={selectedDevice}
          role={role}
          /* Cổng khách: vai trò trong chuỗi (owner/manager/staff) — DeviceModal quy về vai
             trò nội bộ tương đương để chấm quyền. Rỗng với app nội bộ. */
          portalRole={session.user?.portal_role || ''}
          alCodeAccess={!!session.user?.al_code_access}
          locations={locations}
          canAssignStore={!!session.user?.can_manage}
          onClose={() => setSelectedDevice(null)}
          onAction={handleAction}
          onReload={reload}
          showToast={showToast}
          isPending={pending === selectedDevice.udid}
        />
      )}


      {/* Gửi thông báo hàng loạt (ảnh nền) — chỉ quản lý/điều hành/admin */}
      {notifyAllOpen && (
        <NotifyModal device={null} onClose={() => setNotifyAllOpen(false)} showToast={showToast} />
      )}

      {/* Toast */}
      <Toast toast={toast} />
      <DialogHost />

      <style>{GLOBAL_CSS}</style>
    </div>
  );
}

// ── Trang Báo cáo (doanh thu + trễ hạn) ──────────────────────────────────────
const vnd = (v) => (Number(v) || 0).toLocaleString('vi-VN') + ' ₫';

// ── Máy tính kỳ hạn: công cụ gợi ý cho mọi nhân viên (không lưu vào máy nào) ──
function InstallmentCalcView() {
  const [total, setTotal]   = useState('');
  const [down, setDown]     = useState('');
  const [months, setMonths] = useState('');

  const o = { total_amount: total, down_payment: down, installment_months: months };
  const financed = Math.max(0, (Number(total) || 0) - (Number(down) || 0)); // số tiền còn lại (chưa phụ phí)
  const valid    = Number(total) > 0 && Number(months) > 0 && (Number(total) - (Number(down) || 0)) >= 0;
  const monthly  = valid ? Number(calcMonthly(o)) : 0;
  const totalPay = valid ? Math.round(totalWithInterest(o)) : 0;
  const interest = valid ? Math.round(interestAmount(o)) : 0;

  return (
    <div className="page">
      <div className="page-card" style={{ maxWidth: 880 }}>
        <div className="page-card-title">🧮 Máy tính kỳ hạn</div>
        <div className="page-note" style={{ marginBottom: 16 }}>
          Nhập giá máy, tiền trả trước và chọn kỳ để xem mức <b>thanh toán mỗi tháng</b> gợi ý.
          Đây chỉ là công cụ tính nhanh — <b>không lưu</b> vào thiết bị hay khách hàng nào.
        </div>
        <div className="calc-grid">
          {/* Cột trái: nhập liệu */}
          <div className="uform" style={{ gridTemplateColumns: '1fr' }}>
            <label><span>Tổng giá máy {Number(total) > 0 && <em style={{ fontStyle: 'normal', color: 'var(--text3)', fontWeight: 400 }}>· {vnd(total)}</em>}</span>
              <input type="number" inputMode="numeric" placeholder="VND" value={total} onChange={e => setTotal(e.target.value)} />
            </label>
            <label><span>Trả trước {Number(down) > 0 && <em style={{ fontStyle: 'normal', color: 'var(--text3)', fontWeight: 400 }}>· {vnd(down)}</em>}</span>
              <input type="number" inputMode="numeric" placeholder="VND (có thể để trống)" value={down} onChange={e => setDown(e.target.value)} />
            </label>
            <label><span>Kỳ (số tháng)</span>
              <select value={months} onChange={e => setMonths(e.target.value)}>
                <option value="">— Chọn kỳ —</option>
                {Array.from({ length: 6 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m} tháng</option>)}
              </select>
            </label>
          </div>

          {/* Cột phải: kết quả */}
          {valid ? (
            <div className="info-grid g2">
              <div className="info-cell" style={{ gridColumn: '1 / -1', background: 'var(--green-bg, rgba(34,197,94,.08))' }}>
                <div className="info-cell-label">Thanh toán mỗi tháng</div>
                <div className="info-cell-val" style={{ fontSize: 27, fontWeight: 800, color: 'var(--green)' }}>
                  {vnd(monthly)} <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text3)' }}>× {months} tháng</span>
                </div>
              </div>
              <div className="info-cell">
                <div className="info-cell-label">Số tiền còn lại</div>
                <div className="info-cell-val" style={{ fontSize: 15 }}>{vnd(financed)}</div>
              </div>
              <div className="info-cell">
                <div className="info-cell-label">Phí dịch vụ</div>
                <div className="info-cell-val" style={{ fontSize: 15, color: 'var(--amber)' }}>{vnd(interest)}</div>
              </div>
              <div className="info-cell" style={{ gridColumn: '1 / -1' }}>
                <div className="info-cell-label">Tổng phải trả (trả trước + định kỳ)</div>
                <div className="info-cell-val" style={{ fontSize: 17, fontWeight: 700 }}>{vnd(totalPay)}</div>
              </div>
            </div>
          ) : (
            <div className="calc-empty">Nhập <b>giá máy</b> và chọn <b>kỳ</b> để xem kết quả.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportsView({ showToast, onOpenDevice }) {
  const [rev, setRev] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([query.getRevenue(), query.getOverdue()]).then(([r, o]) => {
      if (r.status === 'fulfilled') setRev(r.value);
      if (o.status === 'fulfilled') setOverdue(o.value || []);
      setLoading(false);
    });
  }, []);

  const maxMonth = rev && rev.by_month.length ? Math.max(1, ...rev.by_month.map(m => m.amount)) : 1;
  const monthLabel = (ym) => { const [y, m] = ym.split('-'); return `Th${Number(m)}/${y.slice(2)}`; };

  function exportOverdue() {
    const cols = ['Khách hàng', 'SĐT', 'Thiết bị', 'Cửa hàng', 'Kỳ đã đến hạn', 'Đã thu', 'Còn thiếu', 'Ngày cài máy'];
    const rows = overdue.map(o => [o.owner_name, o.phone, o.device, o.store, o.periods, o.paid, o.shortfall, dmy(o.enrolled_on)]);
    const csv = [cols, ...rows].map(r => r.map(c => `"${c ?? ''}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `khach-tre-han_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  }

  if (loading) return <div className="page"><div className="page-card"><span className="spinner" /> Đang tải báo cáo…</div></div>;

  return (
    <div className="page">
      <div className="page-card" style={{ marginBottom: 14 }}>
        <div className="page-card-title">Doanh thu đã thu (định kỳ)</div>
        <div className="stat-val green" style={{ fontSize: 29, marginBottom: 16 }}>{vnd(rev && rev.total)}</div>

        <div className="breakdown-title">Theo tháng (12 tháng gần nhất)</div>
        {(!rev || !rev.by_month.length) ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>Chưa có thanh toán nào.</div> :
          rev.by_month.map(m => (
            <div className="breakdown-row" key={m.key}>
              <span className="breakdown-key">{monthLabel(m.key)}</span>
              <span className="breakdown-bar"><span style={{ width: `${(m.amount / maxMonth) * 100}%` }} /></span>
              <span className="breakdown-val mono" style={{ width: 'auto', whiteSpace: 'nowrap' }}>{vnd(m.amount)}</span>
            </div>
          ))}

        <div className="breakdown-title" style={{ marginTop: 16 }}>Theo cửa hàng</div>
        {(!rev || !rev.by_store.length) ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>—</div> :
          rev.by_store.map(s => (
            <div className="breakdown-row" key={s.key}>
              <span className="breakdown-key" title={s.key}>{s.key}</span>
              <span className="breakdown-bar"><span style={{ width: `${(s.amount / (rev.by_store[0].amount || 1)) * 100}%`, background: 'var(--green)' }} /></span>
              <span className="breakdown-val mono" style={{ width: 'auto', whiteSpace: 'nowrap' }}>{vnd(s.amount)}</span>
            </div>
          ))}
      </div>

      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center' }}>
          <span>Hợp đồng trễ hạn ({overdue.length})</span>
          <button className="btn" style={{ marginLeft: 'auto' }} onClick={exportOverdue} disabled={!overdue.length}>Xuất CSV</button>
        </div>
        {overdue.length === 0 ? (
          <div style={{ fontSize: 14, color: 'var(--text3)', padding: '8px 0' }}>🎉 Không có hợp đồng nào trễ hạn.</div>
        ) : (
          <>
          <div className="tbl-wrap dev-table">
            <table>
              <thead><tr><th>Khách hàng</th><th>Thiết bị</th><th>Cửa hàng</th><th>Kỳ</th><th>Đã thu</th><th>Còn thiếu</th></tr></thead>
              <tbody>
                {overdue.map(o => (
                  <tr key={o.udid} onClick={() => onOpenDevice && onOpenDevice(o.udid)}>
                    <td><div style={{ fontWeight: 600 }}>{o.owner_name || '—'}</div><div className="mono" style={{ fontSize: 12, color: 'var(--text2)' }}>{o.phone || ''}</div></td>
                    <td>{o.device}</td>
                    <td>{o.store || '—'}</td>
                    <td className="mono">{o.periods}</td>
                    <td className="mono">{vnd(o.paid)}</td>
                    <td className="mono" style={{ color: 'var(--red)', fontWeight: 600 }}>{vnd(o.shortfall)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile: thẻ (ẩn bảng, hiện <=768px) */}
          <div className="dev-cards">
            {overdue.map(o => (
              <div key={o.udid} className="user-card" onClick={() => onOpenDevice && onOpenDevice(o.udid)} style={{ cursor: 'pointer' }}>
                <div className="user-card-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="user-card-name">{o.owner_name || '—'}</div>
                    {o.phone && <div className="user-card-sub mono">{o.phone}</div>}
                  </div>
                  <div style={{ flex: 'none', textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600 }}>CÒN THIẾU</div>
                    <div className="mono" style={{ color: 'var(--red)', fontWeight: 700 }}>{vnd(o.shortfall)}</div>
                  </div>
                </div>
                <div className="user-card-meta">
                  <div><span className="ucl">Thiết bị</span> {o.device}</div>
                  <div><span className="ucl">Cửa hàng</span> {o.store || '—'}</div>
                  <div><span className="ucl">Kỳ</span> <span className="mono">{o.periods}</span> &nbsp;·&nbsp; <span className="ucl">Đã thu</span> <span className="mono">{vnd(o.paid)}</span></div>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Nhật ký thao tác (audit) ─────────────────────────────────────────────────
const AUDIT_META = {
  command:         { icon: '📟', label: 'Lệnh' },
  push:            { icon: '🔄', label: 'Đánh thức máy' },
  lock_state:      { icon: '🔒', label: 'Khoá' },
  assign_location: { icon: '🏬', label: 'Cửa hàng' },
  owner_update:    { icon: '👤', label: 'Khách hàng' },
  payment_add:     { icon: '💵', label: 'Thu tiền' },
  payment_delete:  { icon: '🗑', label: 'Xoá thanh toán' },
  image_add:       { icon: '🖼', label: 'Thêm ảnh' },
  image_delete:    { icon: '🗑', label: 'Xóa ảnh' },
  unenroll:        { icon: '🔓', label: 'Ngừng quản lý' },
  clear_orglock:   { icon: '🔓', label: 'Ngừng quản lý' },             // nhật ký cũ (trước là Xóa Org-lock + Gỡ MDM)
  erase:           { icon: '🗑', label: 'Xoá trắng (đã bỏ)' }, // hiển thị nhật ký cũ
  assign_installer:{ icon: '🧑‍🔧', label: 'Gán người cài đặt' },
  icloud_block:    { icon: '🔒', label: 'Chặn iCloud riêng' },
  user_create:     { icon: '👤', label: 'Tạo tài khoản' },
  user_update:     { icon: '✏️', label: 'Sửa tài khoản' },
  user_delete:     { icon: '🗑', label: 'Xoá tài khoản' },
};
function fmtTimeVN(iso) { return dmyTime(iso); }

export function AuditList({ items, onOpenDevice }) {
  if (!items || !items.length) return <div style={{ padding: '10px 0', color: 'var(--text3)', fontSize: 14 }}>Chưa có nhật ký.</div>;
  return (
    <>
      {items.map(a => {
        const m = AUDIT_META[a.action] || { icon: '•', label: a.action };
        const dev = a.device_name || (a.device_id ? a.device_id.slice(0, 10) : '');
        return (
          <div className="audit-row" key={a.id}
            style={{ cursor: a.device_id ? 'pointer' : 'default' }}
            onClick={() => a.device_id && onOpenDevice && onOpenDevice(a.device_id)}>
            <span className="audit-ico">{m.icon}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="audit-line"><b>{a.actor || '—'}</b> <span className="audit-chip">{m.label}</span> {a.detail}</div>
              <div className="audit-sub">{dev ? dev + ' · ' : ''}{fmtTimeVN(a.created_at)}{a.ip ? ' · ' + a.ip : ''}</div>
            </div>
          </div>
        );
      })}
    </>
  );
}

function AuditView({ onOpenDevice }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const load = (qq = '') => { setLoading(true); query.getAudit(qq).then(setItems).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span>Nhật ký thao tác</span>
          <div className="search-box" style={{ marginLeft: 'auto' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            <input placeholder="Tìm nhân viên / thao tác / thiết bị…" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(q)} />
          </div>
          <button className="btn" onClick={() => load(q)}>Tìm</button>
        </div>
        {loading ? <div><span className="spinner" /> Đang tải…</div> : <AuditList items={items} onOpenDevice={onOpenDevice} />}
      </div>
    </div>
  );
}

// ── Trang chứng chỉ APNs ─────────────────────────────────────────────────────
function PushCertView() {
  const [cert, setCert]   = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certFile, setCertFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null); // {ok:bool, text}
  const [forceNew, setForceNew] = useState(false); // cố ý nạp cert TOPIC MỚI (tách môi trường)

  const reload = () => query.getPushCert().then(c => { setCert(c); setError(null); }).catch(e => setError(e.message));
  useEffect(() => {
    let alive = true;
    query.getPushCert()
      .then(c => { if (alive) { setCert(c); setError(null); } })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  async function upload() {
    if (!certFile || !keyFile) { setMsg({ ok: false, text: 'Chọn cả file chứng chỉ (.pem từ Apple) và file private key.' }); return; }
    const confirmText = forceNew
      ? 'CERT TOPIC MỚI: dùng khi CỐ Ý tạo cert riêng cho môi trường này. Máy đang chạy trên topic CŨ sẽ mất kết nối đẩy lệnh tới khi CÀI ĐẶT LẠI. Chỉ làm khi chưa có/ít máy thật.'
      : 'CHỈ dùng cert vừa RENEW (giữ nguyên topic). Nạp nhầm cert tạo mới (topic khác) sẽ làm HỎNG toàn bộ thiết bị đang quản lý — hệ thống sẽ tự chặn nếu topic khác.';
    if (!(await confirmVN(confirmText, { title: forceNew ? 'Nạp cert TOPIC MỚI?' : 'Nạp push cert (Renew)?', okText: 'Nạp', danger: true }))) return;
    setUploading(true); setMsg(null);
    try {
      const certText = (await certFile.text()).trim();
      const keyText = (await keyFile.text()).trim();
      await query.uploadPushCert(certText + '\n' + keyText + '\n', { force: forceNew });
      setMsg({ ok: true, text: 'Đã nạp push cert mới thành công.' });
      setCertFile(null); setKeyFile(null);
      reload();
    } catch (ex) {
      setMsg({ ok: false, text: (ex.body && ex.body.error) || ex.message });
    } finally { setUploading(false); }
  }

  const daysLeft = cert?.not_after
    ? Math.round((new Date(cert.not_after) - new Date()) / 86400000)
    : null;
  const expClass = daysLeft == null ? 'blue' : daysLeft < 30 ? 'red' : daysLeft < 60 ? 'amber' : 'green';

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title">Chứng chỉ APNs (MDM Push Certificate)</div>
        {loading ? (
          <div style={{ color: 'var(--text3)', fontSize: 14 }}>Đang tải…</div>
        ) : error ? (
          <div style={{ color: 'var(--red)', fontSize: 14 }}>Không đọc được thông tin chứng chỉ: {error}</div>
        ) : (
          <>
            <div className="info-grid g2" style={{ marginBottom: 16 }}>
              <div className="info-cell">
                <div className="info-cell-label">Push topic</div>
                <div className="info-cell-val mono small">{cert?.topic || '—'}</div>
              </div>
              <div className="info-cell">
                <div className="info-cell-label">Ngày hết hạn</div>
                <div className="info-cell-val mono">{cert?.not_after ? dmyTime(cert.not_after) : '—'}</div>
              </div>
            </div>
            {daysLeft != null && (
              <div className={`cert-status ${expClass}`}>
                {daysLeft > 0
                  ? `Còn ${daysLeft} ngày. ${daysLeft < 60 ? 'Nên chuẩn bị gia hạn sớm.' : 'Chứng chỉ đang hợp lệ.'}`
                  : 'Chứng chỉ đã HẾT HẠN — thiết bị sẽ không nhận được push. Cần gia hạn ngay.'}
              </div>
            )}
            <div className="page-note">
              Gia hạn hằng năm bắt buộc đăng nhập <b>đúng Apple ID</b> đã tạo chứng chỉ tại identity.apple.com,
              nếu không toàn bộ máy đang quản lý sẽ mất kết nối đẩy lệnh.
            </div>

            <div className="page-card-title" style={{ marginTop: 22 }}>Nạp / gia hạn chứng chỉ</div>
            <div className="page-note" style={{ marginBottom: 12 }}>
              Sau khi <b>Renew</b> trên Apple portal (giữ nguyên topic), chọn 2 file rồi bấm Nạp.
              Hệ thống <b>tự chặn</b> nếu topic của cert mới khác topic hiện tại (dấu hiệu tạo mới nhầm).
            </div>
            <div className="uform" style={{ maxWidth: 620 }}>
              <label><span>Chứng chỉ (.pem tải từ Apple)</span>
                <input type="file" accept=".pem,.crt,.cer" onChange={e => setCertFile(e.target.files[0] || null)} />
              </label>
              <label><span>Private key (.key / .pem)</span>
                <input type="file" accept=".pem,.key" onChange={e => setKeyFile(e.target.files[0] || null)} />
              </label>
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: 13.5, color: 'var(--text2)', maxWidth: 620, cursor: 'pointer' }}>
              <input type="checkbox" checked={forceNew} onChange={e => setForceNew(e.target.checked)} style={{ marginTop: 2 }} />
              <span>Đây là cert <b>TOPIC MỚI</b> (cố ý tạo cert riêng cho môi trường này) — bỏ chặn topic. <b style={{ color: 'var(--red)' }}>Cảnh báo:</b> máy đang chạy trên topic cũ sẽ mất kết nối đẩy lệnh tới khi <b>cài đặt lại</b>. Để TRỐNG khi chỉ gia hạn (Renew).</span>
            </label>
            {msg && <div className={`cert-status ${msg.ok ? 'green' : 'red'}`} style={{ marginTop: 12 }}>{msg.text}</div>}
            <button className="btn primary" style={{ marginTop: 12 }} disabled={uploading} onClick={upload}>
              {uploading ? 'Đang nạp…' : (forceNew ? '⬆ Nạp cert topic mới' : '⬆ Nạp push cert')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Trang ABM / DEP: trạng thái gán DEP profile cho máy trong Apple Business ──
const ABM_REFRESH_SEC = 30; // chu kỳ tự làm mới danh sách ABM

// ── TÀI KHOẢN QUÉT ABM (Keycloak realm abm) — chỉ admin ────────────────────
// Thay cho việc SSH vào VPS chạy deploy/scripts/scan-account.sh.
//
// Quy ước vận hành: tài khoản quét dùng chung ở cửa hàng và vẫn xem được danh sách thiết
// bị của cả tổ chức nếu ai đó đăng nhập business.apple.com bằng nó — nên bình thường TẮT,
// bật đúng lúc quét rồi tắt lại. Vì vậy tài khoản ĐANG BẬT được đẩy lên đầu (backend
// sắp xếp) và tô cảnh báo — "quên tắt" là cái bẫy chính của màn này.
// Các lựa chọn thời hạn bật — PHẢI khớp kcDurations trong cmd/mdmquery/keycloak.go.
// value rỗng = mãi mãi (không tự tắt).
const SCAN_DURATIONS = [
  { value: '1h', label: '1 giờ' },
  { value: '2h', label: '2 giờ' },
  { value: '4h', label: '4 giờ' },
  { value: '1d', label: '1 ngày' },
  { value: '3d', label: '3 ngày' },
  { value: '7d', label: '7 ngày' },
  { value: '',   label: 'Mãi mãi' },
];

// countdownVN: còn bao lâu tới `iso`. Trả '' nếu không có hạn.
// Làm tròn LÊN phút cho khỏi hiện "còn 0 phút" suốt 59 giây cuối.
function countdownVN(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return '';
  if (ms <= 0) return 'sắp tắt';
  const mins = Math.ceil(ms / 60000);
  if (mins < 60) return `còn ${mins} phút`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `còn ${hours}h${String(mins % 60).padStart(2, '0')}`;
  return `còn ${Math.floor(hours / 24)} ngày ${hours % 24}h`;
}

// Hộp chọn thời hạn khi BẬT tài khoản quét. Bắt chọn thời hạn thay vì chỉ hỏi có/không:
// mặc định của thao tác này phải là "sẽ tự đóng lại", còn mãi mãi là lựa chọn có cân nhắc.
function ScanEnableModal({ user, onPick, onClose }) {
  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <div className="modal" style={{ width: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">🔑</div>
            <div>
              <div className="modal-device-name">Bật tài khoản quét</div>
              <div className="modal-device-sub">{user.username}</div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div className="page-note" style={{ marginBottom: 12 }}>
            Để mở, tài khoản này xem được danh sách thiết bị của cả tổ chức trên
            business.apple.com. Chọn thời hạn ngắn nhất đủ dùng cho đợt quét này.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {SCAN_DURATIONS.map(d => (
              <button key={d.value || 'forever'} type="button" className="btn"
                style={d.value === '' ? { color: 'var(--red)', borderColor: 'var(--red)' } : undefined}
                onClick={() => onPick(d.value)}>{d.label}</button>
            ))}
          </div>
          <div className="page-note" style={{ marginTop: 12 }}>
            <b>Mãi mãi</b> = không tự tắt, phải nhớ tắt tay. Chỉ dùng khi thật sự cần.
          </div>
        </div>
      </div>
    </div>
  );
}

// Đuôi + mật khẩu mặc định — PHẢI khớp kcUserDomain / kcDefaultPassword bên Go.
const SCAN_USER_DOMAIN = '@id.zentratech.vn';
// PHẢI khớp kcDefaultPassword bên Go, và tự nó phải đạt luật trong lib/password.js.
const SCAN_DEFAULT_PASSWORD = 'Zentra@123';

// Hộp tạo tài khoản quét. Tạo ra ở trạng thái TẮT (backend ép), người dùng bật sau khi
// đã đổi mật khẩu — nên form này cố ý không có lựa chọn "tạo rồi bật luôn".
function ScanCreateModal({ onCreate, onClose, saving }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState(SCAN_DEFAULT_PASSWORD);
  const pwErr = passwordErr(password);
  const submit = (e) => { e.preventDefault(); if (!pwErr) onCreate(username.trim(), password); };
  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <form className="modal" style={{ width: 460 }} onClick={e => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">➕</div>
            <div>
              <div className="modal-device-name">Tạo tài khoản quét</div>
              <div className="modal-device-sub">Managed Apple ID dùng cho Apple Configurator</div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div className="uform">
            <label className="full"><span>Tên đăng nhập</span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input value={username} onChange={e => setUsername(e.target.value)} placeholder="scan05"
                  autoComplete="off" required autoFocus style={{ flex: 1, minWidth: 0 }} />
                {!username.includes('@') && (
                  <span style={{ flex: 'none', fontSize: 13, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                    {SCAN_USER_DOMAIN}
                  </span>
                )}
              </div>
            </label>
            <label className="full"><span>Mật khẩu ({PASSWORD_HINT})</span>
              <input value={password} onChange={e => setPassword(e.target.value)}
                autoComplete="new-password" required />
              {/* Nút Tạo bị chặn khi mật khẩu chưa đạt — phải NÓI RA, không thì bấm mà
                  không có gì xảy ra là lỗi khó chịu nhất. */}
              {pwErr && <span style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>{pwErr}</span>}
            </label>
          </div>
          <div className="page-note" style={{ marginTop: 10 }}>
            Tài khoản tạo ra ở trạng thái <b>tắt</b>. Đổi mật khẩu mặc định trước khi bật lần đầu —
            tài khoản này đăng nhập được business.apple.com.
          </div>
          <button className="btn primary" style={{ width: '100%', marginTop: 12 }} disabled={saving || !!pwErr}>
            {saving ? 'Đang tạo…' : 'Tạo tài khoản'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ScanAccountsView({ showToast }) {
  const [rows, setRows] = useState(null);
  const [err, setErr]   = useState(null);
  const [busy, setBusy] = useState('');
  const [q, setQ]       = useState('');
  const [asking, setAsking] = useState(null); // tài khoản đang chờ chọn thời hạn
  const [creating, setCreating] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [, setNow]      = useState(0); // nhịp vẽ lại cho cột đếm ngược

  const load = (term = '') => query.listKCUsers(term)
    .then(d => { setRows(d || []); setErr(null); })
    .catch(e => { setErr((e.body && e.body.error) || e.message); setRows([]); });

  useEffect(() => { load(''); }, []);
  // Đếm ngược chạy ở FE mỗi 30 giây; riêng việc TẮT là do vòng lặp nền backend làm. Tải lại
  // danh sách mỗi 2 phút để trạng thái thật sau khi hết hạn hiện ra mà không cần F5.
  useEffect(() => {
    const t1 = setInterval(() => setNow(Date.now()), 30000);
    const t2 = setInterval(() => load(q.trim()), 120000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [q]);

  async function patch(u, body, okMsg) {
    setBusy(u.id);
    try {
      await query.patchKCUser(u.id, body);
      showToast(okMsg, 'success', '🔑');
      await load(q.trim());
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setBusy(''); }
  }

  // BẬT: mở hộp chọn thời hạn (không hỏi gì thêm — hộp đó chính là bước xác nhận).
  // TẮT: làm ngay, vì đó là chiều đóng quyền, không cần rào cản.
  function toggle(u) {
    if (!u.enabled) { setAsking(u); return; }
    patch(u, { enabled: false }, `Đã tắt ${u.username}`);
  }

  async function enableFor(u, duration) {
    setAsking(null);
    const label = (SCAN_DURATIONS.find(d => d.value === duration) || {}).label || duration;
    await patch(u, { enabled: true, duration },
      duration ? `Đã bật ${u.username} trong ${label}`
               : `Đã bật ${u.username} — KHÔNG tự tắt, nhớ tắt tay`);
  }

  async function toggleOtp(u) {
    await patch(u, { otp_required: !u.otp_required },
      u.otp_required ? `Đã bỏ bắt buộc OTP cho ${u.username}` : `${u.username} sẽ phải đặt OTP ở lần đăng nhập tới`);
  }

  async function create(username, password) {
    setSavingNew(true);
    try {
      const u = await query.createKCUser({ username, password });
      setCreating(false);
      showToast(`Đã tạo ${u.username || username} — đang tắt`, 'success', '🔑');
      await load(q.trim());
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSavingNew(false); }
  }

  async function del(u) {
    // Cảnh báo riêng khi tài khoản ĐANG BẬT: nhiều khả năng có người đang quét dở.
    const warn = u.enabled ? '\n\nTài khoản này ĐANG BẬT — có thể đang có người dùng dở.' : '';
    if (!(await confirmVN(
      `Xoá hẳn ${u.username} khỏi Keycloak? Không khôi phục được.${warn}`,
      { title: 'Xoá tài khoản quét?', okText: 'Xoá', danger: true }))) return;
    setBusy(u.id);
    try {
      await query.deleteKCUser(u.id);
      showToast(`Đã xoá ${u.username}`, 'success', '🗑');
      await load(q.trim());
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setBusy(''); }
  }

  const onSearch = (e) => { e.preventDefault(); load(q.trim()); };
  const openCount = (rows || []).filter(u => u.enabled).length;

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          Tài khoản quét ABM
          {openCount > 0 && <span className="badge badge-amber">{openCount} đang bật</span>}
          <form onSubmit={onSearch} style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Tìm tài khoản…"
              style={{ padding: '7px 10px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--bg2)', color: 'var(--text)', fontFamily: 'inherit' }} />
            <button className="btn" type="submit">Tìm</button>
          </form>
          <button className="btn primary" type="button" onClick={() => setCreating(true)}>+ Thêm tài khoản</button>
        </div>

        <div className="page-note" style={{ marginBottom: 12 }}>
          Bình thường để <b>tắt</b>, chỉ bật đúng lúc nhập máy. Bật xong hệ thống <b>tự tắt</b>
          khi hết thời hạn bạn chọn — trừ khi chọn “Mãi mãi”. Mỗi lần bật/tắt đều bắn
          Telegram và ghi vào Nhật ký. “Bắt OTP” buộc người dùng đặt mã xác thực hai lớp
          ngay lần đăng nhập kế tiếp.
        </div>

        {err && <div className="page-note" style={{ color: 'var(--red)' }}>{err}</div>}
        {rows === null && !err && <div className="page-note">Đang tải…</div>}
        {rows !== null && rows.length === 0 && !err && (
          <div className="page-note">Không có tài khoản nào trong realm.</div>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="tbl-wrap dev-table">
            <table>
              <thead><tr><th>Tài khoản</th><th>Trạng thái</th><th>Tự tắt</th><th>OTP</th><th>Thao tác</th></tr></thead>
              <tbody>
                {rows.map(u => (
                  <tr key={u.id}>
                    <td className="mono" style={{ fontWeight: u.enabled ? 600 : 400 }}>{u.username}</td>
                    <td>{u.enabled
                      ? <span className="badge badge-amber">Đang bật</span>
                      : <span className="badge badge-gray">Đã tắt</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {!u.enabled ? <span style={{ color: 'var(--text3)' }}>—</span>
                        : u.enabled_until
                          ? <span title={new Date(u.enabled_until).toLocaleString('vi-VN')}>
                              {countdownVN(u.enabled_until)}
                            </span>
                          : <span style={{ color: 'var(--red)' }}>không tự tắt</span>}
                    </td>
                    <td>{u.otp_required
                      ? <span className="badge badge-blue">Bắt buộc đặt</span>
                      : <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className={'act-btn' + (u.enabled ? ' danger-act' : '')} disabled={busy === u.id}
                        onClick={() => toggle(u)}>{u.enabled ? 'Tắt' : 'Bật'}</button>{' '}
                      <button className="act-btn" disabled={busy === u.id} onClick={() => toggleOtp(u)}>
                        {u.otp_required ? 'Bỏ OTP' : 'Bắt OTP'}</button>{' '}
                      <button className="act-btn danger-act" disabled={busy === u.id}
                        onClick={() => del(u)}>Xoá</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {asking && <ScanEnableModal user={asking} onClose={() => setAsking(null)}
        onPick={(d) => enableFor(asking, d)} />}
      {creating && <ScanCreateModal saving={savingNew}
        onClose={() => setCreating(false)} onCreate={create} />}
    </div>
  );
}

function AbmView({ showToast }) {
  const [devs, setDevs] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState('');
  const [nextIn, setNextIn] = useState(ABM_REFRESH_SEC); // giây còn lại tới lần làm mới kế
  const nextInRef = useRef(ABM_REFRESH_SEC);

  const load = () => {
    nextInRef.current = ABM_REFRESH_SEC; // mỗi lần tải (tự động/thủ công) reset đồng hồ
    setNextIn(ABM_REFRESH_SEC);
    return query.getAbmDevices().then(d => { setDevs(d || []); setErr(null); }).catch(e => setErr(e.message));
  };
  useEffect(() => {
    load();
    const t = setInterval(() => {
      const v = nextInRef.current - 1;
      if (v <= 0) { load(); }           // hết giờ -> tải lại (load tự reset về ABM_REFRESH_SEC)
      else { nextInRef.current = v; setNextIn(v); }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  async function assign(serial) {
    setBusy(serial);
    try { await query.assignAbm([serial]); showToast('Đã gán cấu hình cho máy', 'success', '✅'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setBusy(''); }
  }
  const badge = (st) => {
    if (st === 'empty')    return <span className="badge badge-amber">⏳ Chưa gán</span>;
    if (st === 'assigned') return <span className="badge badge-green">✅ Đã gán (chờ kích hoạt)</span>;
    if (st === 'pushed')   return <span className="badge badge-blue">✅ Đã cài lên máy</span>;
    return <span className="badge badge-gray">{st || '—'}</span>;
  };

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center' }}>
          Máy trong Apple Business Manager (ABM)
          <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 400, color: 'var(--text3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            ⏱ Tự làm mới sau <b style={{ color: 'var(--text2)', fontVariantNumeric: 'tabular-nums', minWidth: 20, textAlign: 'right' }}>{nextIn}s</b>
          </span>
          <button className="btn" style={{ marginLeft: 12 }} onClick={load}>Làm mới</button>
        </div>
        <div className="page-note" style={{ marginBottom: 12 }}>
          Máy quét thêm bằng Apple Configurator sẽ <b>tự gán cấu hình trong ~90 giây</b> (trạng thái chuyển sang "Đã gán").
          Nếu cần ngay, bấm <b>"Gán ngay"</b>. Chỉ khi trạng thái là <b>"Đã gán"</b> thì xoá máy mới lên đúng chế độ giám sát.
        </div>
        {err ? (
          <div style={{ color: 'var(--red)', fontSize: 14 }}>{err}</div>
        ) : devs === null ? (
          <div style={{ color: 'var(--text3)', fontSize: 14 }}>Đang tải…</div>
        ) : devs.length === 0 ? (
          <div style={{ color: 'var(--text3)', fontSize: 14 }}>Chưa có máy nào trong ABM (trên máy chủ này). Quét máy bằng Apple Configurator để thêm.</div>
        ) : (
          <>
          <div className="tbl-wrap dev-table">
            <table>
              <thead><tr><th>Serial</th><th>Thiết bị</th>
                <th title="Tài khoản Apple Configurator / ABM đã quét máy này vào (device_assigned_by). Khớp với cột ABM ở trang Người dùng để ra cửa hàng + người cài đặt.">Tài khoản ABM</th>
                <th>Trạng thái gán</th><th>Thao tác</th></tr></thead>
              <tbody>
                {devs.map(d => (
                  <tr key={d.serial_number}>
                    <td className="mono">{d.serial_number}</td>
                    <td>{d.model || '—'}<div style={{ fontSize: 12, color: 'var(--text2)' }}>{d.description || ''}</div></td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                           title={d.device_assigned_by || 'Máy không vào qua ABM/Configurator nên Apple không có thông tin này'}>
                        {d.device_assigned_by || '—'}
                      </div>
                    </td>
                    <td>{badge(d.profile_status)}</td>
                    <td>{d.profile_status === 'empty' && (
                      <button className="act-btn" disabled={busy === d.serial_number} onClick={() => assign(d.serial_number)}>
                        {busy === d.serial_number ? 'Đang gán…' : 'Gán ngay'}
                      </button>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile: thẻ (ẩn bảng, hiện <=768px) — giống tab Thiết bị */}
          <div className="dev-cards">
            {devs.map(d => (
              <div key={d.serial_number} className="user-card">
                <div className="user-card-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="user-card-name">{d.model || 'iPhone'}</div>
                    {d.description && <div className="user-card-sub">{d.description}</div>}
                    <div className="mono" style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{d.serial_number}</div>
                  </div>
                  <div style={{ flex: 'none' }}>{badge(d.profile_status)}</div>
                </div>
                <div className="user-card-meta">
                  <div><span className="ucl">ABM</span> <span className="mono" style={{ wordBreak: 'break-all' }}>{d.device_assigned_by || '—'}</span></div>
                </div>
                {d.profile_status === 'empty' && (
                  <div className="user-card-actions">
                    <button className="act-btn" disabled={busy === d.serial_number} onClick={() => assign(d.serial_number)}>
                      {busy === d.serial_number ? 'Đang gán…' : 'Gán ngay'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ label, value, color, sub, ico, subClass }) {
  return (
    <div className="stat-card">
      <div className="stat-top">{label}<span className={`stat-ico ${color}`}>{ico}</span></div>
      <div className={`stat-val ${color}`}><CountUp value={value} /></div>
      <div className={`stat-sub ${subClass || ''}`}>{sub}</div>
    </div>
  );
}

// Thanh "Tình trạng đội thiết bị" (thay các thẻ phân tích cũ).
function FleetBar({ stats }) {
  const total = stats.total || 0;
  const pct = (n) => total ? (n / total * 100) : 0;
  const seg = [
    { key: 'Hoạt động', n: stats.active,  c: 'var(--green)' },
    { key: 'Bị khoá',   n: stats.locked,  c: 'var(--amber)' },
    { key: 'Đã gỡ quản lý', n: stats.offline, c: '#9aa2ba' },
  ];
  return (
    <div className="fleet">
      <div className="fleet-title">Tình trạng đội thiết bị</div>
      <div className="fleet-cap">Phân bố trạng thái của {total} thiết bị theo thời gian thực</div>
      <div className="fleet-bar">
        {seg.map((s, i) => (
          <span key={s.key} style={{ width: `${pct(s.n)}%`, background: s.c, animationDelay: `${.2 + i * .1}s` }} />
        ))}
      </div>
      <div className="fleet-legend">
        {seg.map(s => (
          <div key={s.key}><i className="sw" style={{ background: s.c }} />{s.key} <b>{s.n || 0}</b></div>
        ))}
      </div>
    </div>
  );
}

// Đếm số tăng dần khi hiển thị (ease-out).
function CountUp({ value }) {
  const target = Number(value) || 0;
  const [n, setN] = useState(0);
  useEffect(() => {
    if (target <= 0) { setN(0); return; }
    let raf, t0;
    const dur = 650;
    const step = (t) => {
      if (t0 == null) t0 = t;
      const p = Math.min(1, (t - t0) / dur);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <>{n}</>;
}

function BreakdownCard({ title, data }) {
  const items = Object.entries(data || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const max = items.reduce((m, [, v]) => Math.max(m, v), 0) || 1;
  return (
    <div className="breakdown-card">
      <div className="breakdown-title">{title}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>Chưa có dữ liệu</div>
      ) : items.map(([key, val]) => (
        <div className="breakdown-row" key={key}>
          <span className="breakdown-key" title={key}>{key}</span>
          <span className="breakdown-bar"><span style={{ width: `${(val / max) * 100}%` }} /></span>
          <span className="breakdown-val mono">{val}</span>
        </div>
      ))}
    </div>
  );
}

function DeviceRow({ device: d, isPending, onClick }) {
  const bpct = d.battery || 0;
  const bcolor = bpct < 20 ? 'var(--red)' : bpct < 50 ? 'var(--amber)' : 'var(--green)';
  return (
    <tr onClick={onClick} style={isPending ? { opacity:.6 } : {}}>
      <td onClick={e => e.stopPropagation()}><input type="checkbox"/></td>
      <td>
        <div className="dev">
          <DevicePhoto product={d.product} />
          <div style={{ minWidth:0 }}>
            <div className="dev-name">{appleModelName(d.product, d.model) || d.model || '—'}</div>
            <div className="dev-serial">{d.serial || (d.udid || '').slice(0, 12)}</div>
            <div className="dev-model">{d.name} · iOS {d.os || '—'}</div>
          </div>
        </div>
      </td>
      <td>
        {d.owner_name
          ? <><div style={{ fontWeight:600 }}>{d.owner_name}</div>
              <div className="mono" style={{ fontSize:12, color:'var(--text2)' }}>{d.owner_phone || ''}</div></>
          : <span style={{ color:'var(--text3)' }}>—</span>}
      </td>
      <td>{d.location_name || <span style={{ color:'var(--text3)' }}>—</span>}</td>
      <td>
        <StatusBadge status={d.status}/>
        {d.in_warehouse && <div style={{ marginTop:4 }}><span className="pill" style={{ background:'var(--amber-bg)', color:'var(--amber)' }}>📦 Trong kho</span></div>}
        {d.lost_mode === true && d.status !== 'offline' && <div style={{ marginTop:4 }}><span className="pill danger">📍 Đang khoá</span></div>}
        {d.enabled && staleDaysOf(d.last_seen) >= STALE_DAYS && <div style={{ marginTop:4 }}><span className="pill warn">📡 Mất tín hiệu {staleDaysOf(d.last_seen)} ngày</span></div>}
        {d.due_status === 'overdue' && <div style={{ marginTop:4 }}><span className="pill warn">⚠️ Quá hạn</span></div>}
        {d.due_status === 'ended'   && <div style={{ marginTop:4 }}><span className="pill off">Hết hạn HĐ</span></div>}
      </td>
      <td className="mono num">{d.monthly_amount != null
        ? <>{vnd(d.monthly_amount)}{isRental(d) && <i className="rent-tag" title="Hợp đồng cho thuê — số tiền mỗi kỳ">thuê</i>}</>
        : <span style={{ color:'var(--text3)' }}>—</span>}</td>
      <td className="mono num" style={{ fontWeight:600, color: d.remaining > 0 ? 'var(--text)' : 'var(--text2)' }}>{d.remaining != null ? vnd(d.remaining) : <span style={{ color:'var(--text3)' }}>—</span>}</td>
      <td className="mono">{payDayLabel(d) || <span style={{ color:'var(--text3)' }}>—</span>}</td>
      <td>
        {d.enrolled_by
          ? <><div>{d.enrolled_by}</div>
              <div className="mono" style={{ fontSize:12, color:'var(--text2)' }}>{d.created_at || ''}</div></>
          : <span style={{ color:'var(--text3)' }}>—</span>}
      </td>
      <td>
        {bpct ? (
          <div className="batt">
            <div className="track"><div className="fill" style={{ width:`${bpct}%`, background:bcolor }}/></div>
            <span className="mono">{bpct}%</span>
          </div>
        ) : <span style={{ color:'var(--text3)' }}>—</span>}
      </td>
      <td className="mono" style={{ color:'var(--text2)' }}>{relTime(d.last_seen)}</td>
    </tr>
  );
}

// DeviceCard — thẻ thiết bị cho MOBILE (ưu tiên di động, bố cục giống app đặt món): ảnh máy
// bên trái, tên + khách/cửa hàng + thanh toán + nhãn trạng thái. Bấm cả thẻ để mở chi tiết.
function DeviceCard({ device: d, isPending, onClick }) {
  const stale = d.enabled && staleDaysOf(d.last_seen) >= STALE_DAYS;
  return (
    <button type="button" className="dev-card" onClick={onClick} style={isPending ? { opacity: .6 } : {}}>
      <DevicePhoto product={d.product} className="ph dev-card-ph" />
      <div className="dev-card-body">
        <div className="dev-card-line1">
          <span className="dev-card-name">{appleModelName(d.product, d.model) || d.model || '—'}</span>
          <StatusBadge status={d.status} />
        </div>
        <div className="dev-card-serial mono">{d.serial || (d.udid || '').slice(0, 12)} · iOS {d.os || '—'}</div>
        <div className="dev-card-owner">
          {d.owner_name
            ? <><b>{d.owner_name}</b>{d.owner_phone ? <span className="mono"> · {d.owner_phone}</span> : ''}</>
            : <span style={{ color: 'var(--text3)' }}>Chưa có khách</span>}
          {d.location_name ? <span className="dev-card-store"> · {d.location_name}</span> : ''}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 1 }}>🕐 Lần cuối kết nối: {d.last_seen ? relTime(d.last_seen) : '—'}</div>
        {(d.monthly_amount != null || d.remaining != null) && (
          <div className="dev-card-money">
            {d.monthly_amount != null && <span>{vnd(d.monthly_amount)}<i>{perLabel(d)}</i></span>}
            {d.remaining != null && <span className="rem">Còn {vnd(d.remaining)}</span>}
            {payDayLabel(d) ? <span className="day">{payDayLabel(d)}</span> : ''}
          </div>
        )}
        <div className="dev-card-badges">
          {d.in_warehouse && <span className="pill" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>📦 Trong kho</span>}
          {d.lost_mode === true && d.status !== 'offline' && <span className="pill danger">📍 Đang khoá</span>}
          {stale && <span className="pill warn">📡 Mất tín hiệu {staleDaysOf(d.last_seen)} ngày</span>}
          {d.due_status === 'overdue' && <span className="pill warn">⚠️ Quá hạn</span>}
          {d.due_status === 'ended' && <span className="pill off">Hết hạn HĐ</span>}
          {d.battery ? <span className="pill" style={{ background: 'var(--bg4)', color: 'var(--text2)' }}>🔋 {d.battery}%</span> : ''}
        </div>
      </div>
      <span className="dev-card-chev">›</span>
    </button>
  );
}

// FinalizedView — LỊCH SỬ máy đã từng quản lý (đã gỡ MDM + bấm Hoàn tất). manager+.
// Danh sách dạng thẻ (mobile-first). Bấm thẻ mở chi tiết; nút Khôi phục đưa máy về danh sách.
function FinalizedView({ showToast, onOpenDevice, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(null);
  const load = () => { setLoading(true); query.getFinalizedDevices().then(setItems).catch(e => showToast(e.message, 'error', '❌')).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [refreshKey]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(d => !q || [d.name, d.serial, d.imei, d.model, appleModelName(d.product, d.model), d.owner_name, d.owner_phone, d.location_name].some(v => (v || '').toLowerCase().includes(q)));
  }, [items, search]);

  async function restore(d, e) {
    e.stopPropagation();
    if (!(await confirmVN('Đưa máy trở lại danh sách Thiết bị?', { title: 'Khôi phục máy?', okText: 'Khôi phục' }))) return;
    setBusy(d.udid);
    try { await query.unfinalizeDevice(d.udid); showToast('Đã khôi phục về danh sách Thiết bị', 'success', '↩️'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setBusy(null); }
  }

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title">📁 Lịch sử máy đã từng quản lý ({filtered.length})</div>
        <div className="filters-card" style={{ marginBottom: 12 }}>
          <div className="search-row">
            <div className="search-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input type="text" placeholder="Tìm tên máy, số serial, IMEI, tên khách…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button className="btn" onClick={load} disabled={loading}>🔄 Làm mới</button>
          </div>
        </div>
        <div className="page-note" style={{ marginBottom: 12 }}>
          Máy đã <b>gỡ khỏi hệ thống quản lý</b> và được bấm <b>Hoàn tất</b> — lưu tại đây, không tính vào danh sách/thống kê Thiết bị. Nếu máy <b>được cài đặt lại</b> sẽ tự quay về danh sách chính.
        </div>
        {loading ? <p style={{ color: 'var(--text3)', fontSize: 13 }}>Đang tải…</p>
          : filtered.length === 0 ? <div className="page-note">{items.length === 0 ? 'Chưa có máy nào trong lịch sử.' : 'Không có máy khớp tìm kiếm.'}</div>
            : <div className="fin-list">
              {filtered.map(d => (
                <div key={d.udid} className="dev-card" onClick={() => onOpenDevice(d)}>
                  <DevicePhoto product={d.product} className="ph dev-card-ph" />
                  <div className="dev-card-body">
                    <div className="dev-card-line1">
                      <span className="dev-card-name">{appleModelName(d.product, d.model) || d.model || '—'}</span>
                      <span className="pill off">Đã gỡ quản lý</span>
                    </div>
                    <div className="dev-card-serial mono">{d.serial || (d.udid || '').slice(0, 12)} · iOS {d.os || '—'}</div>
                    <div className="dev-card-owner">
                      {d.owner_name ? <><b>{d.owner_name}</b>{d.owner_phone ? <span className="mono"> · {d.owner_phone}</span> : ''}</> : <span style={{ color: 'var(--text3)' }}>Chưa có khách</span>}
                      {d.location_name ? <span className="dev-card-store"> · {d.location_name}</span> : ''}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
                      ✅ Hoàn tất{d.finalized_at ? ` ${d.finalized_at}` : ''}{d.finalized_by ? ` · ${d.finalized_by}` : ''}
                    </div>
                    {d.termination_profit != null && (
                      <div style={{ fontSize: 12.5, marginTop: 2 }}>💰 {d.termination_scenario === 'return' ? 'Thu ròng' : 'Tiền lời'}: <b style={{ color: 'var(--green)' }}>{vnd(d.termination_profit)}</b></div>
                    )}
                  </div>
                  <button className="btn" style={{ alignSelf: 'center', whiteSpace: 'nowrap' }} disabled={busy === d.udid} onClick={e => restore(d, e)}>
                    {busy === d.udid ? '⏳…' : '↩️ Khôi phục'}
                  </button>
                </div>
              ))}
            </div>}
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active:  { cls: 'on',     label: 'Hoạt động' },
    locked:  { cls: 'warn',   label: 'Bị khoá'   },
    offline: { cls: 'danger', label: 'Đã gỡ quản lý' },
  };
  const { cls, label } = map[status] || map.off;
  return <span className={`pill ${cls}`}><span className="pd"/>{label}</span>;
}

// Ngưỡng "mất tín hiệu": máy còn quản lý nhưng không check-in quá số ngày này.
const STALE_DAYS = 3;
// Số ngày kể từ lần cuối online (9999 nếu chưa từng / không hợp lệ).
function staleDaysOf(iso) {
  if (!iso) return 9999;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 9999;
  return Math.floor((Date.now() - t) / 86400000);
}

function relTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '—';
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60)    return 'vài giây trước';
  if (s < 3600)  return Math.floor(s / 60) + ' phút trước';
  if (s < 86400) return Math.floor(s / 3600) + ' giờ trước';
  return Math.floor(s / 86400) + ' ngày trước';
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Sinh mật khẩu ngẫu nhiên (chữ hoa + thường + số, không ký tự đặc biệt),
// mặc định 14 ký tự — ví dụ: T9cPXO2kYgWHo6. Dùng crypto để ngẫu nhiên an toàn.
// Mật khẩu sinh tự động (nút 🎲) PHẢI tự nó đạt luật trong lib/password.js — không thì
// bấm 🎲 xong bấm Lưu là ăn lỗi từ backend, đúng chỗ người dùng tin tưởng nhất.
// Ký tự đặc biệt giới hạn trong nhóm an toàn khi copy/dán và gõ lại trên bàn phím iOS.
function randomPassword(len = 14) {
  const U = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const L = 'abcdefghijklmnopqrstuvwxyz';
  const D = '0123456789';
  const S = '@#$%&*!?';
  const all = U + L + D + S;
  const r = new Uint32Array(len * 2);
  crypto.getRandomValues(r);
  // Đảm bảo tối thiểu 1 chữ hoa, 1 chữ thường, 1 số, 1 ký tự đặc biệt.
  const chars = [U[r[0] % U.length], L[r[1] % L.length], D[r[2] % D.length], S[r[3] % S.length]];
  for (let i = 4; i < len; i++) chars.push(all[r[i] % all.length]);
  // Xáo trộn để 3 ký tự bắt buộc không luôn đứng đầu (Fisher–Yates).
  for (let i = chars.length - 1; i > 0; i--) {
    const j = r[len + i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

function exportCSV(devices) {
  const cols = ['Tên thiết bị','Serial','Model','iOS','Pin','Dung lượng','Trạng thái','Cửa hàng','Khách hàng','SĐT','Thanh toán/tháng','Còn lại','Ngày trả','Người tạo','Tạo lúc'];
  const rows = devices.map(d => [d.name, d.serial, d.model, d.os, d.battery+'%', d.storage, d.status, d.location_name||'', d.owner_name||'', d.owner_phone||'', d.monthly_amount??'', d.remaining??'', d.installment_day??'', d.enrolled_by||'', d.created_at||'']);
  const csv  = [cols, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF'+csv], { type:'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `devices_${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

// ── CSS ────────────────────────────────────────────────────────────────────────

// ── Login ─────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [username, setU] = useState('');
  const [password, setP] = useState('');
  const [code, setCode]  = useState('');
  const [needTOTP, setNeed] = useState(false);
  const [err, setErr]    = useState('');
  const [busy, setBusy]  = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await auth.login(username.trim(), password, needTOTP ? code.trim() : undefined);
      onLogin(r);
    } catch (ex) {
      if (ex.body && ex.body.totp_required) { setNeed(true); setErr(''); }
      else setErr((ex.body && ex.body.error) || 'Đăng nhập thất bại');
    } finally { setBusy(false); }
  }

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <div className="login-logo"><div className="logo-mark logo-mark-lg"><img src="/logo.svg" alt="Zentra MDM" /></div><div><div className="login-title">Zentra MDM</div><div className="login-brand-sub">Hệ thống Quản lý Thiết bị</div></div></div>
        <div className="login-sub">Đăng nhập để quản lý thiết bị</div>
        <label className="fld"><span>Tài khoản</span>
          <input autoFocus value={username} onChange={e => setU(e.target.value)} autoComplete="username" /></label>
        <label className="fld"><span>Mật khẩu</span>
          <input type="password" value={password} onChange={e => setP(e.target.value)} autoComplete="current-password" /></label>
        {needTOTP && (
          <label className="fld"><span>Mã Google Authenticator</span>
            <input autoFocus inputMode="numeric" maxLength={6} placeholder="6 chữ số" value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))} /></label>
        )}
        {err && <div className="login-err">{err}</div>}
        <button className="btn primary login-btn" disabled={busy}>{busy ? 'Đang đăng nhập…' : 'Đăng nhập'}</button>
      </form>
      <style>{GLOBAL_CSS}</style>
    </div>
  );
}

// ── Kho / Tồn kho ─────────────────────────────────────────────────────────────
function WarehouseView({ me, locations, showToast, onOpenDevice, onReloadDevices }) {
  const role = me.role;
  const canManage = canManageRole(role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);          // udid đang kéo
  const [pickStore, setPickStore] = useState({});  // udid -> store id (operator/admin chọn)
  // Bộ lọc kho
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [khoFilter, setKhoFilter] = useState('');
  const load = () => { setLoading(true); query.getWarehouse().then(setItems).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const stores = locations.filter(l => !l.is_warehouse);
  const warehouses = locations.filter(l => l.is_warehouse);
  // Cửa hàng đích được phép kéo về: admin=tất cả; operator=nhóm; manager=cửa hàng mình.
  const myStores = role === 'admin' ? stores
    : stores.filter(l => (me.location_ids || []).includes(l.id) || l.id === me.location_id);

  const toYMD = (s) => { const m = String(s || '').match(/^(\d{2})\/(\d{2})\/(\d{4})/); return m ? `${m[3]}-${m[2]}-${m[1]}` : ''; };
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(d => {
      const matchSearch = !q || [d.name, d.serial, d.imei, d.model, appleModelName(d.product, d.model)].some(v => (v || '').toLowerCase().includes(q));
      const matchKho = !khoFilter || String(d.location_id ?? '') === khoFilter;
      const ymd = toYMD(d.created_at);
      return matchSearch && matchKho && (!fromDate || (ymd && ymd >= fromDate)) && (!toDate || (ymd && ymd <= toDate));
    });
  }, [items, search, khoFilter, fromDate, toDate]);

  async function pull(d) {
    let locId = 0;
    if (role !== 'manager') {
      locId = Number(pickStore[d.udid]) || 0;
      if (!locId) { showToast && showToast('Chọn cửa hàng đích trước', 'error', '❌'); return; }
    }
    if (!(await confirmVN(`Kéo máy "${d.name || d.serial}" từ kho về cửa hàng?`, { title: 'Kéo về cửa hàng', okText: 'Kéo về' }))) return;
    setBusy(d.udid);
    try {
      await query.pullDevice(d.udid, locId);
      showToast && showToast('Đã kéo máy về cửa hàng', 'success', '⬇️');
      load(); onReloadDevices && onReloadDevices();
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setBusy(null); }
  }

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span>📦 Tồn kho ({filtered.length}{filtered.length !== items.length ? `/${items.length}` : ''})</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={load} disabled={loading}>⟳ Làm mới</button>
            <button className="btn" onClick={() => exportKhoCSV(filtered)}>Xuất CSV</button>
          </div>
        </div>
        {/* Tìm kiếm + bộ lọc — cùng khối */}
        <div className="filters-card" style={{ marginBottom: 12 }}>
          <div className="filters-card-title">🔎 Tìm kiếm &amp; bộ lọc</div>
          <div className="search-box">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            <input type="text" placeholder="Tìm tên, serial, IMEI, model…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="filter-bar">
            <label className="filt"><span>Từ ngày (vào kho)</span><FilterDate value={fromDate} max={toDate} onChange={setFromDate} /></label>
            <label className="filt"><span>Đến ngày</span><FilterDate value={toDate} min={fromDate} onChange={setToDate} /></label>
            {warehouses.length > 1 && (
              <label className="filt"><span>Kho</span>
                <select value={khoFilter} onChange={e => setKhoFilter(e.target.value)}>
                  <option value="">Tất cả</option>
                  {warehouses.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
                </select>
              </label>
            )}
            {(search || fromDate || toDate || khoFilter) && (
              <button className="btn" style={{ alignSelf: 'flex-end' }} onClick={() => { setSearch(''); setFromDate(''); setToDate(''); setKhoFilter(''); }}>✕ Xóa lọc</button>
            )}
          </div>
        </div>
        <div className="page-note" style={{ marginBottom: 12 }}>
          Máy đã cài quản lý, đang nằm trong kho và chưa gán khách. {canManage
            ? 'Bấm "Kéo về cửa hàng" để đưa máy xuống cửa hàng rồi nhập khách.'
            : 'Chỉ xem, không sửa được.'}
        </div>
        {loading ? <p style={{ color: 'var(--text3)', fontSize: 13 }}>Đang tải…</p>
          : items.length === 0 ? <div className="page-note">Kho trống — chưa có máy nào.</div>
            : filtered.length === 0 ? <div className="page-note">Không có máy khớp bộ lọc.</div>
              : <>
              {/* Desktop: bảng (ẩn <=768px). Mobile: thẻ .dev-card — giống danh sách Thiết bị. */}
              <div className="tbl-wrap dev-table"><table>
                <thead><tr><th>Thiết bị</th><th>Serial</th><th>IMEI</th><th>Dung lượng</th><th>Kho</th><th>Pin</th><th>Vào kho</th><th>Lần cuối kết nối</th>{canManage && <th></th>}</tr></thead>
                <tbody>
                  {filtered.map(d => (
                    <tr key={d.udid}>
                      <td onClick={() => onOpenDevice && onOpenDevice(d)} style={{ cursor: 'pointer' }}>
                        <div className="dev">
                          <DevicePhoto product={d.product} />
                          <div style={{ minWidth: 0 }}>
                            <b>{appleModelName(d.product, d.model) || d.name || 'iPhone'}</b>
                            <div className="mono" style={{ fontSize: 12, color: 'var(--text3)' }}>{[d.model, d.os && ('iOS ' + d.os)].filter(Boolean).join(' · ')}</div>
                          </div>
                        </div>
                      </td>
                      <td className="mono">{d.serial || '—'}</td>
                      <td className="mono">{d.imei || '—'}</td>
                      <td className="mono">{d.storage || '—'}</td>
                      <td>{d.location_name || '—'}</td>
                      <td>{d.battery != null ? d.battery + '%' : '—'}</td>
                      <td className="mono" style={{ color: 'var(--text2)', fontSize: 12 }}>{d.created_at || '—'}</td>
                      <td className="mono" style={{ color: 'var(--text2)' }}>{d.last_seen ? relTime(d.last_seen) : '—'}</td>
                      {canManage && <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {role !== 'manager' && (
                          <select value={pickStore[d.udid] || ''} onChange={e => setPickStore(p => ({ ...p, [d.udid]: e.target.value }))} style={{ marginRight: 6 }}>
                            <option value="">— Cửa hàng —</option>
                            {myStores.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                          </select>
                        )}
                        <button className="btn primary" disabled={busy === d.udid} onClick={() => pull(d)}>
                          {busy === d.udid ? '⏳…' : '⬇ Kéo về cửa hàng'}
                        </button>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table></div>
              <div className="dev-cards">
                {filtered.map(d => (
                  <div key={d.udid} className="dev-card">
                    <DevicePhoto product={d.product} className="ph dev-card-ph" />
                    <div className="dev-card-body">
                      <div onClick={() => onOpenDevice && onOpenDevice(d)} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div className="dev-card-line1">
                          <span className="dev-card-name">{appleModelName(d.product, d.model) || d.name || 'iPhone'}</span>
                          <span className="pill" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>📦 {d.location_name || 'Kho'}</span>
                        </div>
                        <div className="dev-card-serial mono">{d.serial || (d.udid || '').slice(0, 12)} · iOS {d.os || '—'}</div>
                        <div className="dev-card-owner">{[d.imei && ('IMEI ' + d.imei), d.storage, d.battery != null && ('🔋 ' + d.battery + '%')].filter(Boolean).join(' · ') || '—'}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>Vào kho {d.created_at || '—'}{d.last_seen ? ` · online ${relTime(d.last_seen)}` : ''}</div>
                      </div>
                      {canManage && (
                        <div className="kho-card-actions" onClick={e => e.stopPropagation()}>
                          {role !== 'manager' && (
                            <select value={pickStore[d.udid] || ''} onChange={e => setPickStore(p => ({ ...p, [d.udid]: e.target.value }))}>
                              <option value="">— Cửa hàng —</option>
                              {myStores.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                          )}
                          <button className="btn primary" disabled={busy === d.udid} onClick={() => pull(d)}>
                            {busy === d.udid ? '⏳…' : '⬇ Kéo về cửa hàng'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </>}
      </div>
    </div>
  );
}

// Xuất CSV tồn kho (thông tin nhận dạng máy).
function exportKhoCSV(items) {
  const cols = ['Tên thương mại', 'Tên máy', 'Serial', 'IMEI', 'Model', 'iOS', 'Dung lượng', 'Kho', 'Pin', 'Vào kho', 'Lần cuối online'];
  const rows = items.map(d => [appleModelName(d.product, d.model) || '', d.name || '', d.serial || '', d.imei || '', d.model || '', d.os || '', d.storage || '', d.location_name || '', (d.battery != null ? d.battery + '%' : ''), d.created_at || '', d.last_seen || '']);
  const csv = [cols, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `kho_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

// ── Quản lý người dùng ────────────────────────────────────────────────────────
function UsersView({ me, locations, showToast }) {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);     // user đang sửa hồ sơ
  const [creating, setCreating] = useState(false);   // đang mở modal thêm người dùng
  const [resetting, setResetting] = useState(null);  // user đang đổi mật khẩu
  const isAdmin = me.role === 'admin';
  // Bộ lọc
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState('');

  const load = () => query.listUsers().then(setUsers).catch(e => showToast(e.message, 'error', '❌'));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      const matchSearch = !q || [u.username, u.full_name, (u.configurator_accounts || []).join(' '), u.phone].some(v => (v || '').toLowerCase().includes(q));
      const matchRole = !roleFilter || u.role === roleFilter;
      const matchStore = !storeFilter || String(u.location_id ?? '') === storeFilter || (u.location_ids || []).includes(Number(storeFilter));
      return matchSearch && matchRole && matchStore;
    });
  }, [users, search, roleFilter, storeFilter]);

  // Danh sách cửa hàng cho bộ lọc — CÙNG RULE form thêm người dùng: theo phạm vi user, và
  // KHO chỉ hiện khi lọc vai trò = "Kho" (giống form thêm: chọn role kho mới thấy kho).
  // Mọi vai trò khác (kể cả "Tất cả") -> chỉ CỬA HÀNG BÁN, không lẫn kho.
  const filterStores = useMemo(
    () => storeChoicesFor(me, locations, roleFilter === 'kho' ? 'kho' : 'store'),
    [me, locations, roleFilter],
  );
  // Đổi vai trò khiến cửa hàng đang chọn không còn hợp lệ -> bỏ lọc cửa hàng.
  useEffect(() => {
    if (storeFilter && !filterStores.some(l => String(l.id) === storeFilter)) setStoreFilter('');
  }, [filterStores, storeFilter]);

  async function toggle(u) {
    try { await query.updateUser(u.id, { disabled: !u.disabled }); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }
  async function toggleReq(u) {
    try {
      await query.updateUser(u.id, { totp_required: !u.totp_required });
      showToast(u.totp_required ? 'Đã bỏ yêu cầu 2FA' : 'Đã yêu cầu 2FA', 'success', '🔐');
      load();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }
  async function resetTOTP(u) {
    if (!(await confirmVN(`Reset 2FA của ${u.username}? Họ sẽ phải đăng ký lại từ đầu.`, { title: 'Đặt lại 2FA', okText: 'Đặt lại', danger: true }))) return;
    try { await query.updateUser(u.id, { reset_totp: true }); showToast('Đã reset 2FA', 'success', '🔓'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }
  async function del(u) {
    if (!(await confirmVN(`Xóa người dùng ${u.username}?`, { title: 'Xóa người dùng', okText: 'Xóa', danger: true }))) return;
    try { await query.deleteUser(u.id); showToast('Đã xóa', 'success', '🗑'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center' }}>
          <span>Danh sách người dùng ({filtered.length}{filtered.length !== users.length ? `/${users.length}` : ''})</span>
          <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setCreating(true)}>+ Thêm người dùng</button>
        </div>
        <div className="filters-card" style={{ marginBottom: 12 }}>
          <div className="filters-card-title">🔎 Tìm kiếm &amp; bộ lọc</div>
          <div className="search-box">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            <input type="text" placeholder="Tìm email, tên, tài khoản ABM, số điện thoại…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="filter-bar">
            <label className="filt"><span>Vai trò</span>
              <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                <option value="">Tất cả</option>
                {['admin', 'operator', 'manager', 'staff', 'kho'].map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select></label>
            <label className="filt"><span>Cửa hàng / Kho</span>
              <select value={storeFilter} onChange={e => setStoreFilter(e.target.value)}>
                <option value="">Tất cả</option>
                {filterStores.map(l => <option key={l.id} value={String(l.id)}>{l.name}{l.is_warehouse ? ' (Kho)' : ''}</option>)}
              </select></label>
            {(search || roleFilter || storeFilter) && (
              <button className="btn" style={{ alignSelf: 'flex-end' }} onClick={() => { setSearch(''); setRoleFilter(''); setStoreFilter(''); }}>✕ Xóa lọc</button>
            )}
          </div>
        </div>
        <div className="tbl-wrap dev-table">
          <table>
            <thead><tr><th>Tài khoản</th><th>Vai trò</th><th style={{ width: 200 }}>Cửa hàng</th><th style={{ width: 170 }}>Khách hàng</th><th style={{ width: 190 }}>Tài khoản Apple Configurator</th><th>Liên hệ</th><th>2FA</th><th>Trạng thái</th><th>Thao tác</th></tr></thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{u.username}</div>
                    {u.full_name && <div style={{ fontSize: 12, color: 'var(--text2)' }}>{u.full_name}</div>}
                  </td>
                  <td>
                    <RoleBadge role={u.role} />
                    {u.role === 'operator' && u.al_code_access && (
                      <div style={{ marginTop: 4 }}><span className="badge badge-amber" title="Được cấp quyền xem mã mở khoá kích hoạt">🔑 xem mã mở khoá</span></div>
                    )}
                    {u.no_device_history && (
                      <div style={{ marginTop: 4 }}><span className="badge badge-red" title="Không vào được trang Lịch sử máy">⛔ không xem lịch sử máy</span></div>
                    )}
                  </td>
                  <td style={{ width: 200 }}>
                    {u.role === 'operator'
                      ? (() => {
                        const names = (u.location_ids || []).map(id => (locations.find(l => l.id === id) || {}).name).filter(Boolean);
                        if (!names.length) return <span style={{ color: 'var(--text3)' }}>— nhóm trống —</span>;
                        const shown = names.slice(0, 2).join(', ');
                        const more = names.length - 2;
                        return <span title={names.join(', ')}>{shown}{more > 0 ? <b style={{ color: 'var(--text3)' }}> +{more} nữa</b> : ''}</span>;
                      })()
                      : <div style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.location_name || ''}>{u.location_name || '—'}</div>}
                  </td>
                  {/* Khách hàng suy từ cửa hàng được gán — xem customersOfUser. */}
                  <td style={{ width: 170 }}>
                    {(() => {
                      const custs = customersOfUser(u, locations);
                      if (!custs.length) return <span style={{ color: 'var(--text3)' }}>—</span>;
                      return (
                        <div style={{ maxWidth: 165, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={custs.join('\n')}>{customerLabel(custs)}</div>
                      );
                    })()}
                  </td>
                  <td className="mono" style={{ fontSize: 13, width: 190 }}>
                    <div style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={(u.configurator_accounts || []).join('\n')}>{(u.configurator_accounts || []).length ? u.configurator_accounts.join(', ') : '—'}</div>
                  </td>
                  <td>
                    {u.phone ? <div className="mono" style={{ fontSize: 13 }}>{u.phone}</div> : null}
                    {u.national_id ? <div className="mono" style={{ fontSize: 12, color: 'var(--text2)' }}>CCCD {u.national_id}</div> : null}
                    {!u.phone && !u.national_id ? <span style={{ color: 'var(--text3)' }}>—</span> : null}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {u.totp_required && <span className="badge badge-amber">Bắt buộc</span>}{' '}
                    {u.totp_enabled ? <span className="badge badge-green">Đã bật</span> : <span className="badge badge-gray">Chưa</span>}
                  </td>
                  <td>{u.disabled ? <span className="badge badge-red">Khoá</span> : <span className="badge badge-green">Hoạt động</span>}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="act-btn" onClick={() => setEditing(u)}>Sửa</button>{' '}
                    <button className="act-btn" onClick={() => setResetting(u)}>Đổi MK</button>{' '}
                    {u.id !== me.id && <button className="act-btn" onClick={() => toggleReq(u)}>{u.totp_required ? 'Bỏ 2FA' : 'Yêu cầu 2FA'}</button>}{' '}
                    {u.id !== me.id && u.totp_enabled && <button className="act-btn" onClick={() => resetTOTP(u)}>Reset 2FA</button>}{' '}
                    <button className="act-btn" onClick={() => toggle(u)}>{u.disabled ? 'Mở' : 'Khoá'}</button>{' '}
                    {u.id !== me.id && <button className="act-btn danger-act" onClick={() => del(u)}>Xóa</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile: danh sách dạng thẻ (ẩn bảng, hiện <=768px) — giống tab Thiết bị */}
        <div className="dev-cards">
          {filtered.map(u => {
            const storeText = u.role === 'operator'
              ? (() => {
                const names = (u.location_ids || []).map(id => (locations.find(l => l.id === id) || {}).name).filter(Boolean);
                if (!names.length) return '— nhóm trống —';
                return names.slice(0, 2).join(', ') + (names.length > 2 ? ` +${names.length - 2} nữa` : '');
              })()
              : (u.location_name || '—');
            const custText = customerLabel(customersOfUser(u, locations));
            return (
              <div key={u.id} className="user-card">
                <div className="user-card-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="user-card-name">{u.username}</div>
                    {u.full_name && <div className="user-card-sub">{u.full_name}</div>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flex: 'none' }}>
                    <RoleBadge role={u.role} />
                    {u.role === 'operator' && u.al_code_access && <span className="badge badge-amber" title="Được cấp quyền xem mã mở khoá kích hoạt">🔑 xem mã mở khoá</span>}
                    {u.no_device_history && <span className="badge badge-red" title="Không vào được trang Lịch sử máy">⛔ không xem lịch sử máy</span>}
                  </div>
                </div>
                <div className="user-card-meta">
                  <div><span className="ucl">Cửa hàng</span> {storeText}</div>
                  <div><span className="ucl">Khách hàng</span> {custText}</div>
                  <div><span className="ucl">ABM</span> <span className="mono" style={{ wordBreak: 'break-all' }}>{(u.configurator_accounts || []).length ? u.configurator_accounts.join(', ') : '—'}</span></div>
                  {(u.phone || u.national_id) && <div><span className="ucl">Liên hệ</span> <span className="mono">{[u.phone, u.national_id && ('CCCD ' + u.national_id)].filter(Boolean).join(' · ')}</span></div>}
                  <div><span className="ucl">Trạng thái</span>
                    {u.totp_required && <span className="badge badge-amber" style={{ marginRight: 4 }}>2FA bắt buộc</span>}
                    {u.totp_enabled ? <span className="badge badge-green" style={{ marginRight: 4 }}>2FA bật</span> : <span className="badge badge-gray" style={{ marginRight: 4 }}>2FA chưa</span>}
                    {u.disabled ? <span className="badge badge-red">Khoá</span> : <span className="badge badge-green">Hoạt động</span>}
                  </div>
                </div>
                <div className="user-card-actions">
                  <button className="act-btn" onClick={() => setEditing(u)}>Sửa</button>
                  <button className="act-btn" onClick={() => setResetting(u)}>Đổi MK</button>
                  {u.id !== me.id && <button className="act-btn" onClick={() => toggleReq(u)}>{u.totp_required ? 'Bỏ 2FA' : 'Yêu cầu 2FA'}</button>}
                  {u.id !== me.id && u.totp_enabled && <button className="act-btn" onClick={() => resetTOTP(u)}>Reset 2FA</button>}
                  <button className="act-btn" onClick={() => toggle(u)}>{u.disabled ? 'Mở' : 'Khoá'}</button>
                  {u.id !== me.id && <button className="act-btn danger-act" onClick={() => del(u)}>Xóa</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {editing && <UserEditModal user={editing} locations={locations} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} showToast={showToast} />}
      {creating && <UserCreateModal me={me} locations={locations} onClose={() => setCreating(false)} onCreated={load} showToast={showToast} />}
      {resetting && <PasswordResetModal user={resetting} onClose={() => setResetting(null)} showToast={showToast} />}
    </div>
  );
}

// Trường nhập nhiều tài khoản App Configurator (ABM) — tối đa 3. `rows` là mảng chuỗi
// (luôn có ít nhất 1 dòng để nhập); parent tự lọc rỗng khi submit.
const MAX_ABM_ACCOUNTS = 3;
// Email không chứa khoảng trắng, nhưng dán tay rất dễ lọt một dấu cách GIỮA chuỗi
// ("...limit ed.com") — trim() chỉ cắt hai đầu nên nó lưu được, rồi tài khoản đó
// không bao giờ khớp device_assigned_by của ABM và máy quét bằng nó không được tự
// gán cửa hàng, im lặng không báo gì. Xoá sạch khoảng trắng + ký tự zero-width,
// rồi validate như email trước khi gửi.
const cleanAbm = s => (s || '').replace(/[\s\u200b-\u200d\ufeff]/g, '');
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const cleanAbmList = list => (list || []).map(cleanAbm).filter(Boolean).slice(0, MAX_ABM_ACCOUNTS);
function ConfiguratorAccountsField({ rows, setRows }) {
  const list = rows && rows.length ? rows : [''];
  const update = (i, v) => setRows(list.map((r, idx) => (idx === i ? v : r)));
  const add = () => { if (list.length < MAX_ABM_ACCOUNTS) setRows([...list, '']); };
  const remove = (i) => { const next = list.filter((_, idx) => idx !== i); setRows(next.length ? next : ['']); };
  return (
    <label className="full">
      <span>Tài khoản Apple Configurator (ABM) — tối đa {MAX_ABM_ACCOUNTS}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {list.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 6 }}>
            <input value={r} onChange={e => update(i, e.target.value)} placeholder="email ABM dùng để quét máy — máy quét xong tự vào cửa hàng này" style={{ flex: 1 }} />
            {list.length > 1 && <button type="button" className="btn" title="Xóa tài khoản này" style={{ flex: 'none' }} onClick={() => remove(i)}>✕</button>}
          </div>
        ))}
        {list.length < MAX_ABM_ACCOUNTS && (
          <button type="button" className="btn" style={{ alignSelf: 'flex-start' }} onClick={add}>+ Thêm tài khoản ABM</button>
        )}
      </div>
    </label>
  );
}

// CustomerHint: dòng "tài khoản này thuộc khách hàng nào", đặt ngay dưới ô chọn cửa
// hàng. Chưa gán thì nói thẳng là chưa gán (im lặng thì người dùng lại tưởng không có
// khái niệm này). Phục vụ nhiều khách -> tô vàng: mô hình cho phép, nhưng tick nhầm
// cửa hàng của khách khác là chuyện dễ xảy ra nên phải nhìn thấy.
function CustomerHint({ names = [] }) {
  if (!names.length) {
    return <div className="cust-hint">Khách hàng: <b>— chưa gán —</b> <span className="cust-hint-sub">(gán cửa hàng cho khách ở trang Cửa hàng)</span></div>;
  }
  if (names.length === 1) return <div className="cust-hint">Khách hàng: <b>{names[0]}</b></div>;
  return <div className="cust-hint warn">⚠️ Đang phục vụ <b>{names.length} khách hàng</b>: {names.join(' · ')}</div>;
}

// Modal thêm người dùng — cùng bố cục với UserEditModal cho đồng bộ.
function UserCreateModal({ me, locations = [], onClose, onCreated, showToast }) {
  const EMPTY = { username: '', password: '', role: 'staff', location_id: '', location_ids: [], full_name: '', email: '', phone: '', national_id: '', address: '', configurator_accounts: [''], al_code_access: false, no_device_history: false };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false); // hiện/ẩn ô mật khẩu
  const [created, setCreated] = useState(null); // { username, password } vừa tạo — hiển thị để copy
  const isAdmin = me.role === 'admin';
  // admin tạo mọi role (kèm Kho); điều hành tạo manager/staff; manager tạo staff.
  const roleOpts = isAdmin ? ['staff', 'manager', 'operator', 'admin', 'kho'] : (me.role === 'operator' ? ['staff', 'manager'] : ['staff']);
  // Cửa hàng/Kho được phép gán (rule dùng chung storeChoicesFor): admin=tất cả; điều hành=
  // nhóm mình; manager=cửa hàng mình. Role kho -> chỉ KHO; role khác -> chỉ cửa hàng bán.
  const storeChoices = storeChoicesFor(me, locations, form.role === 'kho' ? 'kho' : 'store');
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  function copy(text, msg = 'Đã copy') {
    navigator.clipboard?.writeText(text).then(() => showToast(msg, 'success', '📋')).catch(() => {});
  }

  async function save(e) {
    e.preventDefault();
    const uname = form.username.trim();
    // Tài khoản đăng nhập BẮT BUỘC là email.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(uname)) {
      showToast('Tài khoản đăng nhập phải là email hợp lệ (vd: nhanvien@zentratech.vn)', 'error', '❌');
      return;
    }
    const pwErr = passwordErr(form.password);
    if (pwErr) { showToast(pwErr, 'error', '❌'); return; }
    const abmAccounts = cleanAbmList(form.configurator_accounts);
    const badAbm = abmAccounts.find(a => !EMAIL_RE.test(a));
    if (badAbm) {
      showToast(`Tài khoản App Configurator không hợp lệ: ${badAbm}`, 'error', '❌');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        username: uname, password: form.password, role: form.role,
        full_name: form.full_name.trim(), email: uname, phone: form.phone.trim(),
        national_id: form.national_id.trim(), address: form.address.trim(),
        configurator_accounts: abmAccounts,
      };
      if (form.role === 'operator') { payload.location_ids = form.location_ids; payload.al_code_access = !!form.al_code_access; }
      else if (form.role !== 'admin') payload.location_id = Number(form.location_id) || 0;
      // Khoá xem "Lịch sử máy" — chỉ admin đặt được (backend cũng chặn).
      if (isAdmin && form.role !== 'admin') payload.no_device_history = !!form.no_device_history;
      await query.createUser(payload);
      showToast('Đã tạo người dùng', 'success', '👤');
      setCreated({ username: payload.username, password: form.password });
      onCreated();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }

  if (created) {
    const both = `Tài khoản: ${created.username}\nMật khẩu: ${created.password}`;
    return (
      <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
        <div className="modal" style={{ width: 480 }} onClick={e => e.stopPropagation()}>
          <div className="modal-head">
            <div className="modal-title-wrap">
              <div className="modal-icon">✅</div>
              <div>
                <div className="modal-device-name">Đã tạo người dùng</div>
                <div className="modal-device-sub">Lưu / gửi thông tin đăng nhập cho người dùng ngay bây giờ</div>
              </div>
            </div>
            <button type="button" className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="tab-body">
            <div className="page-note" style={{ marginBottom: 12 }}>
              ⚠️ Mật khẩu chỉ hiển thị <b>một lần</b> ở đây. Hãy copy và gửi cho người dùng trước khi đóng.
            </div>
            <div className="cred-row">
              <div className="cred-cell">
                <div className="info-cell-label">Tài khoản</div>
                <div className="info-cell-val mono">{created.username}</div>
              </div>
              <button type="button" className="btn" onClick={() => copy(created.username, 'Đã copy tài khoản')}>Copy</button>
            </div>
            <div className="cred-row">
              <div className="cred-cell">
                <div className="info-cell-label">Mật khẩu</div>
                <div className="info-cell-val mono">{created.password}</div>
              </div>
              <button type="button" className="btn" onClick={() => copy(created.password, 'Đã copy mật khẩu')}>Copy</button>
            </div>
            <button type="button" className="btn primary" style={{ width: '100%', marginTop: 12 }} onClick={() => copy(both, 'Đã copy tài khoản & mật khẩu')}>
              📋 Copy tài khoản & mật khẩu
            </button>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" className="btn" style={{ flex: 1 }} onClick={() => { setForm(EMPTY); setCreated(null); }}>+ Tạo người khác</button>
              <button type="button" className="btn" style={{ flex: 1 }} onClick={onClose}>Đóng</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <form className="modal" style={{ width: 560 }} onClick={e => e.stopPropagation()} onSubmit={save}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">➕</div>
            <div>
              <div className="modal-device-name">Thêm người dùng</div>
              <div className="modal-device-sub">Tạo tài khoản đăng nhập cho nhân viên / quản lý</div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div className="uform">
            <label><span>Email (đăng nhập)</span><input type="email" value={form.username} onChange={set('username')} placeholder="nhanvien@zentratech.vn" required autoFocus /></label>
            <label><span>Mật khẩu ({PASSWORD_HINT})</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type={showPw ? 'text' : 'password'} value={form.password} onChange={set('password')} required style={{ flex: 1 }} />
                <button type="button" className="btn" title={showPw ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'} style={{ flex: 'none' }} onClick={() => setShowPw(s => !s)}>{showPw ? '🙈' : '👁'}</button>
                <button type="button" className="btn" title="Tạo mật khẩu ngẫu nhiên" style={{ flex: 'none' }} onClick={() => { setForm(p => ({ ...p, password: randomPassword() })); setShowPw(true); }}>🎲</button>
              </div>
            </label>
            <label><span>Vai trò</span>
              <select value={form.role} onChange={set('role')}>
                {roleOpts.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </label>
            {form.role === 'operator' ? (
              <label className="full">
                <span>Nhóm cửa hàng (Điều hành)</span>
                {/* Gom theo KHÁCH HÀNG: nhìn ô tick là biết tài khoản phục vụ công ty nào,
                    và thấy ngay khi đang tick chéo nhiều khách. */}
                <div className="store-groups">
                  {storeChoices.length === 0 && <span style={{ fontSize: 13, color: 'var(--text3)' }}>Không có cửa hàng</span>}
                  {groupStoresByCustomer(storeChoices).map(([cust, stores]) => (
                    <div key={cust || '__chua_gan__'}>
                      <div className="store-group-label">{cust || 'Chưa gán khách hàng'}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {stores.map(l => {
                          const on = form.location_ids.includes(l.id);
                          return (
                            <label key={l.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', background: on ? 'var(--blue-bg)' : 'var(--bg3)' }}>
                              <input type="checkbox" checked={on} onChange={e => setForm(f => ({ ...f, location_ids: e.target.checked ? [...f.location_ids, l.id] : f.location_ids.filter(x => x !== l.id) }))} />
                              {l.name}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <CustomerHint names={customersOfUser({ role: 'operator', location_ids: form.location_ids }, locations)} />
              </label>
            ) : form.role !== 'admin' ? (
              <label><span>{form.role === 'kho' ? 'Kho' : 'Cửa hàng'}</span>
                <select value={form.location_id} onChange={set('location_id')} required>
                  <option value="">{form.role === 'kho' ? '— Chọn kho —' : '— Chọn cửa hàng —'}</option>
                  {storeChoices.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <CustomerHint names={customersOfUser({ role: form.role, location_id: form.location_id }, locations)} />
              </label>
            ) : null}
            {form.role === 'operator' && (
              <label className="full" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
                <input type="checkbox" style={{ marginTop: 3, width: 'auto' }} checked={!!form.al_code_access}
                  onChange={e => setForm(p => ({ ...p, al_code_access: e.target.checked }))} />
                <span style={{ fontWeight: 400 }}>Cho phép xem <b>mã mở khoá kích hoạt</b> của máy.
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Chỉ XEM mã để mở khoá máy; không được bật khoá hay tạo mã mới (quyền của admin).</div></span>
              </label>
            )}
            {isAdmin && (form.role === 'operator' || form.role === 'manager') && (
              <label className="full" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
                <input type="checkbox" style={{ marginTop: 3, width: 'auto' }} checked={!!form.no_device_history}
                  onChange={e => setForm(p => ({ ...p, no_device_history: e.target.checked }))} />
                <span style={{ fontWeight: 400 }}>KHÔNG cho xem <b>Lịch sử máy</b>.
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Ẩn mục "Lịch sử máy" (máy đã Hoàn tất / Ngưng hợp đồng) khỏi tài khoản này. Các quyền khác giữ nguyên.</div></span>
              </label>
            )}
            <label><span>Họ tên</span><input value={form.full_name} onChange={set('full_name')} placeholder="Nguyễn Văn A" /></label>
            <label><span>Số điện thoại</span><input value={form.phone} onChange={set('phone')} inputMode="tel" /></label>
            <label><span>CCCD</span><input value={form.national_id} onChange={set('national_id')} inputMode="numeric" /></label>
            <ConfiguratorAccountsField rows={form.configurator_accounts} setRows={rows => setForm(p => ({ ...p, configurator_accounts: rows }))} />
            <label className="full"><span>Địa chỉ</span><input value={form.address} onChange={set('address')} /></label>
          </div>
          <button className="btn primary" style={{ width: '100%', marginTop: 12 }} disabled={saving}>
            {saving ? 'Đang tạo…' : '➕ Tạo người dùng'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Modal đổi mật khẩu — cho tạo mật khẩu ngẫu nhiên & copy sau khi đổi thành công.
function PasswordResetModal({ user, onClose, showToast }) {
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false); // đã đổi xong — hiển thị để copy

  function copy(text, msg = 'Đã copy') {
    navigator.clipboard?.writeText(text).then(() => showToast(msg, 'success', '📋')).catch(() => {});
  }

  async function save(e) {
    e.preventDefault();
    const pwErr = passwordErr(password);
    if (pwErr) { showToast(pwErr, 'error', '❌'); return; }
    setSaving(true);
    try {
      await query.updateUser(user.id, { password });
      showToast('Đã đổi mật khẩu', 'success', '🔑');
      setDone(true);
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }

  if (done) {
    const both = `Tài khoản: ${user.username}\nMật khẩu: ${password}`;
    return (
      <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
        <div className="modal" style={{ width: 480 }} onClick={e => e.stopPropagation()}>
          <div className="modal-head">
            <div className="modal-title-wrap">
              <div className="modal-icon">✅</div>
              <div>
                <div className="modal-device-name">Đã đổi mật khẩu</div>
                <div className="modal-device-sub">Gửi mật khẩu mới cho {user.username} ngay bây giờ</div>
              </div>
            </div>
            <button type="button" className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="tab-body">
            <div className="page-note" style={{ marginBottom: 12 }}>
              ⚠️ Mật khẩu chỉ hiển thị <b>một lần</b> ở đây. Hãy copy và gửi cho người dùng trước khi đóng.
            </div>
            <div className="cred-row">
              <div className="cred-cell">
                <div className="info-cell-label">Tài khoản</div>
                <div className="info-cell-val mono">{user.username}</div>
              </div>
              <button type="button" className="btn" onClick={() => copy(user.username, 'Đã copy tài khoản')}>Copy</button>
            </div>
            <div className="cred-row">
              <div className="cred-cell">
                <div className="info-cell-label">Mật khẩu mới</div>
                <div className="info-cell-val mono">{password}</div>
              </div>
              <button type="button" className="btn" onClick={() => copy(password, 'Đã copy mật khẩu')}>Copy</button>
            </div>
            <button type="button" className="btn primary" style={{ width: '100%', marginTop: 12 }} onClick={() => copy(both, 'Đã copy tài khoản & mật khẩu')}>
              📋 Copy tài khoản & mật khẩu
            </button>
            <button type="button" className="btn" style={{ width: '100%', marginTop: 8 }} onClick={onClose}>Đóng</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <form className="modal" style={{ width: 480 }} onClick={e => e.stopPropagation()} onSubmit={save}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">🔑</div>
            <div>
              <div className="modal-device-name">Đổi mật khẩu: {user.username}</div>
              <div className="modal-device-sub">{ROLE_LABEL[user.role] || user.role}{user.location_name ? ` · ${user.location_name}` : ''}</div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div className="uform">
            <label className="full"><span>Mật khẩu mới ({PASSWORD_HINT})</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required autoFocus style={{ flex: 1 }} />
                <button type="button" className="btn" title={showPw ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'} style={{ flex: 'none' }} onClick={() => setShowPw(s => !s)}>{showPw ? '🙈' : '👁'}</button>
                <button type="button" className="btn" title="Tạo mật khẩu ngẫu nhiên" style={{ flex: 'none' }} onClick={() => { setPassword(randomPassword()); setShowPw(true); }}>🎲</button>
              </div>
            </label>
          </div>
          <button className="btn primary" style={{ width: '100%', marginTop: 12 }} disabled={saving}>
            {saving ? 'Đang đổi…' : '🔑 Đổi mật khẩu'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Modal sửa người dùng — cùng bố cục với form Tạo (email đăng nhập, mật khẩu, vai
// trò, cửa hàng, hồ sơ). Các field tài khoản (email/vai trò/cửa hàng) CHỈ admin đổi
// được (backend enforce); người khác chỉ sửa hồ sơ + đặt lại mật khẩu.
function UserEditModal({ user, locations = [], isAdmin, onClose, onSaved, showToast }) {
  const [f, setF] = useState({
    username: user.username || '', password: '', role: user.role,
    location_id: user.location_id || '', location_ids: user.location_ids || [],
    full_name: user.full_name || '', phone: user.phone || '',
    national_id: user.national_id || '', address: user.address || '', note: user.note || '',
    configurator_accounts: (user.configurator_accounts && user.configurator_accounts.length ? [...user.configurator_accounts] : ['']),
    al_code_access: !!user.al_code_access,
    no_device_history: !!user.no_device_history,
  });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const roleOpts = ['staff', 'manager', 'operator', 'admin', 'kho']; // chỉ admin mới sửa được role

  async function save() {
    if (isAdmin && f.username.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.username.trim())) {
      showToast('Email đăng nhập không hợp lệ', 'error', '❌'); return;
    }
    if (f.password && passwordErr(f.password)) {
      showToast(passwordErr(f.password), 'error', '❌'); return;
    }
    const abmAccounts = cleanAbmList(f.configurator_accounts);
    const badAbm = abmAccounts.find(a => !EMAIL_RE.test(a));
    if (badAbm) {
      showToast(`Tài khoản App Configurator không hợp lệ: ${badAbm}`, 'error', '❌'); return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: f.full_name.trim(), phone: f.phone.trim(),
        national_id: f.national_id.trim(), address: f.address.trim(), note: f.note.trim(),
        configurator_accounts: abmAccounts,
      };
      if (f.password) payload.password = f.password;
      if (isAdmin) { // các field tài khoản chỉ admin đổi
        payload.username = f.username.trim();
        payload.role = f.role;
        payload.al_code_access = f.role === 'operator' ? !!f.al_code_access : false;
        payload.no_device_history = f.role !== 'admin' ? !!f.no_device_history : false;
        if (f.role === 'operator') payload.location_ids = f.location_ids;
        else if (f.role !== 'admin') payload.location_id = Number(f.location_id) || 0;
      }
      await query.updateUser(user.id, payload);
      showToast('Đã lưu người dùng', 'success', '💾');
      onSaved();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <div className="modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">👤</div>
            <div>
              <div className="modal-device-name">Sửa người dùng: {user.username}</div>
              <div className="modal-device-sub">
                {ROLE_LABEL[user.role] || user.role}{user.location_name ? ` · ${user.location_name}` : ''}
                {/* Khách hàng theo cửa hàng ĐANG chọn trong form, đổi tick là đổi theo. */}
                {(() => {
                  const custs = customersOfUser({ role: f.role, location_id: f.location_id, location_ids: f.location_ids }, locations);
                  return custs.length ? ` · ${customerLabel(custs)}` : '';
                })()}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div className="uform">
            <label><span>Email (đăng nhập){!isAdmin ? ' — chỉ admin đổi' : ''}</span>
              <input type="email" value={f.username} onChange={set('username')} placeholder="nhanvien@zentratech.vn" disabled={!isAdmin} required />
            </label>
            <label><span>Mật khẩu mới (để trống = giữ nguyên)</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type={showPw ? 'text' : 'password'} value={f.password} onChange={set('password')} style={{ flex: 1 }} placeholder={PASSWORD_HINT} />
                <button type="button" className="btn" title={showPw ? 'Ẩn' : 'Hiện'} style={{ flex: 'none' }} onClick={() => setShowPw(s => !s)}>{showPw ? '🙈' : '👁'}</button>
                <button type="button" className="btn" title="Tạo mật khẩu ngẫu nhiên" style={{ flex: 'none' }} onClick={() => { setF(p => ({ ...p, password: randomPassword() })); setShowPw(true); }}>🎲</button>
              </div>
            </label>
            <label><span>Vai trò{!isAdmin ? ' — chỉ admin đổi' : ''}</span>
              <select value={f.role} onChange={set('role')} disabled={!isAdmin}>
                {roleOpts.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </label>
            {f.role === 'operator' ? (
              <label className="full">
                <span>Nhóm cửa hàng (Điều hành){!isAdmin ? ' — chỉ admin đổi' : ''}</span>
                {/* Gom theo KHÁCH HÀNG — xem groupStoresByCustomer. */}
                <div className="store-groups" style={{ opacity: isAdmin ? 1 : 0.6, pointerEvents: isAdmin ? 'auto' : 'none' }}>
                  {locations.length === 0 && <span style={{ fontSize: 13, color: 'var(--text3)' }}>Chưa có cửa hàng</span>}
                  {groupStoresByCustomer(locations).map(([cust, stores]) => (
                    <div key={cust || '__chua_gan__'}>
                      <div className="store-group-label">{cust || 'Chưa gán khách hàng'}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {stores.map(l => {
                          const on = f.location_ids.includes(l.id);
                          return (
                            <label key={l.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', background: on ? 'var(--blue-bg)' : 'var(--bg3)' }}>
                              <input type="checkbox" checked={on} disabled={!isAdmin} onChange={e => setF(p => ({ ...p, location_ids: e.target.checked ? [...p.location_ids, l.id] : p.location_ids.filter(x => x !== l.id) }))} />
                              {l.name}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <CustomerHint names={customersOfUser({ role: 'operator', location_ids: f.location_ids }, locations)} />
              </label>
            ) : f.role !== 'admin' ? (
              <label><span>{f.role === 'kho' ? 'Kho' : 'Cửa hàng'}{!isAdmin ? ' — chỉ admin đổi' : ''}</span>
                <select value={f.location_id} onChange={set('location_id')} disabled={!isAdmin}>
                  <option value="">{f.role === 'kho' ? '— Chọn kho —' : '— Chọn cửa hàng —'}</option>
                  {locations.filter(l => f.role === 'kho' ? l.is_warehouse : !l.is_warehouse).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <CustomerHint names={customersOfUser({ role: f.role, location_id: f.location_id }, locations)} />
              </label>
            ) : null}
            {f.role === 'operator' && (
              <label className="full" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 8, opacity: isAdmin ? 1 : 0.6 }}>
                <input type="checkbox" style={{ marginTop: 3, width: 'auto' }} checked={!!f.al_code_access} disabled={!isAdmin}
                  onChange={e => setF(p => ({ ...p, al_code_access: e.target.checked }))} />
                <span style={{ fontWeight: 400 }}>Cho phép xem <b>mã mở khoá kích hoạt</b> của máy{!isAdmin ? ' — chỉ admin đổi được' : ''}.
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Chỉ XEM mã để mở khoá máy; không được bật khoá hay tạo mã mới (quyền của admin).</div></span>
              </label>
            )}
            {(f.role === 'operator' || f.role === 'manager') && (
              <label className="full" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 8, opacity: isAdmin ? 1 : 0.6 }}>
                <input type="checkbox" style={{ marginTop: 3, width: 'auto' }} checked={!!f.no_device_history} disabled={!isAdmin}
                  onChange={e => setF(p => ({ ...p, no_device_history: e.target.checked }))} />
                <span style={{ fontWeight: 400 }}>KHÔNG cho xem <b>Lịch sử máy</b>{!isAdmin ? ' — chỉ admin đổi được' : ''}.
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Ẩn mục "Lịch sử máy" (máy đã Hoàn tất / Ngưng hợp đồng) khỏi tài khoản này. Các quyền khác giữ nguyên.</div></span>
              </label>
            )}
            <label><span>Họ tên</span><input value={f.full_name} onChange={set('full_name')} placeholder="Nguyễn Văn A" /></label>
            <label><span>Số điện thoại</span><input value={f.phone} onChange={set('phone')} inputMode="tel" /></label>
            <label><span>CCCD</span><input value={f.national_id} onChange={set('national_id')} inputMode="numeric" /></label>
            <label className="full"><span>Địa chỉ</span><input value={f.address} onChange={set('address')} /></label>
            <ConfiguratorAccountsField rows={f.configurator_accounts} setRows={rows => setF(p => ({ ...p, configurator_accounts: rows }))} />
            <label className="full"><span>Ghi chú</span><textarea rows={2} value={f.note} onChange={set('note')} /></label>
          </div>
          <button className="btn primary" style={{ width: '100%', marginTop: 12 }} disabled={saving} onClick={save}>
            {saving ? 'Đang lưu…' : '💾 Lưu người dùng'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quản lý cửa hàng ──────────────────────────────────────────────────────────
function LocationsView({ showToast, onChange }) {
  const [locs, setLocs] = useState([]);
  const [creating, setCreating] = useState(false); // đang mở modal thêm cửa hàng
  const [editing, setEditing] = useState(null);    // cửa hàng đang sửa
  const load = () => query.listLocations().then(setLocs).catch(e => showToast(e.message, 'error', '❌'));
  useEffect(() => { load(); }, []);

  async function del(l) {
    if (!(await confirmVN(`Xóa cửa hàng "${l.name}"? Thiết bị/nhân viên gán vào sẽ bị gỡ liên kết.`, { title: 'Xóa cửa hàng', okText: 'Xóa', danger: true }))) return;
    try { await query.deleteLocation(l.id); showToast('Đã xóa', 'success', '🗑'); load(); onChange && onChange(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center' }}>
          <span>Danh sách cửa hàng ({locs.length})</span>
          <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setCreating(true)}>+ Thêm cửa hàng</button>
        </div>
        <div className="tbl-wrap dev-table">
          <table>
            <thead><tr><th>Tên</th><th>Khách hàng</th><th>Số điện thoại</th><th>Hotline</th><th>Địa chỉ</th><th>Thao tác</th></tr></thead>
            <tbody>
              {locs.map(l => (
                <tr key={l.id}>
                  <td style={{ fontWeight: 500 }}>{l.name}{l.is_warehouse && <span className="pill" style={{ marginLeft: 6, background: 'var(--amber-bg)', color: 'var(--amber)' }}>📦 Kho</span>}</td>
                  <td>{l.customer_name || <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                  <td className="mono">{l.phone || '—'}</td>
                  <td className="mono">{l.hotline || '—'}</td>
                  <td>{l.address || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="act-btn" onClick={() => setEditing(l)}>Sửa</button>{' '}
                    <button className="act-btn danger-act" onClick={() => del(l)}>Xóa</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile: thẻ (ẩn bảng, hiện <=768px) */}
        <div className="dev-cards">
          {locs.map(l => (
            <div key={l.id} className="user-card">
              <div className="user-card-head">
                <div className="user-card-name">{l.name}{l.is_warehouse && <span className="pill" style={{ marginLeft: 6, background: 'var(--amber-bg)', color: 'var(--amber)' }}>📦 Kho</span>}</div>
              </div>
              <div className="user-card-meta">
                <div><span className="ucl">Khách hàng</span> {l.customer_name || '—'}</div>
                <div><span className="ucl">SĐT</span> <span className="mono">{l.phone || '—'}</span></div>
                <div><span className="ucl">Hotline</span> <span className="mono">{l.hotline || '—'}</span></div>
                <div><span className="ucl">Địa chỉ</span> {l.address || '—'}</div>
              </div>
              <div className="user-card-actions">
                <button className="act-btn" onClick={() => setEditing(l)}>Sửa</button>
                <button className="act-btn danger-act" onClick={() => del(l)}>Xóa</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {(creating || editing) && (
        <LocationModal
          location={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); onChange && onChange(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// Modal thêm/sửa cửa hàng — cùng bố cục với các modal khác cho đồng bộ.
function LocationModal({ location, onClose, onSaved, showToast }) {
  const editing = !!location;
  const [form, setForm] = useState({
    name: location?.name || '', phone: location?.phone || '', hotline: location?.hotline || '', address: location?.address || '',
    is_warehouse: !!location?.is_warehouse,
    customer_id: location?.customer_id != null ? String(location.customer_id) : '',
    rental_enabled: !!location?.rental_enabled,
  });
  const [customers, setCustomers] = useState([]);
  useEffect(() => { query.listCustomers().then(setCustomers).catch(() => setCustomers([])); }, []);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), phone: form.phone.trim(), hotline: form.hotline.trim(),
        address: form.address.trim(), is_warehouse: form.is_warehouse,
        customer_id: form.customer_id === '' ? null : Number(form.customer_id),
        rental_enabled: form.rental_enabled,
      };
      if (editing) await query.updateLocation(location.id, payload);
      else await query.createLocation(payload);
      showToast(editing ? 'Đã lưu cửa hàng' : 'Đã tạo cửa hàng', 'success', '🏬');
      onSaved();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <form className="modal" style={{ width: 480 }} onClick={e => e.stopPropagation()} onSubmit={save}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">🏬</div>
            <div>
              <div className="modal-device-name">{editing ? 'Sửa cửa hàng' : 'Thêm cửa hàng'}</div>
              <div className="modal-device-sub">{editing ? location.name : 'Tạo cửa hàng để gán thiết bị và nhân viên'}</div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div className="uform">
            <label className="full"><span>Tên cửa hàng</span><input value={form.name} onChange={set('name')} placeholder="72 Quang Trung" required autoFocus /></label>
            <label className="full"><span>Số điện thoại (tùy chọn)</span><input value={form.phone} onChange={set('phone')} inputMode="tel" placeholder="0901234567" /></label>
            <label className="full"><span>Hotline (tùy chọn)</span><input value={form.hotline} onChange={set('hotline')} inputMode="tel" placeholder="1900xxxx" /></label>
            <label className="full"><span>Địa chỉ (tùy chọn)</span><input value={form.address} onChange={set('address')} /></label>
            <label className="full"><span>Khách hàng thuê bao (tùy chọn)</span>
              <select value={form.customer_id} onChange={set('customer_id')}>
                <option value="">— Chưa gán —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.plan_label})</option>)}
              </select>
            </label>
            <label className="full" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={form.is_warehouse} onChange={e => setForm(p => ({ ...p, is_warehouse: e.target.checked }))} />
              <span style={{ margin: 0 }}>Là <b>Kho</b> (không phải cửa hàng bán — máy cài vào đây là hàng tồn kho)</span>
            </label>
            {!form.is_warehouse && (
              <label className="full" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={form.rental_enabled} onChange={e => setForm(p => ({ ...p, rental_enabled: e.target.checked }))} />
                <span style={{ margin: 0 }}>Cho phép <b>Cho thuê máy</b> (hồ sơ khách được chọn thuê thay cho thanh toán định kỳ)</span>
              </label>
            )}
          </div>
          <button className="btn primary" style={{ width: '100%', marginTop: 12 }} disabled={saving}>
            {saving ? 'Đang lưu…' : (editing ? '💾 Lưu cửa hàng' : '🏬 Tạo cửa hàng')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Khách hàng thuê bao (vendor) — admin quản lý DN mua gói ZENTRA ─────────────
const PLAN_OPTIONS = [
  { value: 'khoi_dau', label: 'Khởi Đầu' },
  { value: 'cua_hang', label: 'Cửa Hàng' },
  { value: 'chuoi_300', label: 'Chuỗi 300' },
  { value: 'chuoi_vo_han', label: 'Chuỗi Vô Hạn' },
  { value: 'doanh_nghiep', label: 'Doanh Nghiệp' },
];
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Chờ duyệt' },
  { value: 'active', label: 'Đang hoạt động' },
  { value: 'trial', label: 'Dùng thử' },
  { value: 'suspended', label: 'Tạm ngưng' },
  { value: 'expired', label: 'Hết hạn' },
];
// ── Ô tên đăng nhập cổng khách ────────────────────────────────────────────────
// Người tạo chỉ gõ phần đầu, đuôi hiện sẵn bên phải cho thấy tên đầy đủ sẽ ra sao.
// NỐI ĐUÔI THẬT LÀ VIỆC CỦA BACKEND (normalizePortalUsername trong portal.go) — ở đây
// chỉ hiển thị, nên gửi nguyên chuỗi người dùng gõ, không tự ghép rồi gửi đi.
// Hằng này phải khớp portalUserDomain bên Go.
const PORTAL_USER_DOMAIN = '@portal.zentratech.vn';

function PortalUsernameInput({ value, onChange, autoFocus }) {
  // Gõ sẵn "@" (tài khoản cũ, hoặc cố ý đặt tên ngoài quy ước) thì ẩn đuôi đi, vì lúc
  // đó backend giữ nguyên chuỗi — hiện tiếp sẽ thành nói dối người dùng.
  const custom = value.includes('@');
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input value={value} onChange={onChange} placeholder="nv.quan1" autoComplete="off"
        required autoFocus={autoFocus} style={{ flex: 1, minWidth: 0 }} />
      {!custom && (
        <span style={{ flex: 'none', fontSize: 13, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
          {PORTAL_USER_DOMAIN}
        </span>
      )}
    </div>
  );
}

const capText = (n) => (n === null || n === undefined ? '∞' : n);

// Badge số ngày còn lại tới hạn: quá hạn = đỏ, ≤14 ngày = vàng, còn lại = xanh.
function ExpiryBadge({ days, date }) {
  if (days === null || days === undefined) return <span className="badge badge-gray">Không đặt hạn</span>;
  const cls = days < 0 ? 'badge-red' : days <= 14 ? 'badge-amber' : 'badge-green';
  const txt = days < 0 ? `Quá hạn ${-days} ngày` : `Còn ${days} ngày`;
  return <span className={`badge ${cls}`} title={date || ''}>{txt}</span>;
}

// ── Sổ thu/chi tổng (admin): thu khách + chi HH + chi ngoại lệ ──────────────
function LedgerView({ showToast }) {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [from, setFrom] = useState(firstDay);
  const [to, setTo] = useState(lastDay);
  const [rep, setRep] = useState(null);
  const [adding, setAdding] = useState(false);     // modal chi phí ngoại lệ
  const [collecting, setCollecting] = useState(false); // modal thu tiền khách
  const [custs, setCusts] = useState([]);          // danh sách khách cho ô chọn khi thu tiền
  const [q, setQ] = useState('');                  // ô tìm: mã chứng từ hoặc nội dung
  const [qLive, setQLive] = useState('');          // giá trị đang gõ (chưa gọi API)
  const [evi, setEvi] = useState(null);            // giao dịch đang xem bằng chứng
  const load = () => query.getLedger({ from, to, q }).then(setRep).catch(e => showToast(e.message, 'error', '❌'));
  useEffect(() => { load(); }, [from, to, q]);
  // Gõ tới đâu tìm tới đó, nhưng chờ 350ms cho hết nhịp gõ mới gọi server.
  useEffect(() => {
    const t = setTimeout(() => setQ(qLive.trim()), 350);
    return () => clearTimeout(t);
  }, [qLive]);
  // Nạp một lần: khoản thu luôn phải gắn với MỘT khách hàng (thu = customer_payments).
  useEffect(() => { query.listCustomers().then(setCusts).catch(() => setCusts([])); }, []);

  const kindLabel = { thu: 'Thu', chi_hh: 'Chi HH', chi_khac: 'Chi khác' };
  const kindColor = (k) => (k === 'thu' ? 'var(--green)' : 'var(--red)');

  function exportCSV() {
    if (!rep) return;
    const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const lines = ['Ngày,Mã chứng từ,Loại,Mô tả,Bằng chứng,Thu,Chi'];
    for (const t of rep.txns) lines.push([t.date, t.code || '', kindLabel[t.kind], t.desc, t.files || 0, t.kind === 'thu' ? t.amount : '', t.kind !== 'thu' ? t.amount : ''].map(esc).join(','));
    lines.push(['', '', '', 'TỔNG', '', rep.total_in, rep.total_out].map(esc).join(','));
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `so-thu-chi-${from}_${to}.csv`; document.body.appendChild(a); a.click(); a.remove();
  }

  return (
    <div className="page">
      <div className="page-card" style={{ marginBottom: 16 }}>
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>Sổ thu / chi</span>
          <label style={{ marginLeft: 'auto', fontSize: 13 }}>Từ <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ padding: '5px 8px', border: '1px solid var(--border2)', borderRadius: 8 }} /></label>
          <label style={{ fontSize: 13 }}>đến <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ padding: '5px 8px', border: '1px solid var(--border2)', borderRadius: 8 }} /></label>
          <button className="btn" onClick={() => { setFrom(firstDay); setTo(lastDay); }}>Tháng này</button>
          <input value={qLive} onChange={e => setQLive(e.target.value)} placeholder="Tìm mã chứng từ / nội dung…"
            style={{ padding: '6px 10px', border: '1px solid var(--border2)', borderRadius: 8, minWidth: 210, fontSize: 13 }} />
          <button className="btn" onClick={exportCSV} disabled={!rep || !rep.txns.length}>⬇ CSV</button>
          <button className="btn primary" onClick={() => setCollecting(true)}>+ Thu tiền</button>
          <button className="btn" onClick={() => setAdding(true)}>+ Chi phí</button>
        </div>
        {rep && (
          <div style={{ padding: '4px 16px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
            {[
              { label: 'Tổng thu', val: rep.total_in, color: 'var(--green)', ico: '📥' },
              { label: 'Tổng chi', val: rep.total_out, color: 'var(--red)', ico: '📤' },
              { label: 'Số dư (thu − chi)', val: rep.balance, color: rep.balance >= 0 ? 'var(--green)' : 'var(--red)', ico: '💼' },
            ].map(k => (
              <div key={k.label} className="stat-card">
                <div className="stat-top">{k.label}<span className="stat-ico">{k.ico}</span></div>
                <div style={{ fontSize: 20, fontWeight: 700, color: k.color, marginTop: 4 }}>{vnd(k.val)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Biểu đồ nằm giữa thẻ tổng và bảng: cùng một lát dữ liệu do bộ lọc phía trên
          quyết định, nên ba chỗ luôn khớp số. */}
      {rep && rep.txns.length > 0 && (
        <div className="page-card" style={{ marginBottom: 16 }}>
          <LedgerChart rep={rep} from={from} to={to} />
        </div>
      )}

      <div className="page-card">
        <div className="page-card-title">Giao dịch ({rep ? rep.txns.length : 0})</div>
        <div className="tbl-wrap dev-table">
          <table>
            <thead><tr><th>Ngày</th><th style={{ width: 130 }}>Mã chứng từ</th><th>Loại</th><th>Mô tả</th><th style={{ width: 110 }}>Bằng chứng</th><th style={{ textAlign: 'right' }}>Thu</th><th style={{ textAlign: 'right' }}>Chi</th></tr></thead>
            <tbody>
              {rep && rep.txns.map((t, i) => (
                <tr key={t.code || i} className="txn-row" onClick={() => setEvi(t)}
                  title="Bấm để xem chi tiết giao dịch">
                  <td className="mono">{t.date}</td>
                  <td className="mono" style={{ fontSize: 12 }}>{t.code || '—'}</td>
                  <td><span style={{ color: kindColor(t.kind), fontWeight: 600 }}>{kindLabel[t.kind]}</span></td>
                  <td>{t.desc}</td>
                  <td>
                    {/* Cùng mở chi tiết — chỗ xem và đính kèm bằng chứng nằm trong đó. */}
                    <button className="act-btn" onClick={e => { e.stopPropagation(); setEvi(t); }}
                      title={t.files ? `${t.files} bằng chứng` : 'Chưa có bằng chứng — bấm để đính kèm'}>
                      📎 {t.files ? t.files : '+'}
                    </button>
                  </td>
                  <td style={{ textAlign: 'right' }} className="mono">{t.kind === 'thu' ? vnd(t.amount) : ''}</td>
                  <td style={{ textAlign: 'right', color: 'var(--red)' }} className="mono">{t.kind !== 'thu' ? vnd(t.amount) : ''}</td>
                </tr>
              ))}
              {rep && rep.txns.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)', padding: 24 }}>{q ? `Không thấy giao dịch nào khớp "${q}"` : 'Không có giao dịch trong khoảng này'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {adding && <ExpenseModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} showToast={showToast} />}
      {evi && (
        <LedgerTxnModal
          kind={evi.kind} id={evi.id}
          onClose={() => { setEvi(null); load(); }}
          onChanged={() => {}}
          showToast={showToast}
        />
      )}
      {/* Thu tiền: dùng lại modal của trang Khách hàng, chỉ thêm ô chọn khách. */}
      {collecting && (
        <CustomerPaymentsModal
          customers={custs}
          onClose={() => { setCollecting(false); load(); }}
          onChanged={load}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// Hạng mục chi phí ngoại lệ. "Khác" mở thêm ô ghi rõ — trước đây ô này là input tự do
// kèm <datalist>, nhưng datalist LỌC theo chữ đang có trong ô, mà ô lại điền sẵn "Thuê
// server", nên bung ra chỉ thấy đúng một dòng: coi như không ai biết còn hạng mục nào.
const EXPENSE_CATS = ['Thuê server', 'Tên miền', 'Marketing', 'Lương', 'Apple/ABM', 'Khác'];

// Modal thêm chi phí ngoại lệ (thuê server, marketing…).
function ExpenseModal({ onClose, onSaved, showToast }) {
  const [form, setForm] = useState({ amount: '', spent_on: '', category: 'Thuê server', note: '' });
  const [catOther, setCatOther] = useState(''); // ghi rõ hạng mục khi chọn "Khác"
  const [saving, setSaving] = useState(false);
  // Ghi xong KHÔNG đóng ngay: giữ modal lại để đính kèm hoá đơn/biên lai của khoản
  // vừa ghi, vì đó là lúc chứng từ đang cầm trên tay.
  const [saved, setSaved] = useState(null); // { id, code }
  const [pending, setPending] = useState([]); // chứng từ chọn TRƯỚC khi ghi
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  async function save(e) {
    e.preventDefault();
    if (saved) return; // đã ghi rồi — Enter lần nữa không được tạo khoản trùng
    if (!form.amount || Number(form.amount) <= 0) { showToast('Nhập số tiền > 0', 'error', '❌'); return; }
    setSaving(true);
    try {
      // Chọn "Khác" mà không ghi rõ thì cứ ghi là "Khác" — vẫn hơn để trống hạng mục.
      const category = form.category === 'Khác' ? (catOther.trim() || 'Khác') : form.category;
      const res = await query.addExpense({ amount: Number(form.amount), spent_on: form.spent_on, category, note: form.note.trim() });
      let failed = 0;
      if (res && res.id && pending.length) failed = await uploadPending('chi_khac', res.id, pending);
      showToast(
        failed ? `Đã ghi ${res.code || 'chi phí'} nhưng ${failed} file chưa lên — đính kèm lại bên dưới`
          : 'Đã ghi chi phí' + (res && res.code ? ` — ${res.code}` : ''),
        failed ? 'error' : 'success', failed ? '⚠️' : '📤');
      setPending([]);
      setSaved(res || {});
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }
  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <form className="modal" style={{ width: 460 }} onClick={e => e.stopPropagation()} onSubmit={save}>
        <div className="modal-head">
          <div className="modal-title-wrap"><div className="modal-icon">📤</div>
            <div><div className="modal-device-name">Thêm chi phí</div>
              <div className="modal-device-sub">Chi phí ngoại lệ: thuê server, marketing, lương…</div></div></div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body"><div className="uform">
          <label><span>Số tiền (VND)</span><input type="number" min="0" step="1000" value={form.amount} onChange={set('amount')} required autoFocus /></label>
          <label><span>Ngày chi</span><input type="date" value={form.spent_on} onChange={set('spent_on')} /></label>
          <label className={form.category === 'Khác' ? '' : 'full'}><span>Hạng mục</span>
            <select value={form.category} onChange={set('category')}>
              {EXPENSE_CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          {form.category === 'Khác' && (
            <label><span>Ghi rõ hạng mục</span>
              <input value={catOther} onChange={e => setCatOther(e.target.value)} placeholder="vd Phí ngân hàng" />
            </label>
          )}
          <label className="full"><span>Ghi chú</span><input value={form.note} onChange={set('note')} placeholder="vd Vultr tháng 8" /></label>
          {!saved && (
            <div className="full" style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>Chứng từ (ảnh / PDF)</div>
              <PendingEvidence files={pending} setFiles={setPending} showToast={showToast} />
            </div>
          )}
          {saved ? (
            <div className="full" style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600, marginBottom: 6 }}>
                ✓ Đã ghi {saved.code ? <span className="mono">{saved.code}</span> : null} — đính kèm hoá đơn / biên lai:
              </div>
              <LedgerEvidence kind="chi_khac" id={saved.id} showToast={showToast} />
              <button type="button" className="btn primary" style={{ width: '100%', marginTop: 12 }} onClick={onSaved}>Xong</button>
            </div>
          ) : (
            <button className="btn primary full" style={{ width: '100%' }} disabled={saving}>{saving ? 'Đang lưu…' : '📤 Ghi chi phí'}</button>
          )}
        </div></div>
      </form>
    </div>
  );
}

// ── Cộng tác viên (admin): CTV + hoa hồng + chi ─────────────────────────────
function CollaboratorsView({ showToast }) {
  const [rows, setRows] = useState([]);
  const [fin, setFin] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payoutFor, setPayoutFor] = useState(null);
  const [usersFor, setUsersFor] = useState(null); // CTV đang quản lý tài khoản đăng nhập
  const load = () => {
    query.listCollaborators().then(setRows).catch(e => showToast(e.message, 'error', '❌'));
    query.getFinanceReport().then(setFin).catch(() => setFin(null));
  };
  useEffect(() => { load(); }, []);

  async function del(c) {
    if (!(await confirmVN(`Xoá CTV "${c.name}"? Các lần chi cho CTV này cũng bị xoá; khách đang gắn sẽ bỏ gắn CTV.`, { title: 'Xoá CTV', okText: 'Xoá', danger: true }))) return;
    try { await query.deleteCollaborator(c.id); showToast('Đã xoá', 'success', '🗑'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  return (
    <div className="page">
      {fin && (
        <div className="page-card" style={{ marginBottom: 16 }}>
          <div className="page-card-title">Báo cáo thu / chi</div>
          <div style={{ padding: '4px 16px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
            {[
              { label: 'Đã thu (khách)', val: fin.total_collected, color: 'var(--green)', ico: '💰' },
              { label: 'Hoa hồng dự tính', val: fin.commission_est, color: 'var(--amber)', ico: '📊' },
              { label: 'Hoa hồng đã chi', val: fin.commission_paid, color: 'var(--blue)', ico: '💸' },
              { label: 'Lợi nhuận (thu − chi HH)', val: fin.net_profit, color: fin.net_profit >= 0 ? 'var(--green)' : 'var(--red)', ico: '📈' },
            ].map(k => (
              <div key={k.label} className="stat-card">
                <div className="stat-top">{k.label}<span className="stat-ico">{k.ico}</span></div>
                <div style={{ fontSize: 19, fontWeight: 700, color: k.color, marginTop: 4 }}>{vnd(k.val)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center' }}>
          <span>Cộng tác viên ({rows.length})</span>
          <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setCreating(true)}>+ Thêm CTV</button>
        </div>
        <div className="tbl-wrap dev-table">
          <table>
            <thead><tr>
              <th>Tên</th><th>SĐT</th><th style={{ textAlign: 'right' }}>Khách</th>
              <th style={{ textAlign: 'right' }}>HH dự tính</th><th style={{ textAlign: 'right' }}>Đã chi</th>
              <th style={{ textAlign: 'right' }}>Còn lại</th><th>Thao tác</th>
            </tr></thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td className="mono">{c.phone || '—'}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{c.customers}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{vnd(c.commission_est)}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{vnd(c.paid_out)}</td>
                  <td style={{ textAlign: 'right' }} className="mono">
                    <span style={{ color: c.remaining > 0 ? 'var(--amber)' : 'var(--text3)', fontWeight: c.remaining > 0 ? 700 : 400 }}>{vnd(c.remaining)}</span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="act-btn" onClick={() => setPayoutFor(c)}>Chi HH</button>{' '}
                    <button className="act-btn" onClick={() => setUsersFor(c)}>Tài khoản</button>{' '}
                    <button className="act-btn" onClick={() => setEditing(c)}>Sửa</button>{' '}
                    <button className="act-btn danger-act" onClick={() => del(c)}>Xoá</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)', padding: 24 }}>Chưa có CTV nào</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {(creating || editing) && (
        <CollaboratorModal collaborator={editing} onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }} showToast={showToast} />
      )}
      {payoutFor && (
        <PayoutsModal collaborator={payoutFor} onClose={() => setPayoutFor(null)} onChanged={load} showToast={showToast} />
      )}
      {usersFor && (
        <CollaboratorUsersModal collaborator={usersFor} onClose={() => setUsersFor(null)} showToast={showToast} />
      )}
    </div>
  );
}

// Modal quản lý TÀI KHOẢN ĐĂNG NHẬP cổng CTV (collaborator_users) của 1 CTV.
function CollaboratorUsersModal({ collaborator, onClose, showToast }) {
  const [rows, setRows] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => query.listCollaboratorUsers(collaborator.id).then(setRows).catch(e => showToast(e.message, 'error', '❌'));
  useEffect(() => { load(); }, [collaborator.id]);

  async function create(e) {
    e.preventDefault();
    if (username.trim().length < 3) { showToast('Tên đăng nhập tối thiểu 3 ký tự', 'error', '❌'); return; }
    const pwErr = passwordErr(password);
    if (pwErr) { showToast(pwErr, 'error', '❌'); return; }
    setBusy(true);
    try {
      await query.createCollaboratorUser(collaborator.id, { username: username.trim(), password });
      showToast('Đã tạo tài khoản CTV', 'success', '✅');
      setUsername(''); setPassword(''); load();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setBusy(false); }
  }
  async function resetPw(u) {
    const pw = await promptVN(`Mật khẩu mới cho "${u.username}" (${PASSWORD_HINT}):`,
      randomPassword(), { title: 'Đổi mật khẩu', okText: 'Đổi' });
    if (pw == null) return;
    const pwErr = passwordErr(pw);
    if (pwErr) { showToast(pwErr, 'error', '❌'); return; }
    try { await query.updateCollaboratorUser(collaborator.id, u.id, { password: pw }); showToast('Đã đổi mật khẩu', 'success', '🔑'); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }
  async function toggle(u) {
    try { await query.updateCollaboratorUser(collaborator.id, u.id, { disabled: !u.disabled }); showToast(u.disabled ? 'Đã mở khoá' : 'Đã khoá', 'success', '✅'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }
  async function del(u) {
    if (!(await confirmVN(`Xoá tài khoản "${u.username}"?`, { title: 'Xoá tài khoản', okText: 'Xoá', danger: true }))) return;
    try { await query.deleteCollaboratorUser(collaborator.id, u.id); showToast('Đã xoá', 'success', '🗑'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <div className="modal" style={{ width: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title-wrap"><div className="modal-icon">🔑</div><div><div className="modal-device-name">Tài khoản cổng CTV</div><div className="modal-device-sub">{collaborator.name}</div></div></div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 16 }}>
          <div className="tbl-wrap dev-table" style={{ marginBottom: 14 }}>
            <table><thead><tr><th>Tài khoản</th><th>Trạng thái</th><th>Thao tác</th></tr></thead>
              <tbody>
                {rows.map(u => (
                  <tr key={u.id}>
                    <td className="mono">{u.username}</td>
                    <td>{u.disabled ? <span className="badge badge-red">Đã khoá</span> : <span className="badge badge-green">Hoạt động</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="act-btn" onClick={() => resetPw(u)}>Đổi MK</button>{' '}
                      <button className="act-btn" onClick={() => toggle(u)}>{u.disabled ? 'Mở' : 'Khoá'}</button>{' '}
                      <button className="act-btn danger-act" onClick={() => del(u)}>Xoá</button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text3)', padding: 18 }}>Chưa có tài khoản. Tạo bên dưới.</td></tr>}
              </tbody>
            </table>
          </div>
          <form onSubmit={create} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Tạo tài khoản mới</div>
            <input placeholder="Tên đăng nhập" value={username} onChange={e => setUsername(e.target.value)} style={{ padding: '9px 11px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontFamily: 'inherit', background: 'var(--bg2)', color: 'var(--text)' }} />
            <input placeholder={`Mật khẩu — ${PASSWORD_HINT}`} type="text" value={password} onChange={e => setPassword(e.target.value)} style={{ padding: '9px 11px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontFamily: 'inherit', background: 'var(--bg2)', color: 'var(--text)' }} />
            <button className="btn primary" disabled={busy} type="submit">{busy ? 'Đang tạo…' : '+ Tạo tài khoản'}</button>
          </form>
        </div>
      </div>
    </div>
  );
}

function CollaboratorModal({ collaborator, onClose, onSaved, showToast }) {
  const editing = !!collaborator;
  const [form, setForm] = useState({
    name: collaborator?.name || '', phone: collaborator?.phone || '',
    email: collaborator?.email || '', bank: collaborator?.bank || '', note: collaborator?.note || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  async function save(e) {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(), bank: form.bank.trim(), note: form.note.trim() };
      if (editing) await query.updateCollaborator(collaborator.id, payload);
      else await query.createCollaborator(payload);
      showToast(editing ? 'Đã lưu CTV' : 'Đã tạo CTV', 'success', '🤝'); onSaved();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }
  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <form className="modal" style={{ width: 460 }} onClick={e => e.stopPropagation()} onSubmit={save}>
        <div className="modal-head">
          <div className="modal-title-wrap"><div className="modal-icon">🤝</div>
            <div><div className="modal-device-name">{editing ? 'Sửa CTV' : 'Thêm CTV'}</div>
              <div className="modal-device-sub">Cộng tác viên giới thiệu khách</div></div></div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body"><div className="uform">
          <label className="full"><span>Tên CTV</span><input value={form.name} onChange={set('name')} required autoFocus /></label>
          <label><span>Số điện thoại</span><input value={form.phone} onChange={set('phone')} inputMode="tel" /></label>
          <label><span>Email</span><input value={form.email} onChange={set('email')} inputMode="email" /></label>
          <label className="full"><span>Tài khoản ngân hàng</span><input value={form.bank} onChange={set('bank')} placeholder="STK - Ngân hàng - Chủ TK" /></label>
          <label className="full"><span>Ghi chú</span><input value={form.note} onChange={set('note')} /></label>
          <button className="btn primary full" style={{ width: '100%' }} disabled={saving}>{saving ? 'Đang lưu…' : (editing ? '💾 Lưu' : '🤝 Tạo CTV')}</button>
        </div></div>
      </form>
    </div>
  );
}

// Modal chi hoa hồng cho 1 CTV: dự tính vs đã chi + danh sách + thêm/xoá.
function PayoutsModal({ collaborator, onClose, onChanged, showToast }) {
  const [rows, setRows] = useState([]);
  const [custs, setCusts] = useState([]);
  const [form, setForm] = useState({ amount: '', paid_on: '', note: '' });
  const [saving, setSaving] = useState(false);
  const load = () => {
    query.listPayouts(collaborator.id).then(setRows).catch(e => showToast(e.message, 'error', '❌'));
    query.listCollaboratorCustomers(collaborator.id).then(setCusts).catch(() => setCusts([]));
  };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  async function add(e) {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { showToast('Nhập số tiền > 0', 'error', '❌'); return; }
    setSaving(true);
    try {
      await query.addPayout(collaborator.id, { amount: Number(form.amount), paid_on: form.paid_on, note: form.note.trim() });
      showToast('Đã ghi nhận chi', 'success', '💸'); setForm({ amount: '', paid_on: '', note: '' });
      load(); onChanged && onChanged();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }
  async function del(p) {
    if (!(await confirmVN(`Xoá khoản chi ${vnd(p.amount)} ngày ${p.paid_on}?`, { title: 'Xoá khoản chi', okText: 'Xoá', danger: true }))) return;
    try { await query.deletePayout(p.id); load(); onChanged && onChanged(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }
  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <div className="modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title-wrap"><div className="modal-icon">💸</div>
            <div><div className="modal-device-name">Chi hoa hồng — {collaborator.name}</div>
              <div className="modal-device-sub">Dự tính <b>{vnd(collaborator.commission_est)}</b> · Đã chi <b>{vnd(collaborator.paid_out)}</b> · Còn lại <b style={{ color: collaborator.remaining > 0 ? 'var(--amber)' : 'inherit' }}>{vnd(collaborator.remaining)}</b></div></div></div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>Khách hàng của CTV ({custs.length})</div>
          <div className="tbl-wrap" style={{ marginBottom: 16 }}>
            <table>
              <thead><tr><th>Doanh nghiệp</th><th>Gói</th><th style={{ textAlign: 'right' }}>Máy</th><th style={{ textAlign: 'right' }}>HH dự tính</th></tr></thead>
              <tbody>
                {custs.map(c => (
                  <tr key={c.id}>
                    <td>{c.name}</td><td>{c.plan_label}</td>
                    <td style={{ textAlign: 'right' }} className="mono">{c.device_used}</td>
                    <td style={{ textAlign: 'right' }} className="mono">{vnd(c.commission_est)}</td>
                  </tr>
                ))}
                {custs.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text3)', padding: 12 }}>CTV này chưa có khách nào</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>Ghi nhận chi hoa hồng</div>
          <form className="uform" onSubmit={add}>
            <label><span>Số tiền chi (VND)</span><input type="number" min="0" step="1000" value={form.amount} onChange={set('amount')} required autoFocus /></label>
            <label><span>Ngày chi</span><input type="date" value={form.paid_on} onChange={set('paid_on')} /></label>
            <label className="full"><span>Ghi chú</span><input value={form.note} onChange={set('note')} placeholder="vd HH kỳ 1 khách ABC" /></label>
            <button className="btn primary full" style={{ width: '100%' }} disabled={saving}>{saving ? 'Đang lưu…' : '＋ Ghi nhận chi'}</button>
          </form>
          <div className="tbl-wrap" style={{ marginTop: 14 }}>
            <table>
              <thead><tr><th>Ngày</th><th style={{ textAlign: 'right' }}>Số tiền</th><th>Ghi chú</th><th></th></tr></thead>
              <tbody>
                {rows.map(p => (
                  <tr key={p.id}>
                    <td className="mono">{p.paid_on}</td>
                    <td style={{ textAlign: 'right' }} className="mono">{vnd(p.amount)}</td>
                    <td>{p.note || (p.customer_name ? 'Khách: ' + p.customer_name : '—')}</td>
                    <td><button className="act-btn danger-act" onClick={() => del(p)}>Xoá</button></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text3)', padding: 16 }}>Chưa có khoản chi nào</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Panel báo cáo thuê bao (đầu trang Khách hàng): KPI + phân bố theo gói + cần chú ý.
function CustomerReportPanel({ report: rp }) {
  const cap = (n) => (n === null || n === undefined ? '∞' : n);
  const kpis = [
    { label: 'Tổng khách', value: rp.total_customers, color: 'blue', ico: '🏢' },
    { label: 'Thiết bị quản lý', value: rp.total_devices, color: 'green', ico: '📱' },
    { label: 'Cửa hàng', value: rp.total_stores, color: 'blue', ico: '🏬' },
    { label: 'Sắp hết hạn (≤30 ngày)', value: rp.expiring_count, color: 'amber', ico: '⏳' },
    { label: 'Đã hết hạn', value: rp.expired_count, color: 'red', ico: '🔴' },
  ];
  // Các nhóm cần chú ý — chỉ hiện nhóm có dữ liệu.
  const groups = [
    { key: 'expired', title: '🔴 Đã hết hạn', items: rp.expired, fmt: c => `${c.name} — quá hạn ${-c.days_left} ngày` },
    { key: 'expiring', title: '🟡 Sắp hết hạn (≤30 ngày)', items: rp.expiring_soon, fmt: c => `${c.name} — còn ${c.days_left} ngày (${c.expires_at})` },
    { key: 'overdev', title: '⚠️ Vượt trần thiết bị', items: rp.over_device_cap, fmt: c => `${c.name} — ${c.device_used}/${cap(c.device_cap)} máy` },
    { key: 'neardev', title: '🟠 Gần trần thiết bị (≥80%)', items: rp.near_device_cap, fmt: c => `${c.name} — ${c.device_used}/${cap(c.device_cap)} máy` },
    { key: 'overstore', title: '⚠️ Vượt trần cửa hàng', items: rp.over_store_cap, fmt: c => `${c.name} — ${c.store_used}/${cap(c.store_cap)} cửa hàng` },
  ].filter(g => g.items && g.items.length);

  return (
    <div className="page-card" style={{ marginBottom: 16 }}>
      <div className="page-card-title">Báo cáo thuê bao</div>
      <div style={{ padding: '4px 16px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
          {kpis.map(k => <StatCard key={k.label} label={k.label} value={k.value} color={k.color} ico={k.ico} sub="" />)}
        </div>

        <div style={{ marginTop: 18, display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text2)' }}>Phân bố theo gói</span>
          <span style={{ marginLeft: 'auto', fontSize: 13 }}>Doanh thu/kỳ (ước tính): <b style={{ color: 'var(--green)' }}>{vnd(rp.total_revenue)}</b></span>
        </div>
        {rp.missing_price > 0 && (
          <div style={{ fontSize: 12, color: 'var(--amber)', marginTop: 4 }}>⚠️ {rp.missing_price} khách chưa nhập giá trị hợp đồng — doanh thu chưa gồm các khách này.</div>
        )}
        <div className="tbl-wrap" style={{ marginTop: 8 }}>
          <table>
            <thead><tr><th>Gói</th><th style={{ textAlign: 'right' }}>Khách</th><th style={{ textAlign: 'right' }}>Thiết bị</th><th style={{ textAlign: 'right' }}>Cửa hàng</th><th style={{ textAlign: 'right' }}>Doanh thu/kỳ</th></tr></thead>
            <tbody>
              {rp.by_plan.map(p => (
                <tr key={p.plan}>
                  <td>{p.plan_label}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{p.customers}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{p.devices}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{p.stores}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{p.revenue ? vnd(p.revenue) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {groups.length > 0 && (
          <>
            <div style={{ marginTop: 18, fontWeight: 600, fontSize: 13, color: 'var(--text2)' }}>Cần chú ý</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 12, marginTop: 8 }}>
              {groups.map(g => (
                <div key={g.key} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', background: 'var(--bg3)' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{g.title} ({g.items.length})</div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
                    {g.items.map((c, i) => <li key={i}>{g.fmt(c)}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CustomersView({ showToast }) {
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [portalFor, setPortalFor] = useState(null); // khách đang quản lý tài khoản cổng khách // khách đang xem/thu tiền
  const [search, setSearch] = useState('');
  const load = () => {
    query.listCustomers().then(setRows).catch(e => showToast(e.message, 'error', '❌'));
    query.getCustomerReport().then(setReport).catch(() => setReport(null));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(c => [c.name, c.phone, c.email, c.plan_label, c.collaborator_name].some(v => (v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  async function del(c) {
    if (!(await confirmVN(`Xóa khách hàng "${c.name}"?`, { title: 'Xóa khách hàng', okText: 'Xóa', danger: true }))) return;
    try { await query.deleteCustomer(c.id); showToast('Đã xóa', 'success', '🗑'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  // Duyệt khách do CTV tạo (pending -> active): đặt hạn thuê bao + bắt đầu tính hoa hồng.
  async function approve(c) {
    if (!(await confirmVN(`Duyệt khách "${c.name}" (gói ${c.plan_label})? Khách sẽ hoạt động và bắt đầu tính hạn mức + hoa hồng CTV.`, { title: 'Duyệt khách hàng', okText: 'Duyệt' }))) return;
    try { await query.approveCustomer(c.id); showToast('Đã duyệt khách hàng', 'success', '✅'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  function exportCSV() {
    const cols = ['Doanh nghiệp', 'Người liên hệ', 'SĐT', 'Email', 'Gói', 'Thiết bị dùng', 'Trần thiết bị',
      'Cửa hàng', 'Trần cửa hàng', 'Ngày bắt đầu', 'Chu kỳ (tháng)', 'Hết hạn', 'Ngày còn lại', 'Giá trị HĐ/kỳ', 'Trạng thái', 'Ghi chú'];
    const esc = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const lines = [cols.join(',')];
    for (const c of rows) {
      lines.push([c.name, c.contact_name, c.phone, c.email, c.plan_label,
        c.device_used, c.device_cap ?? '∞', c.store_used, c.store_cap ?? '∞',
        c.started_at, c.cycle_months || '', c.expires_at, c.days_left ?? '',
        c.price ?? '', c.status, c.note].map(esc).join(','));
    }
    // BOM để Excel mở đúng tiếng Việt UTF-8.
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `khach-hang-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  const statusBadge = (s) => {
    const map = { pending: 'badge-amber', active: 'badge-green', trial: 'badge-blue', suspended: 'badge-amber', expired: 'badge-red' };
    const lbl = (STATUS_OPTIONS.find(o => o.value === s) || {}).label || s;
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{lbl}</span>;
  };
  // Thiết bị: hiện dùng/trần, tô đỏ nếu vượt trần.
  const deviceCell = (c) => {
    const over = c.device_cap != null && c.device_used > c.device_cap;
    return <span className="mono" style={over ? { color: 'var(--red)', fontWeight: 700 } : undefined}>{c.device_used}/{capText(c.device_cap)}</span>;
  };

  return (
    <div className="page">
      {report && <CustomerReportPanel report={report} />}
      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>Khách hàng thuê bao ({rows.length})</span>
          <input style={{ maxWidth: 220, marginLeft: 'auto', padding: '7px 11px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg2)', color: 'var(--text)' }} placeholder="Tìm tên / SĐT / gói…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn" onClick={exportCSV} disabled={!rows.length}>⬇ Xuất CSV</button>
          <button className="btn primary" onClick={() => setCreating(true)}>+ Thêm khách hàng</button>
        </div>
        <div className="tbl-wrap dev-table">
          <table>
            <thead><tr>
              <th>Doanh nghiệp</th><th>Gói</th><th>Thiết bị</th><th>Cửa hàng</th>
              <th>Hết hạn</th><th style={{ textAlign: 'right' }}>Đã thu</th><th>CTV</th><th>Trạng thái</th><th>Thao tác</th>
            </tr></thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>{c.name}<div style={{ fontSize: 11, color: 'var(--text3)' }} className="mono">{c.phone || ''}{c.tax_code ? `${c.phone ? ' · ' : ''}MST ${c.tax_code}` : ''}</div></td>
                  <td>{c.plan_label}</td>
                  <td>{deviceCell(c)}</td>
                  <td className="mono">{c.store_used}/{capText(c.store_cap)}</td>
                  <td><ExpiryBadge days={c.days_left} date={c.expires_at} /></td>
                  <td style={{ textAlign: 'right' }} className="mono">{c.paid ? vnd(c.paid) : '—'}</td>
                  <td>{c.collaborator_name || <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                  <td>{statusBadge(c.status)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {c.status === 'pending' && <><button className="act-btn" style={{ color: 'var(--green)', fontWeight: 600 }} onClick={() => approve(c)}>✓ Duyệt</button>{' '}</>}
                    <button className="act-btn" onClick={() => setPaying(c)}>Thu tiền</button>{' '}
                    <button className="act-btn" onClick={() => setPortalFor(c)} title="Tài khoản đăng nhập cổng khách">Tài khoản</button>{' '}
                    <button className="act-btn" onClick={() => setEditing(c)}>Sửa</button>{' '}
                    <button className="act-btn danger-act" onClick={() => del(c)}>Xóa</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text3)', padding: 24 }}>Chưa có khách hàng nào</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {(creating || editing) && (
        <CustomerModal
          customer={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
          showToast={showToast}
        />
      )}
      {paying && (
        <CustomerPaymentsModal
          customer={paying}
          onClose={() => setPaying(null)}
          onChanged={load}
          showToast={showToast}
        />
      )}
      {portalFor && (
        <PortalUsersModal customer={portalFor} onClose={() => setPortalFor(null)} showToast={showToast} />
      )}
    </div>
  );
}

// Modal thu tiền thuê bao của 1 khách: danh sách các lần thu + thêm/xoá.
// ── TÀI KHOẢN CỔNG KHÁCH của một khách hàng ──────────────────────────────────
//
// Trong cổng khách, chỉ chủ chuỗi/quản lý mới tạo được tài khoản cho người của mình —
// nên tài khoản ĐẦU TIÊN của mỗi khách không có đường tạo, trước đây phải ssh + curl
// (hoặc INSERT tay) mỗi lần ký hợp đồng mới. Đây là màn hình chính thức cho việc đó.
//
// Tài khoản này TÁCH HẲN khỏi app_users: khách không bao giờ dùng chung tài khoản với
// nhân viên Zentra, và tên đăng nhập là duy nhất trên TOÀN hệ thống.
const PORTAL_ROLES = [
  { value: 'owner', label: 'Chủ chuỗi — thấy toàn bộ cửa hàng' },
  { value: 'manager', label: 'Quản lý cửa hàng — chỉ 1 cửa hàng' },
  { value: 'staff', label: 'Nhân viên — chỉ 1 cửa hàng' },
];
const PORTAL_URL = 'https://portal.zentratech.vn';

function PortalUsersModal({ customer, onClose, showToast }) {
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const empty = { username: '', password: '', full_name: '', phone: '', role: 'owner', location_id: '' };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  const load = () => {
    setLoading(true);
    query.listCustomerPortalUsers(customer.id)
      .then(setRows).catch(e => showToast((e.body && e.body.error) || e.message, 'error', '❌'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // Cửa hàng để gán cho manager/staff — CHỈ cửa hàng của chính khách này (server cũng
    // chặn lại: gán nhầm cửa hàng khách khác là khách này nhìn thấy máy của khách kia).
    query.listLocations()
      .then(list => setStores((list || []).filter(l => l.customer_id === customer.id && !l.is_warehouse)))
      .catch(() => setStores([]));
  }, [customer.id]);

  const needStore = f.role === 'manager' || f.role === 'staff';

  async function create(e) {
    e.preventDefault();
    if (f.username.trim().length < 3) { showToast('Tên đăng nhập tối thiểu 3 ký tự', 'error', '❌'); return; }
    const pwErr = passwordErr(f.password);
    if (pwErr) { showToast(pwErr, 'error', '❌'); return; }
    if (needStore && !f.location_id) { showToast('Chọn cửa hàng cho tài khoản này', 'error', '❌'); return; }
    setSaving(true);
    try {
      const created = await query.createCustomerPortalUser(customer.id, {
        username: f.username.trim(), password: f.password,
        full_name: f.full_name.trim(), phone: f.phone.trim(),
        role: f.role, location_id: needStore ? Number(f.location_id) : 0,
      });
      // Lấy tên TỪ SERVER: người tạo gõ "daitin" nhưng tài khoản thật là
      // "daitin@portal.zentratech.vn" — báo tên ngắn thì họ dặn nhân viên sai.
      showToast(`Đã tạo tài khoản ${created?.username || f.username.trim()}`, 'success', '🔑');
      setF(empty); setShowPw(false); load();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }

  async function resetPw(u) {
    // promptVN(message, defaultValue, options) — mật khẩu gợi ý là tham số THỨ HAI.
    const pw = await promptVN(`Mật khẩu mới cho "${u.username}" (${PASSWORD_HINT}):`,
      randomPassword(), { title: 'Đặt lại mật khẩu', okText: 'Đặt lại' });
    if (pw === null) return;
    const pwErr = passwordErr(String(pw));
    if (pwErr) { showToast(pwErr, 'error', '❌'); return; }
    try {
      await query.updateCustomerPortalUser(customer.id, u.id, { new_password: String(pw) });
      showToast('Đã đặt lại mật khẩu — nhớ gửi cho khách', 'success', '🔑');
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  async function toggle(u) {
    const on = !u.disabled;
    if (on && !(await confirmVN(`Khoá tài khoản "${u.username}"? Người này sẽ không đăng nhập cổng khách được nữa.`,
      { title: 'Khoá tài khoản', okText: 'Khoá', danger: true }))) return;
    try {
      await query.updateCustomerPortalUser(customer.id, u.id, { disabled: on });
      showToast(on ? 'Đã khoá' : 'Đã mở khoá', 'success', '🔑'); load();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  async function del(u) {
    if (!(await confirmVN(`Xoá hẳn tài khoản "${u.username}"? Không khôi phục được.`,
      { title: 'Xoá tài khoản', okText: 'Xoá', danger: true }))) return;
    try {
      await query.deleteCustomerPortalUser(customer.id, u.id);
      showToast('Đã xoá', 'success', '🗑'); load();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <div className="modal" style={{ width: 640 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">🔑</div>
            <div>
              <div className="modal-device-name">Tài khoản cổng khách — {customer.name}</div>
              <div className="modal-device-sub">Khách đăng nhập tại <b>{PORTAL_URL}</b></div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div className="tbl-wrap" style={{ marginBottom: 14 }}>
            <table>
              <thead><tr><th>Tên đăng nhập</th><th>Vai trò</th><th>Cửa hàng</th><th>Trạng thái</th><th>Thao tác</th></tr></thead>
              <tbody>
                {rows.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.username}</div>
                      {u.full_name && <div style={{ fontSize: 12, color: 'var(--text2)' }}>{u.full_name}{u.phone ? ` · ${u.phone}` : ''}</div>}
                    </td>
                    <td>{u.role_label}</td>
                    <td>{u.location_name || <span style={{ color: 'var(--text3)' }}>toàn chuỗi</span>}</td>
                    <td>{u.disabled ? <span className="badge badge-red">Khoá</span> : <span className="badge badge-green">Hoạt động</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="act-btn" onClick={() => resetPw(u)}>Đổi MK</button>{' '}
                      <button className="act-btn" onClick={() => toggle(u)}>{u.disabled ? 'Mở' : 'Khoá'}</button>{' '}
                      <button className="act-btn danger-act" onClick={() => del(u)}>Xoá</button>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text3)', padding: 16 }}>
                    Khách này chưa có tài khoản nào — tạo tài khoản <b>chủ chuỗi</b> bên dưới, họ tự thêm người của mình trong cổng.
                  </td></tr>
                )}
                {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 16 }}><span className="spinner" /> Đang tải…</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="info-section-title2">Thêm tài khoản</div>
          <form className="uform" onSubmit={create}>
            <label><span>Tên đăng nhập</span>
              <PortalUsernameInput value={f.username} onChange={set('username')} />
            </label>
            <label><span>Mật khẩu ({PASSWORD_HINT})</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type={showPw ? 'text' : 'password'} value={f.password} onChange={set('password')}
                  style={{ flex: 1 }} autoComplete="new-password" required />
                <button type="button" className="btn" style={{ flex: 'none' }} title={showPw ? 'Ẩn' : 'Hiện'}
                  onClick={() => setShowPw(v => !v)}>{showPw ? '🙈' : '👁'}</button>
                <button type="button" className="btn" style={{ flex: 'none' }} title="Tạo mật khẩu ngẫu nhiên"
                  onClick={() => { setF(p => ({ ...p, password: randomPassword() })); setShowPw(true); }}>🎲</button>
              </div>
            </label>
            <label><span>Họ tên</span><input value={f.full_name} onChange={set('full_name')} /></label>
            <label><span>Số điện thoại</span><input value={f.phone} onChange={set('phone')} inputMode="tel" /></label>
            <label className={needStore ? '' : 'full'}><span>Vai trò trong chuỗi</span>
              <select value={f.role} onChange={e => setF(p => ({ ...p, role: e.target.value, location_id: '' }))}>
                {PORTAL_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            {needStore && (
              <label><span>Cửa hàng</span>
                <select value={f.location_id} onChange={set('location_id')} required>
                  <option value="">— Chọn cửa hàng —</option>
                  {stores.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                {stores.length === 0 && <span className="cust-hint">Khách này chưa có cửa hàng nào — gán cửa hàng ở trang Cửa hàng trước.</span>}
              </label>
            )}
            <button className="btn primary full" style={{ width: '100%' }} disabled={saving}>
              {saving ? 'Đang tạo…' : '🔑 Tạo tài khoản'}
            </button>
          </form>
          <div className="page-note" style={{ marginTop: 10 }}>
            Tên đăng nhập là duy nhất trên toàn hệ thống. Mật khẩu chỉ hiện đúng một lần lúc
            tạo — hệ thống lưu dạng băm, không xem lại được; quên thì đặt lại bằng nút <b>Đổi MK</b>.
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal thu tiền thuê bao, dùng ở HAI chỗ:
//   • trang Khách hàng — mở sẵn cho một khách (truyền `customer`)
//   • Sổ thu/chi       — chưa biết khách nào, truyền `customers` để tự chọn trong form
// Một form duy nhất cho cả hai, nên luật nhập (số tiền > 0, kỳ, hình thức…) không có
// cơ hội lệch nhau; chọn khách xong là thấy luôn lịch sử thu của khách đó.
function CustomerPaymentsModal({ customer, customers, onClose, onChanged, showToast }) {
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(customer || null);
  const [form, setForm] = useState({ amount: '', paid_on: '', period: '', method: 'Chuyển khoản', note: '' });
  const [saving, setSaving] = useState(false);
  const [openFiles, setOpenFiles] = useState(null); // id khoản thu đang mở phần bằng chứng
  const [pending, setPending] = useState([]);       // chứng từ chọn TRƯỚC khi ghi
  const load = () => {
    if (!picked) { setRows([]); return; }
    query.listCustomerPayments(picked.id).then(setRows).catch(e => showToast(e.message, 'error', '❌'));
  };
  useEffect(() => { load(); }, [picked && picked.id]);
  const total = useMemo(() => rows.reduce((s, p) => s + (p.amount || 0), 0), [rows]);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  async function add(e) {
    e.preventDefault();
    if (!picked) { showToast('Chọn khách hàng đã thu tiền', 'error', '❌'); return; }
    if (!form.amount || Number(form.amount) <= 0) { showToast('Nhập số tiền > 0', 'error', '❌'); return; }
    setSaving(true);
    try {
      const saved = await query.addCustomerPayment(picked.id, {
        amount: Number(form.amount), paid_on: form.paid_on,
        period: form.period.trim(), method: form.method.trim(), note: form.note.trim(),
      });
      // Đẩy chứng từ đã chọn lên cho khoản vừa ghi. Tiền ghi xong rồi nên file hỏng
      // KHÔNG làm hỏng thao tác — chỉ báo để đính kèm lại bằng nút 📎.
      let failed = 0;
      if (saved && saved.id && pending.length) failed = await uploadPending('thu', saved.id, pending);
      showToast(
        failed
          ? `Đã ghi ${saved.code || 'khoản thu'} nhưng ${failed} file chưa lên — đính kèm lại bằng nút 📎`
          : 'Đã ghi nhận thu tiền' + (saved && saved.code ? ` — ${saved.code}` : ''),
        failed ? 'error' : 'success', failed ? '⚠️' : '💰');
      setForm({ amount: '', paid_on: '', period: '', method: form.method, note: '' });
      setPending([]);
      // Mở sẵn phần bằng chứng của khoản vừa ghi để xem lại / bổ sung.
      if (saved && saved.id) setOpenFiles(saved.id);
      load(); onChanged && onChanged();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }
  async function del(p) {
    if (!(await confirmVN(`Xoá khoản thu ${vnd(p.amount)} ngày ${p.paid_on}?`, { title: 'Xoá khoản thu', okText: 'Xoá', danger: true }))) return;
    try { await query.deleteCustomerPayment(picked.id, p.id); load(); onChanged && onChanged(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <div className="modal" style={{ width: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">💰</div>
            <div>
              <div className="modal-device-name">{picked ? `Thu tiền — ${picked.name}` : 'Thu tiền thuê bao'}</div>
              <div className="modal-device-sub">
                {picked
                  ? <>Đã thu: <b style={{ color: 'var(--green)' }}>{vnd(total)}</b>{picked.price ? ` · Giá HĐ/kỳ: ${vnd(picked.price)}` : ''}</>
                  : 'Chọn khách hàng đã trả tiền để ghi vào sổ thu'}
              </div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <form className="uform" onSubmit={add}>
            {/* Mở từ Sổ thu/chi thì chưa biết khách nào -> chọn ngay trong form. */}
            {customers && (
              <label className="full"><span>Khách hàng</span>
                <select value={picked ? String(picked.id) : ''} required autoFocus
                  onChange={e => setPicked(customers.find(c => String(c.id) === e.target.value) || null)}>
                  <option value="">— Chọn khách hàng —</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.plan_label ? ` · ${c.plan_label}` : ''}</option>
                  ))}
                </select>
              </label>
            )}
            <label><span>Số tiền (VND)</span><input type="number" min="0" step="1000" value={form.amount} onChange={set('amount')} required autoFocus={!customers} /></label>
            <label><span>Ngày thu</span><input type="date" value={form.paid_on} onChange={set('paid_on')} /></label>
            <label><span>Kỳ</span><input value={form.period} onChange={set('period')} placeholder="vd Kỳ 1" /></label>
            <label><span>Hình thức</span>
              <select value={form.method} onChange={set('method')}>
                <option>Chuyển khoản</option><option>Tiền mặt</option><option>Khác</option>
              </select>
            </label>
            <label className="full"><span>Ghi chú</span><input value={form.note} onChange={set('note')} /></label>
            {/* Chứng từ chọn ngay lúc nhập; file bay lên sau khi server trả mã. */}
            <div className="full" style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>Chứng từ (ảnh / PDF)</div>
              <PendingEvidence files={pending} setFiles={setPending} showToast={showToast} />
            </div>
            <button className="btn primary full" style={{ width: '100%' }} disabled={saving}>{saving ? 'Đang lưu…' : '＋ Ghi nhận thu'}</button>
          </form>
          <div className="tbl-wrap" style={{ marginTop: 14 }}>
            <table>
              <thead><tr><th>Ngày</th><th>Mã</th><th style={{ textAlign: 'right' }}>Số tiền</th><th>Kỳ</th><th>Hình thức</th><th>Bằng chứng</th><th></th></tr></thead>
              <tbody>
                {rows.map(p => (
                  <Fragment key={p.id}>
                    <tr>
                      <td className="mono">{p.paid_on}</td>
                      <td className="mono" style={{ fontSize: 12 }}>{p.code || '—'}</td>
                      <td style={{ textAlign: 'right' }} className="mono">{vnd(p.amount)}</td>
                      <td>{p.period || '—'}</td>
                      <td>{p.method || '—'}</td>
                      <td>
                        <button className="act-btn" onClick={() => setOpenFiles(o => o === p.id ? null : p.id)}
                          title={p.files ? `${p.files} bằng chứng` : 'Chưa có bằng chứng — bấm để đính kèm'}>
                          📎 {p.files ? p.files : '+'}
                        </button>
                      </td>
                      <td><button className="act-btn danger-act" onClick={() => del(p)}>Xoá</button></td>
                    </tr>
                    {/* Mở ngay dưới dòng thay vì bật modal chồng modal. */}
                    {openFiles === p.id && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--bg3)' }}>
                          <LedgerEvidence kind="thu" id={p.id} showToast={showToast} onCount={() => load()} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)', padding: 16 }}>Chưa có khoản thu nào</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomerModal({ customer, onClose, onSaved, showToast }) {
  const editing = !!customer;
  const [form, setForm] = useState({
    name: customer?.name || '', contact_name: customer?.contact_name || '',
    phone: customer?.phone || '', email: customer?.email || '',
    address: customer?.address || '',
    ...legalForm(customer), // khối pháp nhân "Bên B" của hợp đồng (lib/legalParty)
    plan: customer?.plan || 'cua_hang', started_at: customer?.started_at || '',
    cycle_months: customer?.cycle_months ?? '', device_cap: customer?.device_cap ?? '',
    price: customer?.price ?? '',
    collaborator_id: customer?.collaborator_id != null ? String(customer.collaborator_id) : '',
    commission_base: customer?.commission_base ?? '', commission_per_device: customer?.commission_per_device ?? '',
    status: customer?.status || 'active', note: customer?.note || '',
  });
  const [ctvs, setCtvs] = useState([]);
  useEffect(() => { query.listCollaborators().then(setCtvs).catch(() => setCtvs([])); }, []);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  async function save(e) {
    e.preventDefault();
    const miss = legalMissing(form);
    if (miss.length) { showToast('Thiếu: ' + miss.join(', '), 'error', '❌'); return; }
    setSaving(true);
    try {
      // Chỉ gửi cap/cycle nếu người dùng nhập -> để backend tự suy theo gói khi bỏ trống.
      const payload = {
        name: form.name.trim(), contact_name: form.contact_name.trim(),
        phone: form.phone.trim(), email: form.email.trim(), address: form.address.trim(),
        ...legalPayload(form),
        plan: form.plan, started_at: form.started_at, status: form.status, note: form.note.trim(),
      };
      if (form.cycle_months !== '' && form.cycle_months !== null) payload.cycle_months = Number(form.cycle_months);
      if (form.device_cap !== '' && form.device_cap !== null) payload.device_cap = Number(form.device_cap);
      if (form.price !== '' && form.price !== null) payload.price = Number(form.price);
      if (form.commission_base !== '' && form.commission_base !== null) payload.commission_base = Number(form.commission_base);
      if (form.commission_per_device !== '' && form.commission_per_device !== null) payload.commission_per_device = Number(form.commission_per_device);
      if (form.collaborator_id === '') payload.clear_collaborator = true;
      else payload.collaborator_id = Number(form.collaborator_id);
      if (editing) await query.updateCustomer(customer.id, payload);
      else await query.createCustomer(payload);
      showToast(editing ? 'Đã lưu khách hàng' : 'Đã tạo khách hàng', 'success', '🏢');
      onSaved();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <form className="modal" style={{ width: 520 }} onClick={e => e.stopPropagation()} onSubmit={save}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">🏢</div>
            <div>
              <div className="modal-device-name">{editing ? 'Sửa khách hàng' : 'Thêm khách hàng'}</div>
              <div className="modal-device-sub">{editing ? customer.name : 'Doanh nghiệp mua gói ZENTRA'}</div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div className="uform">
            <label className="full"><span>Tên doanh nghiệp</span><input value={form.name} onChange={set('name')} placeholder="Công ty TNHH ABC" required autoFocus /></label>
            <label><span>Người liên hệ</span><input value={form.contact_name} onChange={set('contact_name')} /></label>
            <label><span>Số điện thoại</span><input value={form.phone} onChange={set('phone')} inputMode="tel" placeholder="0901234567" /></label>
            <label className="full"><span>Email</span><input value={form.email} onChange={set('email')} inputMode="email" /></label>
            <label className="full"><span>Địa chỉ (trụ sở chính / địa điểm kinh doanh đã đăng ký)</span>
              <textarea rows={2} value={form.address} onChange={set('address')} /></label>
            {/* Khối pháp nhân "Bên B" theo mẫu hợp đồng — nhãn/ô nhập lấy từ lib/legalParty. */}
            {LEGAL_GROUPS.map(g => (
              <Fragment key={g.key}>
                <div className="fsect">{g.title}</div>
                {legalGroupFields(g.key, form.rep_capacity).map(fld => (
                  <label key={fld.k} className={fld.full ? 'full' : undefined}>
                    <span>{fld.label}</span>
                    {fld.type === 'select'
                      ? <select value={form[fld.k]} onChange={set(fld.k)}>
                          {fld.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      : <input type={fld.type === 'date' ? 'date' : 'text'} value={form[fld.k]}
                          onChange={set(fld.k)} placeholder={fld.placeholder || ''} />}
                  </label>
                ))}
              </Fragment>
            ))}
            <div className="fsect">Gói &amp; hợp đồng</div>
            <label><span>Gói</span>
              <select value={form.plan} onChange={set('plan')}>
                {PLAN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label><span>Trạng thái</span>
              <select value={form.status} onChange={set('status')}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label><span>Ngày bắt đầu</span><input type="date" value={form.started_at} onChange={set('started_at')} /></label>
            <label><span>Chu kỳ (tháng)</span><input type="number" min="0" value={form.cycle_months} onChange={set('cycle_months')} placeholder="theo gói" /></label>
            <label><span>Trần thiết bị</span><input type="number" min="0" value={form.device_cap} onChange={set('device_cap')} placeholder="theo gói (trống = ∞)" /></label>
            <label><span>Giá trị hợp đồng (VND/kỳ)</span><input type="number" min="0" step="1000" value={form.price} onChange={set('price')} placeholder="để trống nếu chưa chốt giá" /></label>
            <label className="full"><span>Cộng tác viên giới thiệu</span>
              <select value={form.collaborator_id} onChange={set('collaborator_id')}>
                <option value="">— Không có —</option>
                {ctvs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label><span>Hoa hồng cố định (VND/kỳ)</span><input type="number" min="0" step="1000" value={form.commission_base} onChange={set('commission_base')} placeholder="theo gói" /></label>
            <label><span>Hoa hồng mỗi máy (VND)</span><input type="number" min="0" step="1000" value={form.commission_per_device} onChange={set('commission_per_device')} placeholder="theo gói" /></label>
            <label className="full"><span>Ghi chú</span><input value={form.note} onChange={set('note')} /></label>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
            Bỏ trống <b>chu kỳ</b> / <b>trần thiết bị</b> để dùng mặc định theo gói. Ngày hết hạn tự tính từ ngày bắt đầu + chu kỳ.
          </div>
          <button className="btn primary" style={{ width: '100%', marginTop: 12 }} disabled={saving}>
            {saving ? 'Đang lưu…' : (editing ? '💾 Lưu khách hàng' : '🏢 Tạo khách hàng')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Tài khoản cá nhân + TOTP (Google Authenticator) ───────────────────────────
// ── CỔNG KHÁCH: quản trị nhân sự của chuỗi ───────────────────────────────────
// Chỉ hiện với gói Chuỗi (backend trả me.chain_mgmt) và với chủ chuỗi / quản lý cửa
// hàng (me.can_manage). Backend vẫn tự chặn lại từng route — ẩn menu chỉ là giao diện.
//
// Chủ chuỗi: thấy & sửa mọi tài khoản, chọn được cửa hàng và vai trò.
// Quản lý cửa hàng: chỉ thấy nhân viên CÙNG cửa hàng, tạo được đúng nhân viên của
// cửa hàng mình — nên form ẩn hẳn ô vai trò/cửa hàng cho gọn.
const CHAIN_ROLES = [
  { value: 'owner',   label: 'Chủ chuỗi' },
  { value: 'manager', label: 'Quản lý cửa hàng' },
  { value: 'staff',   label: 'Nhân viên' },
];

// ── CỔNG KHÁCH: hồ sơ doanh nghiệp ──────────────────────────────────────────
// Backend (portal_company.go) quyết định trả bao nhiêu: chủ chuỗi nhận đủ (full=true),
// quản lý/nhân viên chỉ nhận gói + thời hạn + hạn mức. FE chỉ vẽ những gì nhận được,
// KHÔNG tự suy ra vai trò để ẩn/hiện — ẩn ở FE không phải là bảo vệ.
const SUB_STATUS = {
  pending: 'Chờ duyệt', active: 'Đang hoạt động', trial: 'Dùng thử',
  suspended: 'Tạm ngưng', expired: 'Hết hạn',
};
// subDaysLeft: dịch days_left thành chữ + màu. Dùng CHUNG cho dải chỉ số trên topbar và
// trang Doanh nghiệp — hai chỗ lệch nhau thì người dùng sẽ tin chỗ nào?
// null/undefined = hợp đồng không đặt ngày hết hạn, khác hẳn với 0 ngày.
function subDaysLeft(d) {
  if (d === null || d === undefined) return { text: 'Không đặt hạn', color: null };
  if (d < 0)   return { text: `Quá hạn ${-d} ngày`, color: 'var(--red)' };
  if (d <= 30) return { text: `Còn ${d} ngày`, color: 'var(--amber, #d97706)' };
  return { text: `Còn ${d} ngày`, color: null };
}

const REP_CAPACITY = {
  legal: 'Đại diện theo pháp luật',
  household: 'Chủ hộ kinh doanh',
  authorized: 'Đại diện theo uỷ quyền',
};

// Dữ liệu do App nạp một lần rồi truyền xuống (dải chỉ số trên topbar cũng dùng chung),
// nên màn này thuần hiển thị — không gọi API riêng.
function CompanyView({ company, error }) {
  if (error) return <div className="page"><div className="page-card">
    <div style={{ color: 'var(--red)', fontSize: 14 }}>{error}</div></div></div>;
  if (!company) return <div className="page"><div className="page-card">
    <span className="spinner" /> Đang tải…</div></div>;
  const c = company;
  const { text: leftText, color: leftColor } = subDaysLeft(c.days_left);

  const cell = (label, val, opts = {}) => (
    <div className="info-cell" style={opts.span ? { gridColumn: '1 / -1' } : undefined}>
      <div className="info-cell-label">{label}</div>
      <div className="info-cell-val" style={opts.style}>{val || '—'}</div>
    </div>
  );

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title">{c.name}</div>

        <div className="info-section-title2">Hợp đồng &amp; thời hạn</div>
        <div className="info-grid g2" style={{ marginBottom: 16 }}>
          {cell('Gói dịch vụ', c.plan_label)}
          {cell('Trạng thái', SUB_STATUS[c.status] || c.status)}
          {cell('Ngày bắt đầu', c.started_at)}
          {cell('Chu kỳ', c.cycle_months ? `${c.cycle_months} tháng` : '—')}
          {cell('Ngày hết hạn', c.expires_at)}
          {cell('Còn lại', leftText, { style: leftColor ? { color: leftColor, fontWeight: 700 } : undefined })}
        </div>

        <div className="info-section-title2">Hạn mức &amp; mức đang dùng</div>
        <div className="info-grid g2" style={{ marginBottom: 16 }}>
          {cell('Thiết bị', `${c.device_used} / ${capText(c.device_cap)}`)}
          {cell('Cửa hàng', `${c.store_used} / ${capText(c.store_cap)}`)}
        </div>

        {c.full && (<>
          <div className="info-section-title2">Doanh nghiệp &amp; liên hệ</div>
          <div className="info-grid g2" style={{ marginBottom: 16 }}>
            {cell('Địa chỉ', c.address, { span: true })}
            {cell('Người liên hệ', c.contact_name)}
            {cell('Điện thoại', c.phone)}
            {cell('Email', c.email, { span: true })}
          </div>

          <div className="info-section-title2">Pháp nhân (Bên B của hợp đồng)</div>
          <div className="info-grid g2">
            {cell('Mã số thuế', c.tax_code)}
            {cell('Số ĐKKD', c.reg_no)}
            {cell('Nơi cấp', c.reg_issuer)}
            {cell('Ngày cấp', c.reg_date)}
            {cell('Người đại diện', c.rep_name)}
            {cell('Chức vụ', c.rep_title)}
            {cell('Tư cách ký', REP_CAPACITY[c.rep_capacity] || c.rep_capacity)}
            {c.rep_capacity === 'authorized' && cell('Giấy uỷ quyền',
              [c.rep_poa_no, c.rep_poa_date].filter(Boolean).join(' — '))}
          </div>
          <div className="page-note" style={{ marginTop: 12 }}>
            Thông tin sai hoặc cần cập nhật? Liên hệ Zentra — hồ sơ này do Zentra quản lý,
            cổng khách chỉ xem.
          </div>
        </>)}
      </div>
    </div>
  );
}

function ChainStaffView({ me, showToast }) {
  const [rows, setRows]       = useState([]);
  const [stores, setStores]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const isOwner = me?.portal_role === 'owner';

  const load = () => {
    setLoading(true);
    query.listChainStaff().then(setRows).catch(e => showToast(e.message, 'error', '❌')).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    query.listChainStores().then(setStores).catch(() => setStores([]));
  }, []);

  async function toggleDisabled(u) {
    const on = !u.disabled;
    const msg = on
      ? `Khoá tài khoản "${u.username}"? Người này sẽ không đăng nhập được nữa.`
      : `Mở khoá tài khoản "${u.username}"?`;
    if (!(await confirmVN(msg, { title: on ? 'Khoá tài khoản' : 'Mở khoá', okText: on ? 'Khoá' : 'Mở khoá', danger: on }))) return;
    try { await query.updateChainStaff(u.id, { disabled: on }); showToast(on ? 'Đã khoá' : 'Đã mở khoá', 'success', '🔑'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  async function del(u) {
    if (!(await confirmVN(`Xoá tài khoản "${u.username}"? Không khôi phục được.`,
      { title: 'Xoá tài khoản', okText: 'Xoá', danger: true }))) return;
    try { await query.deleteChainStaff(u.id); showToast('Đã xoá', 'success', '🗑'); load(); }
    catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title" style={{ display: 'flex', alignItems: 'center' }}>
          <span>Nhân viên của chuỗi ({rows.length})</span>
          <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setCreating(true)}>+ Thêm tài khoản</button>
        </div>
        {loading ? <div><span className="spinner" /> Đang tải…</div> : (
          <>
            <div className="tbl-wrap dev-table">
              <table>
                <thead><tr><th>Tên đăng nhập</th><th>Họ tên</th><th>Vai trò</th><th>Cửa hàng</th><th>ABM</th><th>Điện thoại</th><th>Trạng thái</th><th>Thao tác</th></tr></thead>
                <tbody>
                  {rows.map(u => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 500 }}>{u.username}</td>
                      <td>{u.full_name || '—'}</td>
                      <td>{u.role_label}</td>
                      <td>{u.location_name || <span style={{ color: 'var(--text3)' }}>toàn chuỗi</span>}</td>
                      <td>
                        <div style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={(u.configurator_accounts || []).join('\n')}>
                          {(u.configurator_accounts || []).length ? u.configurator_accounts.join(', ') : '—'}
                        </div>
                      </td>
                      <td className="mono">{u.phone || '—'}</td>
                      <td>{u.disabled
                        ? <span className="badge badge-red">Khoá</span>
                        : <span className="badge badge-green">Hoạt động</span>}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="act-btn" onClick={() => setEditing(u)}>Sửa</button>{' '}
                        <button className="act-btn" onClick={() => toggleDisabled(u)}>{u.disabled ? 'Mở khoá' : 'Khoá'}</button>{' '}
                        <button className="act-btn danger-act" onClick={() => del(u)}>Xoá</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile: thẻ (ẩn bảng, hiện <=768px) */}
            <div className="dev-cards">
              {rows.map(u => (
                <div key={u.id} className="user-card">
                  <div className="user-card-head">
                    <div className="user-card-name">{u.username}</div>
                    {u.disabled && <span className="badge badge-red">Khoá</span>}
                  </div>
                  <div className="user-card-meta">
                    <div><span className="ucl">Họ tên</span> {u.full_name || '—'}</div>
                    <div><span className="ucl">Vai trò</span> {u.role_label}</div>
                    <div><span className="ucl">Cửa hàng</span> {u.location_name || 'toàn chuỗi'}</div>
                    <div><span className="ucl">ABM</span> <span className="mono" style={{ wordBreak: 'break-all' }}>
                      {(u.configurator_accounts || []).length ? u.configurator_accounts.join(', ') : '—'}</span></div>
                    <div><span className="ucl">SĐT</span> <span className="mono">{u.phone || '—'}</span></div>
                  </div>
                  <div className="user-card-actions">
                    <button className="act-btn" onClick={() => setEditing(u)}>Sửa</button>
                    <button className="act-btn" onClick={() => toggleDisabled(u)}>{u.disabled ? 'Mở khoá' : 'Khoá'}</button>
                    <button className="act-btn danger-act" onClick={() => del(u)}>Xoá</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {(creating || editing) && (
        <ChainStaffModal
          staff={editing}
          stores={stores}
          isOwner={isOwner}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function ChainStaffModal({ staff, stores, isOwner, onClose, onSaved, showToast }) {
  const editing = !!staff;
  const [form, setForm] = useState({
    username: staff?.username || '', password: '',
    full_name: staff?.full_name || '', phone: staff?.phone || '',
    role: staff?.role || 'staff',
    location_id: staff?.location_id ? String(staff.location_id) : '',
    configurator_accounts: (staff?.configurator_accounts && staff.configurator_accounts.length
      ? [...staff.configurator_accounts] : ['']),
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  // Chủ chuỗi không gắn cửa hàng — backend từ chối nếu gửi kèm, nên ẩn luôn ô chọn.
  const needStore = form.role !== 'owner';
  // Ô Configurator chỉ có nghĩa với quản lý cửa hàng: auto-gán cần chủ tài khoản gắn ĐÚNG
  // MỘT cửa hàng. Quản lý chỉ tạo được nhân viên nên ô này chỉ hiện với chủ chuỗi.
  const canSetAbm = isOwner && form.role === 'manager';

  async function save(e) {
    e.preventDefault();
    // Chặn tại chỗ cho nhanh; backend vẫn kiểm lại (firstInvalidConfigurator).
    if (canSetAbm) {
      const bad = cleanAbmList(form.configurator_accounts).find(a => !EMAIL_RE.test(a));
      if (bad) { showToast(`Tài khoản Apple Configurator không hợp lệ: ${bad}`, 'error', '❌'); return; }
    }
    // Sửa tài khoản thì để trống = giữ nguyên mật khẩu; có gõ thì phải đạt luật.
    if ((!editing || form.password) && passwordErr(form.password)) {
      showToast(passwordErr(form.password), 'error', '❌'); return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(), phone: form.phone.trim(),
        role: form.role,
        location_id: needStore && form.location_id ? Number(form.location_id) : 0,
      };
      // Gửi mảng (kể cả rỗng) khi ô đang hiện, để xóa hết cũng lưu được. Vai trò khác
      // manager thì KHÔNG gửi — backend tự xoá khi hạ vai trò.
      if (canSetAbm) payload.configurator_accounts = cleanAbmList(form.configurator_accounts);
      if (form.password) payload.password = form.password;
      if (editing) {
        await query.updateChainStaff(staff.id, payload);
      } else {
        payload.username = form.username.trim();
        await query.createChainStaff(payload);
      }
      showToast(editing ? 'Đã lưu tài khoản' : 'Đã tạo tài khoản', 'success', '👤');
      onSaved();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setSaving(false); }
  }

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') onClose(); }}>
      <form className="modal" style={{ width: 480 }} onClick={e => e.stopPropagation()} onSubmit={save}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">👤</div>
            <div>
              <div className="modal-device-name">{editing ? 'Sửa tài khoản' : 'Thêm tài khoản'}</div>
              <div className="modal-device-sub">{editing ? staff.username : 'Tài khoản đăng nhập cổng khách của chuỗi'}</div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          <div className="uform">
            {!editing && (
              <label className="full"><span>Tên đăng nhập</span>
                <PortalUsernameInput value={form.username} onChange={set('username')} autoFocus />
              </label>
            )}
            <label className="full">
              <span>{editing ? 'Mật khẩu mới (bỏ trống nếu không đổi)' : 'Mật khẩu'}</span>
              <input type="password" value={form.password} onChange={set('password')}
                     minLength={editing ? 0 : MIN_PASSWORD_LEN} required={!editing} placeholder={PASSWORD_HINT} />
            </label>
            <label className="full"><span>Họ tên (tuỳ chọn)</span><input value={form.full_name} onChange={set('full_name')} /></label>
            <label className="full"><span>Điện thoại (tuỳ chọn)</span><input value={form.phone} onChange={set('phone')} inputMode="tel" /></label>
            {/* Quản lý cửa hàng chỉ tạo được nhân viên của ĐÚNG cửa hàng mình — backend
                bỏ qua hai trường này với họ, nên ẩn đi cho khỏi gây hiểu nhầm. */}
            {isOwner && (
              <>
                <label className="full"><span>Vai trò</span>
                  <select value={form.role} onChange={set('role')}>
                    {CHAIN_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </label>
                {needStore && (
                  <label className="full"><span>Cửa hàng</span>
                    <select value={form.location_id} onChange={set('location_id')} required>
                      <option value="">— Chọn cửa hàng —</option>
                      {stores.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
                    </select>
                  </label>
                )}
                {canSetAbm && (
                  <>
                    <ConfiguratorAccountsField
                      rows={form.configurator_accounts}
                      setRows={rows => setForm(p => ({ ...p, configurator_accounts: rows }))} />
                    <div className="page-note" style={{ gridColumn: '1 / -1' }}>
                      Máy quét vào ABM bằng những tài khoản này sẽ tự về cửa hàng đã chọn ở trên
                      (kiểm mỗi 2 phút). Mỗi tài khoản chỉ gắn được cho đúng một người.
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <button className="btn primary" style={{ width: '100%', marginTop: 12 }} disabled={saving}>
            {saving ? 'Đang lưu…' : (editing ? '💾 Lưu tài khoản' : '👤 Tạo tài khoản')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── CỔNG KHÁCH: báo cáo tổng hợp theo từng cửa hàng của chuỗi ────────────────
// "Còn phải thu" = tổng giá trị hợp đồng - đã thu, kẹp sàn 0 (backend tính). Máy chưa
// khai giá trị hợp đồng thì đóng góp 0 vào cả hai cột — số liệu chỉ đúng khi hồ sơ đủ.
function ChainReportView({ showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    query.getChainReport()
      .then(setData)
      .catch(e => showToast((e.body && e.body.error) || e.message, 'error', '❌'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page"><div className="page-card"><span className="spinner" /> Đang tải…</div></div>;
  if (!data) return <div className="page"><div className="page-card">Không tải được báo cáo.</div></div>;

  const t = data.total || {};
  // Cùng bố cục thẻ với Sổ thu/chi: stat-top + giá trị tự tô màu. KHÔNG dùng StatCard
  // (component đó chạy CountUp, chỉ hợp với số thuần, không hợp chuỗi tiền đã format).
  const cards = [
    { label: 'Tổng máy',     val: String(t.devices || 0), color: 'var(--blue)',  ico: '📱' },
    { label: 'Đang khoá',    val: String(t.locked || 0),  color: 'var(--amber)', ico: '🔒' },
    { label: 'Đã thu',       val: vnd(t.collected),       color: 'var(--green)', ico: '💰' },
    { label: 'Còn phải thu', val: vnd(t.remaining),       color: 'var(--red)',   ico: '⏳' },
  ];

  return (
    <div className="page">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12, marginBottom: 14 }}>
        {cards.map(c => (
          <div className="stat-card" key={c.label}>
            <div className="stat-top">{c.label}<span className="stat-ico">{c.ico}</span></div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.color, marginTop: 4 }}>{c.val}</div>
          </div>
        ))}
      </div>
      <div className="page-card">
        <div className="page-card-title">Theo cửa hàng ({(data.stores || []).length})</div>
        <div className="tbl-wrap dev-table">
          <table>
            <thead><tr><th>Cửa hàng</th><th style={{ textAlign: 'right' }}>Máy</th><th style={{ textAlign: 'right' }}>Đang khoá</th><th style={{ textAlign: 'right' }}>Nhân viên</th><th style={{ textAlign: 'right' }}>Giá trị HĐ</th><th style={{ textAlign: 'right' }}>Đã thu</th><th style={{ textAlign: 'right' }}>Còn phải thu</th></tr></thead>
            <tbody>
              {(data.stores || []).map(st => (
                <tr key={st.location_id}>
                  <td style={{ fontWeight: 500 }}>{st.name}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{st.devices}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{st.locked}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{st.staff}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{vnd(st.contracted)}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{vnd(st.collected)}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{vnd(st.remaining)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile: thẻ (ẩn bảng, hiện <=768px) */}
        <div className="dev-cards">
          {(data.stores || []).map(st => (
            <div key={st.location_id} className="user-card">
              <div className="user-card-head"><div className="user-card-name">{st.name}</div></div>
              <div className="user-card-meta">
                <div><span className="ucl">Máy</span> <span className="mono">{st.devices}</span></div>
                <div><span className="ucl">Đang khoá</span> <span className="mono">{st.locked}</span></div>
                <div><span className="ucl">Nhân viên</span> <span className="mono">{st.staff}</span></div>
                <div><span className="ucl">Giá trị HĐ</span> <span className="mono">{vnd(st.contracted)}</span></div>
                <div><span className="ucl">Đã thu</span> <span className="mono">{vnd(st.collected)}</span></div>
                <div><span className="ucl">Còn phải thu</span> <span className="mono">{vnd(st.remaining)}</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AccountView({ me, showToast, refreshSession }) {
  const [enabled, setEnabled] = useState(me.totp_enabled);
  const [setup, setSetup] = useState(null); // { secret, otpauth_uri }
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');

  async function startSetup() {
    try {
      const s = await auth.totpSetup();
      setSetup(s);
      setQr(await QRCode.toDataURL(s.otpauth_uri, { margin: 1, width: 200 }));
    } catch (ex) { showToast(ex.message, 'error', '❌'); }
  }
  async function enable() {
    try {
      await auth.totpEnable(code.trim());
      setEnabled(true); setSetup(null); setCode('');
      showToast('Đã bật xác thực 2 lớp', 'success', '🔐');
      refreshSession && refreshSession();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }
  async function disable() {
    const c = await promptVN('Nhập mã Google Authenticator hiện tại để tắt 2FA:', '', { title: 'Tắt 2FA', okText: 'Tắt 2FA' });
    if (!c) return;
    try {
      await auth.totpDisable(c.trim());
      setEnabled(false);
      showToast('Đã tắt 2FA', 'success', '🔓');
      refreshSession && refreshSession();
    } catch (ex) { showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  return (
    <div className="page">
      <div className="page-card">
        <div className="page-card-title">Tài khoản</div>
        <div className="info-grid g2" style={{ marginBottom: 16 }}>
          <div className="info-cell"><div className="info-cell-label">Tài khoản</div><div className="info-cell-val">{me.username}</div></div>
          <div className="info-cell"><div className="info-cell-label">Vai trò</div><div className="info-cell-val">{ROLE_LABEL[me.role] || me.role}</div></div>
        </div>

        <div className="page-card-title">Xác thực 2 lớp (Google Authenticator)</div>
        {enabled ? (
          <>
            <div className="cert-status green">Đang BẬT. Mỗi lần đăng nhập cần nhập mã 6 số từ Google Authenticator.</div>
            {me.totp_required ? (
              <div className="page-note">🔒 2FA do cấp trên yêu cầu — bạn không thể tự tắt. Liên hệ quản trị viên/quản lý nếu cần.</div>
            ) : (
              <button className="btn danger" onClick={disable}>Tắt 2FA</button>
            )}
          </>
        ) : setup ? (
          <div>
            <p className="tab-hint" style={{ marginBottom: 10 }}>Mở <b>Google Authenticator</b> → quét mã QR (hoặc nhập tay khoá bên dưới), rồi nhập mã 6 số để xác nhận.</p>
            {qr && <img src={qr} alt="QR" style={{ background: '#fff', padding: 8, borderRadius: 8, marginBottom: 10 }} />}
            <div className="info-cell" style={{ marginBottom: 10 }}>
              <div className="info-cell-label">Khoá thủ công (Manual key)</div>
              <div className="info-cell-val mono" style={{ wordBreak: 'break-all' }}>{setup.secret}</div>
            </div>
            <div className="row-form">
              <input inputMode="numeric" maxLength={6} placeholder="Mã 6 số" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
              <button className="btn primary" onClick={enable}>Xác nhận & Bật</button>
              <button className="btn" onClick={() => setSetup(null)}>Hủy</button>
            </div>
          </div>
        ) : (
          <>
            <div className="page-note" style={{ marginBottom: 12 }}>Chưa bật. Nên bật để bảo vệ tài khoản bằng mã một lần từ điện thoại.</div>
            <button className="btn primary" onClick={startSetup}>Thiết lập 2FA</button>
          </>
        )}
      </div>
    </div>
  );
}

const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
:root{--bg:#F5E7E2;--bg2:#FFFFFF;--bg3:#FBF3F0;--bg4:#F0DED8;--border:#E7CAC2;--border2:#D3A99E;--text:#1E2C48;--text2:#5C6A7F;--text3:#8F7E77;--blue:#E4574E;--blue-bg:#FFF0EE;--blue-border:rgba(228,87,78,0.38);--green:#0EA96B;--green-bg:#E1F7EF;--amber:#F59E0B;--amber-bg:#FDF1DC;--red:#EF4655;--red-bg:#FCE6E8;--red-border:rgba(239,70,85,0.35);--radius:10px;--radius-lg:14px;--ink:#0A1A33;--nav:#0D2B6F;--nav2:#123680;--brand-navy:#0D2B6F;--brand-teal:#F08A5D;--head:'Be Vietnam Pro',system-ui,sans-serif}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html,body,#root{height:100%}
/* Be Vietnam Pro: font thiết kế cho tiếng Việt (đủ dấu, hiện đại) — dùng cho cả tiêu đề & nội dung */
body{font-family:'Be Vietnam Pro',system-ui,sans-serif;color:var(--text);font-size:14px;line-height:1.5;overflow:hidden;-webkit-font-smoothing:antialiased;
  /* Nền hệ thống = ảnh /bg.jpg (đặt file vào public/bg.jpg). Phủ lớp trắng mờ để nội dung
     admin dễ đọc. Không có file -> tự về màu var(--bg). */
  background:
    linear-gradient(rgba(255,255,255,.5),rgba(255,255,255,.62)),
    url('/bg.jpg') center center/cover no-repeat,
    var(--bg)}
.mono{font-family:'JetBrains Mono',monospace}
.app{display:flex;height:100vh}
.sidebar{width:220px;flex-shrink:0;background:var(--bg2);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden}
.sidebar-logo{padding:18px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:10px}
.logo-mark{width:42px;height:42px;background:var(--blue);border-radius:11px;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0}
.logo-name{font-weight:600;font-size:15px;color:var(--text)}.logo-url{font-size:11px;color:var(--text3);font-family:'IBM Plex Mono',monospace}
.sidebar-nav{flex:1;overflow-y:auto;padding:10px 0}
.nav-section{font-size:11px;font-weight:600;color:var(--text3);text-transform:uppercase;letter-spacing:.08em;padding:12px 16px 4px}
.nav-item{display:flex;align-items:center;gap:9px;padding:8px 16px;cursor:pointer;color:var(--text2);font-size:14px;border-left:2px solid transparent;transition:all .15s}
.nav-item:hover{background:var(--bg3);color:var(--text)}.nav-item.active{background:var(--blue-bg);color:var(--blue);border-left-color:var(--blue);font-weight:500}
.sidebar-user{padding:12px 16px;border-top:1px solid var(--border);display:flex;align-items:center;gap:9px}
.user-avatar{width:28px;height:28px;background:var(--blue-bg);border:1px solid var(--blue-border);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:var(--blue);flex-shrink:0}
.user-name{font-size:13px;font-weight:500;color:var(--text)}.user-role{font-size:11px;color:var(--text3)}
.main{flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0}
.topbar{background:var(--bg2);border-bottom:1px solid var(--border);padding:10px 20px;display:flex;align-items:center;gap:10px;flex-shrink:0}
.topbar-title{font-weight:600;font-size:16px;flex:1}
/* Dải chỉ số thuê bao trên topbar cổng khách — luôn nhìn thấy ở mọi trang. */
.topbar-chips{display:flex;align-items:center;gap:6px;flex-wrap:wrap;min-width:0}
.tchip{display:inline-flex;align-items:center;gap:6px;background:var(--bg3);border:1px solid var(--border);border-radius:999px;padding:4px 11px;font-size:12.5px;color:var(--text);white-space:nowrap}
.tchip b{font-weight:600;color:var(--text3);font-size:11px;text-transform:uppercase;letter-spacing:.03em}
/* Chip đầu tiên (Hết hạn) không được co lại hay bị đẩy ra khỏi tầm mắt. */
.topbar-chips>.tchip:first-child{flex:none}
/* Bề ngang trung bình (laptop nhỏ/tablet): topbar chưa xuống dòng như mobile mà đã
   hết chỗ — bỏ bớt chip phụ, GIỮ nguyên Hết hạn và Trạng thái. Dưới 769px thì
   khối mobile cho cả dải một hàng riêng nên hiện lại đủ. */
@media (min-width:769px) and (max-width:1180px){.tchip-opt{display:none}}
.search-box{position:relative;display:flex;align-items:center}
.search-box svg{position:absolute;left:10px;width:14px;height:14px;color:var(--text3);pointer-events:none}
.search-box input{background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);padding:7px 12px 7px 32px;color:var(--text);font-size:14px;font-family:inherit;width:240px;outline:none;transition:border-color .15s}
.search-box input:focus{border-color:var(--blue-border)}.search-box input::placeholder{color:var(--text3)}
/* Nút thu gọn/mở Tổng quan + ô tìm kiếm đặt ngay trên danh sách */
.summary-toggle{display:flex;align-items:center;justify-content:space-between;margin:16px 20px 0;padding:9px 14px;background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius);font-size:14px;font-weight:600;color:var(--text2);cursor:pointer;font-family:inherit;transition:border-color .15s}
.summary-toggle:hover{border-color:var(--border2)}
.summary-chev{font-size:13px;font-weight:500;color:var(--text3)}
.list-search{padding:14px 20px 10px}
.filters-card{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg);padding:14px 16px}
.filters-card-title{font-size:13px;font-weight:600;color:var(--text2);margin-bottom:10px;letter-spacing:.2px}
.list-search .search-box{display:flex;width:100%}
.list-search .search-box input{width:100%}
/* Ô tìm + nút phễu "Tìm kiếm nâng cao" (ẩn/hiện bộ lọc) trên cùng một hàng */
.search-row{display:flex;gap:8px;align-items:stretch;flex-wrap:wrap}
.search-row .search-box{flex:1 1 auto;min-width:0}
.adv-toggle{display:inline-flex;align-items:center;gap:6px;flex:none;padding:8px 12px;background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);color:var(--text2);font-size:13.5px;font-weight:500;font-family:inherit;cursor:pointer;white-space:nowrap;transition:all .15s}
.adv-toggle svg{width:15px;height:15px;flex:none}
.adv-toggle:hover{border-color:var(--border2);color:var(--text)}
.adv-toggle.on{background:var(--blue-bg);border-color:var(--blue-border);color:var(--blue)}
.adv-toggle.has:not(.on){border-color:var(--blue-border);color:var(--blue)}
.adv-count{background:var(--blue);color:#fff;font-size:11px;font-weight:700;min-width:16px;height:16px;border-radius:9px;display:inline-flex;align-items:center;justify-content:center;padding:0 4px}
.adv-chev{font-size:10px;color:inherit}
@media(max-width:768px){.adv-toggle{flex:1 1 100%;justify-content:center}}
.filter-bar{display:flex;flex-wrap:wrap;gap:10px;margin-top:12px;padding-top:12px;border-top:1px dashed var(--border);align-items:flex-end}
.filter-bar .filt{display:flex;flex-direction:column;gap:3px;font-size:12px;color:var(--text3)}
.filter-bar .filt.grow{flex:1;min-width:180px}
.filter-bar .filt span{font-weight:500}
.filter-bar .filt input,.filter-bar .filt select{background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);height:34px;box-sizing:border-box;padding:0 10px;color:var(--text);font-size:14px;font-family:inherit;outline:none;transition:border-color .15s}
.filter-bar .filt input:focus,.filter-bar .filt select:focus{border-color:var(--blue-border)}
/* Ô ngày dd/mm/yyyy: click toàn ô mở lịch (input date thật TRONG SUỐT đè lên trên) */
.filter-bar .fdate{position:relative;display:flex;align-items:center;gap:6px;background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);height:34px;box-sizing:border-box;padding:0 10px;min-width:132px;cursor:pointer;transition:border-color .15s}
.filter-bar .fdate:hover{border-color:var(--border2)}
/* Ô hiển thị = input TEXT readOnly (không có UI date trình duyệt), làm phẳng để hoà vào ô */
.filter-bar .fdate .fdate-disp{flex:1;min-width:0;border:0!important;outline:none;background:transparent!important;height:auto!important;padding:0!important;font-size:14px;font-family:inherit;color:var(--text);cursor:pointer}
.filter-bar .fdate .fdate-disp::placeholder{color:var(--text3)}
.filter-bar .fdate .fdate-ic{width:15px;height:15px;color:var(--text3);flex-shrink:0;pointer-events:none}
/* input date THẬT: ẩn hẳn (1px, class riêng -> specificity cao), chỉ để mở lịch + giữ giá trị */
.filter-bar .fdate .fdate-native{position:absolute!important;left:8px;bottom:0;width:1px!important;height:1px!important;min-width:0!important;margin:0!important;padding:0!important;border:0!important;background:transparent!important;opacity:0!important;pointer-events:none;clip-path:inset(50%)}
.filter-bar .filt.grow input{width:100%}
.filter-btns{display:flex;gap:4px}
.btn{display:inline-flex;align-items:center;gap:5px;padding:6px 12px;font-size:13px;font-weight:500;font-family:inherit;border:1px solid var(--border2);border-radius:var(--radius);cursor:pointer;background:var(--bg3);color:var(--text2);transition:all .15s;white-space:nowrap}
.btn:hover{background:var(--bg4);color:var(--text)}.btn:disabled{opacity:.5;cursor:default}
.btn.active-filter{background:var(--blue-bg);color:var(--blue);border-color:var(--blue-border)}
.btn.primary{background:var(--blue);color:#fff;border-color:var(--blue)}.btn.primary:hover{background:#2563eb}
.btn.danger{background:var(--red-bg);color:var(--red);border-color:var(--red-border)}
.stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;padding:16px 20px;flex-shrink:0}
.stat-card{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg);padding:14px 16px;transition:border-color .15s}
.stat-card:hover{border-color:var(--border2)}
.stat-label{font-size:12px;color:var(--text3);margin-bottom:6px;font-weight:600;text-transform:uppercase;letter-spacing:.04em}
.stat-val{font-size:27px;font-weight:600;font-family:'IBM Plex Mono',monospace}
.stat-val.blue{color:var(--blue)}.stat-val.green{color:var(--green)}.stat-val.amber{color:var(--amber)}.stat-val.red{color:var(--red)}
.stat-sub{font-size:12px;color:var(--text3);margin-top:3px}
.breakdown{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;padding:0 20px 16px;flex-shrink:0}
.breakdown-card{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg);padding:14px 16px}
.breakdown-title{font-size:12px;color:var(--text3);margin-bottom:10px;font-weight:600;text-transform:uppercase;letter-spacing:.04em}
.breakdown-row{display:flex;align-items:center;gap:8px;margin-bottom:6px}
.breakdown-key{font-size:13px;color:var(--text2);width:110px;flex-shrink:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.breakdown-bar{flex:1;height:6px;background:var(--bg4);border-radius:99px;overflow:hidden}
.breakdown-bar span{display:block;height:100%;background:var(--blue);border-radius:99px}
.breakdown-val{font-size:13px;color:var(--text);width:28px;text-align:right;flex-shrink:0}
.content{flex:1;overflow-y:auto;padding:0 20px 20px}
.table-card{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg);overflow:hidden}
.table-card table{min-width:1400px}
.table-card thead th{padding:12px 14px;color:var(--text2)}
.table-card thead th.num,.table-card tbody td.num{text-align:right}
.table-card tbody td{padding:13px 14px;vertical-align:middle}
.table-card tbody td.num{font-variant-numeric:tabular-nums;letter-spacing:.01em}
.table-card tbody tr{transition:background .13s}
.table-card tbody tr:nth-child(even) td{background:var(--bg3)}
.table-card tbody tr:hover td{background:var(--blue-bg)}
.table-card tbody tr:hover .dev-name{color:var(--blue)}
.table-head{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid var(--border)}
.table-head-title{font-weight:600;font-size:14px}
.table-head .filter-btns{margin-left:auto}
.th-actions{display:flex;gap:6px;margin-left:10px}
/* Nhãn phân biệt hàng CHO THUÊ trong cột tiền (cột dùng chung với trả góp) */
.rent-tag{font-style:normal;font-size:9.5px;font-weight:700;letter-spacing:.02em;
  margin-left:5px;padding:1px 5px;border-radius:5px;vertical-align:middle;
  background:var(--blue-bg);color:var(--blue);border:1px solid var(--blue-border)}
.tbl-wrap{overflow-x:auto}
table{width:100%;border-collapse:collapse;table-layout:fixed}
thead th{text-align:left;padding:9px 12px;font-size:12px;font-weight:600;color:var(--text3);text-transform:uppercase;letter-spacing:.04em;background:var(--bg3);border-bottom:1px solid var(--border);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
tbody td{padding:10px 12px;font-size:13px;border-bottom:1px solid var(--border);color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;cursor:pointer}
tbody tr:last-child td{border-bottom:none}
tbody tr:hover td{background:var(--bg3)}
.badge{display:inline-flex;align-items:center;gap:5px;padding:3px 8px;border-radius:99px;font-size:12px;font-weight:500}
.badge-green{background:var(--green-bg);color:var(--green)}.badge-amber{background:var(--amber-bg);color:var(--amber)}.badge-red{background:var(--red-bg);color:var(--red)}.badge-blue{background:var(--blue-bg);color:var(--blue)}.badge-gray{background:var(--bg4);color:var(--text2)}
.dot{width:6px;height:6px;border-radius:50%;display:inline-block;flex-shrink:0}
.dot-green{background:var(--green)}.dot-amber{background:var(--amber)}.dot-red{background:var(--red)}
.bat-low{color:var(--red)}.bat-mid{color:var(--amber)}.bat-ok{color:var(--green)}
::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--bg4);border-radius:99px}
input[type=checkbox]{accent-color:var(--blue);width:14px;height:14px;cursor:pointer}
@keyframes spin{to{transform:rotate(360deg)}}

/* ── Responsive ── */
.menu-btn{display:none}
.sidebar-backdrop{position:fixed;inset:0;background:rgba(18,21,43,.42);backdrop-filter:blur(2px);z-index:140}

@media (max-width:1100px){
.stats{grid-template-columns:repeat(2,1fr)}
.breakdown{grid-template-columns:1fr}
}

@media (max-width:768px){
body{overflow:auto}
.app{height:auto;min-height:100dvh}
.main{overflow:visible}
.menu-btn{display:inline-flex;padding:6px 11px;font-size:16px}
.sidebar{position:fixed;top:0;left:0;bottom:0;z-index:150;width:240px;transform:translateX(-100%);transition:transform .25s ease;box-shadow:8px 0 40px -8px rgba(18,21,43,.32)}
.sidebar.open{transform:translateX(0)}
/* Thứ tự mobile: topbar → nút Tổng quan → (stats+fleet) → tìm kiếm → danh sách → biểu đồ */
.topbar{flex-wrap:wrap;padding:10px 12px;row-gap:8px;position:sticky;top:0;z-index:50;order:0}
.summary-toggle{order:1;margin:12px 12px 0}
.summary-wrap{order:2}
.fleet{margin:0 12px 12px}
.list-search{order:3;padding:10px 12px}
.content{order:4}
.breakdown{order:5}
.topbar-title{font-size:15px}
.topbar-chips{width:100%;order:9}
.filter-btns{order:11;flex-wrap:wrap}
/* Thanh công cụ danh sách thiết bị: cho xuống dòng thay vì tràn ngang bị cắt.
   Dòng 1: tiêu đề + nút (Làm mới/CSV/Thêm). Dòng 2: chip lọc full-width, tự wrap. */
.table-head{flex-wrap:wrap;row-gap:10px}
.table-head-title{order:1}
.th-actions{order:2;margin-left:auto;flex-wrap:wrap}
.table-head .filter-btns{order:3;flex:1 1 100%;margin-left:0}
.btn{padding:7px 12px}
/* 4 block thống kê: gọn lại trên mobile (nhỏ chữ số, icon, padding) */
.stats{padding:12px 12px 0;gap:8px}
.stat-card{padding:9px 11px}
.stat-top{font-size:12px}
.stat-ico{width:24px;height:24px;font-size:14px;border-radius:7px}
.stat-val{font-size:20px;margin-top:3px}
.stat-sub{font-size:11px;margin-top:1px}
/* Tình trạng đội thiết bị: gọn lại */
.fleet{padding:12px 14px}
.fleet-title{font-size:14px}
.fleet-cap{font-size:12px;margin:2px 0 10px}
.fleet-bar{height:12px}
.fleet-legend{gap:14px;margin-top:10px}
.fleet-legend div{font-size:12.5px}
.breakdown{padding:12px;gap:8px}
.content{padding:12px;flex:none;overflow:visible}
table{min-width:840px}
.tbl-wrap{-webkit-overflow-scrolling:touch}
}

@media (max-width:400px){
.stats{gap:6px}
.stat-card{padding:8px 10px}
.stat-val{font-size:19px}
.stat-ico{width:22px;height:22px;font-size:13px}
}

/* ── Shared: modal + info-cell (dùng cho trang Chứng chỉ khi DeviceModal đóng) ── */
.overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:100;display:flex;align-items:flex-start;justify-content:center;padding:40px 20px;overflow-y:auto}
.modal{background:var(--bg2);border:1px solid var(--border2);border-radius:14px;max-width:100%;flex-shrink:0}
.modal-head{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid var(--border)}
.modal-title-wrap{display:flex;align-items:center;gap:12px;min-width:0}
.modal-icon{width:40px;height:40px;background:var(--blue-bg);border:1px solid var(--blue-border);border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:21px;flex-shrink:0}
.modal-device-name{font-weight:600;font-size:16px}.modal-device-sub{font-size:12px;color:var(--text3);margin-top:1px}
.modal-close{width:28px;height:28px;border:1px solid var(--border);border-radius:6px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--text2);font-size:17px;background:transparent;flex-shrink:0}
.modal-close:hover{background:var(--bg3);color:var(--text)}
.tab-body{padding:18px 20px}
.info-grid{display:grid;gap:8px}.info-grid.g2{grid-template-columns:repeat(2,1fr)}.info-grid.g3{grid-template-columns:repeat(3,1fr)}
.info-cell{background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);padding:10px 12px;min-width:0}
.info-cell-label{font-size:11px;color:var(--text3);font-weight:600;text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px}
.info-cell-val{font-size:13px;color:var(--text);font-weight:500;word-break:break-all}.info-cell-val.mono{font-family:'IBM Plex Mono',monospace;font-size:12px}.info-cell-val.small{font-size:11px}

/* ── Trang nội dung (Chứng chỉ) ── */
.page{flex:1;overflow-y:auto;padding:20px}
/* Laptop: giãn full theo bề rộng khả dụng (bỏ giới hạn hẹp cũ) */
.page-card{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg);padding:18px 20px;max-width:1600px}
/* Form nhập giữ bề rộng vừa phải kể cả khi card rất rộng */
.row-form input,.row-form select{max-width:320px}
.page-card-title{font-weight:600;font-size:15px;margin-bottom:16px}
/* Bảng trong trang quản trị + modal: co giãn theo nội dung, KHÔNG cắt cột */
.page table,.tab-body table{table-layout:auto}
.page thead th,.page tbody td,.tab-body thead th,.tab-body tbody td{overflow:visible;text-overflow:clip;white-space:normal}
.page td .act-btn{margin:2px 0}
.cert-status{font-size:14px;font-weight:500;padding:10px 14px;border-radius:var(--radius);margin-bottom:14px}
.cert-status.green{background:var(--green-bg);color:var(--green)}.cert-status.amber{background:var(--amber-bg);color:var(--amber)}.cert-status.red{background:var(--red-bg);color:var(--red)}.cert-status.blue{background:var(--blue-bg);color:var(--blue)}
.page-note{font-size:13px;color:var(--text3);line-height:1.6;background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);padding:10px 12px}
.cred-row{display:flex;align-items:center;gap:8px;margin-bottom:8px}
.cred-cell{flex:1;min-width:0;background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);padding:8px 12px}
.cred-cell .info-cell-val{font-size:16px;font-weight:600}
@media (max-width:768px){.page{padding:12px;overflow:visible}.page-card{padding:14px}}

/* ── Login / splash ── */
.splash{height:100dvh;display:flex;align-items:center;justify-content:center;color:var(--text3);font-size:15px}
.login-wrap{height:100dvh;display:flex;align-items:center;justify-content:center;padding:20px}
.login-card{width:340px;max-width:100%;background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg);padding:28px 24px;display:flex;flex-direction:column;gap:12px}
.login-logo{display:flex;align-items:center;gap:10px}
.login-title{font-weight:700;font-size:19px}
.login-sub{font-size:13px;color:var(--text3);margin-bottom:8px}
.fld{display:flex;flex-direction:column;gap:5px}
.fld span{font-size:12px;font-weight:600;color:var(--text2)}
.fld input,.row-form input,.row-form select{background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);padding:9px 11px;color:var(--text);font-size:14px;font-family:inherit;outline:none;width:100%}
.fld input:focus,.row-form input:focus,.row-form select:focus{border-color:var(--blue-border)}
/* Form hồ sơ người dùng trong modal */
.uform{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.uform label{display:flex;flex-direction:column;gap:5px}
.uform label.full{grid-column:1 / -1}
.uform label span{font-size:12px;font-weight:600;color:var(--text2)}
.uform input,.uform select,.uform textarea{background:var(--bg3);border:1px solid var(--border);border-radius:9px;padding:9px 11px;color:var(--text);font-size:14px;font-family:inherit;outline:none;width:100%;resize:vertical}
.uform select{appearance:none;-webkit-appearance:none;cursor:pointer;padding-right:34px;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 11px center;background-size:15px}
.uform input:focus,.uform select:focus,.uform textarea:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-bg)}
/* Ô tick cửa hàng gom theo khách hàng (form người dùng) */
.store-groups{display:flex;flex-direction:column;gap:10px;margin-top:4px}
.store-group-label{font-size:11px;font-weight:700;color:var(--text3);text-transform:uppercase;letter-spacing:.03em;margin-bottom:5px}
.cust-hint{font-size:12px;font-weight:400;color:var(--text2);margin-top:6px}
.cust-hint b{color:var(--text)}
.cust-hint .cust-hint-sub{color:var(--text3)}
.cust-hint.warn{color:var(--amber)}
.cust-hint.warn b{color:var(--amber)}
.info-section-title2{font-size:11px;font-weight:700;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin:0 0 8px}
${EVIDENCE_CSS}
${LEDGER_CHART_CSS}
.txn-row{cursor:pointer}
.txn-row:hover{background:var(--bg3)}
.uform .fsect{grid-column:1 / -1;font-size:12px;font-weight:700;color:var(--text2);border-top:1px solid var(--border);padding-top:11px;margin-top:3px}
@media(max-width:560px){.uform{grid-template-columns:1fr}}
/* Máy tính kỳ hạn: form trái · kết quả phải trên desktop, xếp dọc trên mobile */
.calc-grid{display:grid;grid-template-columns:minmax(240px,300px) 1fr;gap:22px;align-items:start}
.calc-empty{display:flex;align-items:center;justify-content:center;text-align:center;min-height:120px;padding:20px;border:1px dashed var(--border);border-radius:var(--radius);color:var(--text3);font-size:14px}
@media(max-width:720px){.calc-grid{grid-template-columns:1fr}}
.login-err{font-size:13px;color:var(--red);background:var(--red-bg);border-radius:var(--radius);padding:8px 10px}
.login-btn{justify-content:center;padding:10px}
/* ── Nút hành động dùng chung (ngoài modal) ── */
.icon-btn{width:28px;height:28px;flex-shrink:0;border:1px solid var(--border);border-radius:6px;background:transparent;color:var(--text2);cursor:pointer;font-size:15px}
.icon-btn:hover{background:var(--bg3);color:var(--text)}
.act-btn{display:inline-flex;align-items:center;gap:4px;padding:5px 10px;font-size:12px;font-weight:500;font-family:inherit;border:1px solid var(--border);border-radius:6px;cursor:pointer;background:var(--bg3);color:var(--text2);transition:all .15s;white-space:nowrap}
.act-btn:hover{background:var(--bg4);color:var(--text);border-color:var(--border2)}
.act-btn.danger-act{background:var(--red-bg);color:var(--red);border-color:var(--red-border)}
.row-form{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.row-form input,.row-form select{width:auto;flex:1;min-width:140px}
.row-form .btn{flex-shrink:0}
@media (max-width:768px){.row-form input,.row-form select{min-width:0;max-width:none;flex:1 1 100%}}

/* ══════════ Light theme (Zentra) + chuyển động ══════════ */
:root{--shadow:0 1px 2px rgba(20,24,55,.05),0 8px 28px -12px rgba(20,24,55,.18);--shadow-lg:0 18px 40px -14px rgba(20,24,55,.4);--grad:linear-gradient(135deg,#DE3025 0%,#CB4B13 100%);--ease:cubic-bezier(.4,0,.2,1)}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes scaleIn{from{opacity:0;transform:translateY(8px) scale(.98)}to{opacity:1;transform:translateY(0) scale(1)}}
@keyframes growBar{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes shimmer{to{background-position:-200% 0}}
@keyframes pulseDot{0%,100%{box-shadow:0 0 0 0 rgba(15,185,129,.5)}70%{box-shadow:0 0 0 6px rgba(15,185,129,0)}}
@keyframes popIn{0%{opacity:0;transform:scale(.8)}60%{transform:scale(1.06)}100%{opacity:1;transform:scale(1)}}

.app{animation:fadeIn .35s ease}
h1,.logo-name,.topbar-title,.stat-val,.page-card-title,.table-head-title,.login-title,.fleet-title{font-family:var(--head)}

/* Sidebar SÁNG — đồng bộ với logo/brand: nền trắng ngả xanh nhạt, chữ navy, mục chọn teal. */
.sidebar{background:linear-gradient(180deg,#FBFDFF,#EEF4FA);border-right:1px solid var(--border);color:#4B5B78}
.sidebar-logo{border-bottom:1px solid var(--border)}
/* Logo = ảnh /public/logo.png (nền trong suốt) đặt trong ô bo góc trắng, viền + bóng nhẹ. */
.logo-mark{background:#fff;box-shadow:0 3px 12px -6px rgba(228,87,78,.30);border:1px solid rgba(228,87,78,.14);overflow:hidden;padding:2px}
.logo-mark img,.logo-mark svg{width:100%;height:100%;object-fit:contain;display:block}
.logo-mark-lg{width:50px;height:50px;border-radius:13px}
.login-brand-sub{font-size:12px;color:var(--text3);font-weight:500;margin-top:2px}
.logo-name{color:var(--text)}.logo-url{color:var(--text3)}
.nav-section{color:#93A0B8}
.nav-item{color:#4B5B78;border-left:none;border-radius:10px;margin:2px 8px;padding:10px 11px;gap:12px;transition:all .15s var(--ease)}
a.nav-item{text-decoration:none}
.nav-ext{font-size:13px;opacity:.5}
.nav-item:hover{background:#E7EFF7;color:var(--text)}
.nav-item.active{background:var(--blue-bg);color:var(--blue);font-weight:600}
.nav-item svg{width:18px;height:18px;flex:none;opacity:.75}
.nav-item.active svg{opacity:1;stroke:var(--blue)}
.nav-count{margin-left:auto;font-size:12px;font-family:'JetBrains Mono';background:var(--blue-bg);padding:1px 7px;border-radius:20px;color:var(--blue)}
.sidebar-user{border-top:1px solid var(--border)}
.user-avatar{background:var(--blue-bg);border:1px solid var(--blue-border);color:var(--blue)}
.user-name{color:var(--text)}.user-role{color:var(--text3)}

/* Topbar sáng, kính mờ */
.topbar{position:sticky;top:0;z-index:30;background:rgba(238,241,247,.82);backdrop-filter:blur(10px)}
.topbar-title{font-size:20px}
.topbar-sub{font-size:13.5px;color:var(--text2);margin-top:1px}
.search-box input:focus{box-shadow:0 0 0 3px var(--blue-bg)}
.conn{display:inline-flex;align-items:center;gap:7px;font-size:13px;font-weight:600;color:#0a8e63;background:var(--green-bg);padding:7px 12px;border-radius:20px;white-space:nowrap}
.conn .cdot{width:8px;height:8px;border-radius:50%;background:var(--green);animation:pulseDot 2.2s infinite}
.bell-wrap{position:relative}
.bell-btn{position:relative;background:#fff;border:1px solid var(--border);width:38px;height:38px;border-radius:10px;cursor:pointer;font-size:17px;line-height:1;transition:all .15s}
.bell-btn:hover{border-color:var(--border2);background:var(--bg3)}
.bell-badge{position:absolute;top:-5px;right:-5px;background:var(--red);color:#fff;font-size:11px;font-weight:700;min-width:17px;height:17px;border-radius:9px;display:grid;place-items:center;padding:0 4px;font-family:'JetBrains Mono'}
.bell-backdrop{position:fixed;inset:0;z-index:40}
.bell-menu{position:absolute;right:0;top:46px;z-index:41;width:300px;max-width:82vw;background:#fff;border:1px solid var(--border);border-radius:12px;box-shadow:var(--shadow-lg);overflow:hidden;animation:fadeUp .18s var(--ease) backwards}
.bell-head{padding:11px 14px;font-weight:600;font-size:14px;border-bottom:1px solid var(--border);font-family:var(--head)}
.bell-empty{padding:18px 14px;font-size:14px;color:var(--text3);text-align:center}
.bell-item{display:flex;align-items:center;gap:10px;padding:10px 14px;cursor:pointer;border-bottom:1px solid var(--border)}
.bell-item:last-child{border-bottom:none}.bell-item:hover{background:var(--bg3)}
.bell-name{font-weight:600;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bell-sub{font-size:12px;color:var(--text3)}
.pd-red{width:8px;height:8px;border-radius:50%;background:var(--red);flex:none;animation:pulseDot 2s infinite}
.pd-amber{width:8px;height:8px;border-radius:50%;background:var(--amber);flex:none;animation:pulseDot 2s infinite}
.audit-row{display:flex;gap:11px;align-items:flex-start;padding:11px 4px;border-bottom:1px solid var(--border)}
.audit-row:last-child{border-bottom:none}
.audit-row:hover{background:var(--bg3)}
.audit-ico{font-size:17px;line-height:1.4;flex:none}
.audit-line{font-size:14px;color:var(--text);line-height:1.5}
.audit-chip{display:inline-block;font-size:11px;font-weight:600;background:var(--bg4);color:var(--text2);padding:1px 7px;border-radius:20px;margin:0 2px}
.audit-sub{font-size:12px;color:var(--text3);font-family:'JetBrains Mono';margin-top:2px}

/* Buttons */
.btn{transition:all .16s var(--ease)}
.btn:hover{background:var(--bg4);color:var(--text);border-color:var(--border2)}
.btn:active{transform:translateY(1px)}
.btn:disabled:hover{transform:none}
.btn.primary{background:var(--grad);border-color:transparent;color:#fff}
.btn.primary:hover{background:var(--grad);box-shadow:0 8px 18px -6px rgba(228,87,78,.50);color:#fff}
.act-btn{transition:all .16s var(--ease)}.act-btn:hover{border-color:var(--border2)}
.btn:focus-visible,.act-btn:focus-visible,.icon-btn:focus-visible,input:focus-visible,select:focus-visible,.chip:focus-visible{outline:2px solid var(--blue-border);outline-offset:1px}

/* Chip lọc (bo tròn, "on" = mực đậm) */
.chip{border:1px solid var(--border);background:#fff;color:var(--text2);padding:6px 14px;border-radius:20px;font-size:13.5px;font-weight:500;cursor:pointer;font-family:inherit;transition:.15s;white-space:nowrap}
.chip:hover{border-color:var(--border2);color:var(--text)}
.chip.on{background:var(--ink);color:#fff;border-color:var(--ink)}

/* Stat cards + icon badge */
.stat-card{position:relative;box-shadow:var(--shadow);animation:fadeUp .5s var(--ease) backwards;transition:transform .2s var(--ease),box-shadow .2s,border-color .2s}
.stat-card:hover{transform:translateY(-3px);box-shadow:var(--shadow-lg);border-color:var(--border2)}
.stats .stat-card:nth-child(2){animation-delay:.06s}.stats .stat-card:nth-child(3){animation-delay:.12s}.stats .stat-card:nth-child(4){animation-delay:.18s}
.stat-top{display:flex;align-items:center;justify-content:space-between;color:var(--text2);font-size:13.5px;font-weight:500}
.stat-ico{width:32px;height:32px;border-radius:9px;display:grid;place-items:center;font-size:17px}
.stat-ico.blue{background:var(--blue-bg)}.stat-ico.green{background:var(--green-bg)}.stat-ico.amber{background:var(--amber-bg)}.stat-ico.red{background:var(--red-bg)}
.stat-val{font-size:31px;font-weight:700;letter-spacing:-.5px;margin-top:8px}
.stat-sub{font-size:12.5px;color:var(--text3);margin-top:2px}
.stat-sub.up{color:#0a8e63}.stat-sub.down{color:var(--red)}

/* Fleet health bar */
.fleet{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg);padding:18px 20px;margin:0 20px 16px;box-shadow:var(--shadow);animation:fadeUp .5s var(--ease) backwards;animation-delay:.12s}
.fleet-title{font-size:15.5px;font-weight:600}
.fleet-cap{color:var(--text2);font-size:13px;margin:2px 0 14px}
.fleet-bar{height:16px;border-radius:8px;overflow:hidden;display:flex;background:var(--border)}
.fleet-bar span{display:block;height:100%;transform-origin:left;animation:growBar .8s var(--ease) backwards;animation-delay:.2s}
.fleet-legend{display:flex;gap:22px;margin-top:14px;flex-wrap:wrap}
.fleet-legend div{display:flex;align-items:center;gap:8px;font-size:13.5px;color:var(--text2)}
.fleet-legend b{color:var(--text);font-family:'JetBrains Mono'}
.sw{width:10px;height:10px;border-radius:3px;flex:none}

/* Table: card, avatar, pill, battery */
.table-card,.page-card{box-shadow:var(--shadow);animation:fadeUp .45s var(--ease) backwards}
thead th{background:var(--bg3)}
tbody td{transition:background .12s}
tbody tr:hover td{background:var(--bg3)}
.dev{display:flex;align-items:center;gap:11px}
.ph{width:30px;height:42px;border-radius:8px;border:1.6px solid var(--border2);position:relative;flex:none;background:linear-gradient(160deg,#EDF1FB,#F9FBFF);box-shadow:inset 0 0 0 2px #fff}
.ph::before{content:"";position:absolute;left:50%;top:4px;transform:translateX(-50%);width:8px;height:2px;border-radius:2px;background:var(--border2)}
.ph::after{content:"";position:absolute;left:50%;bottom:3px;transform:translateX(-50%);width:9px;height:2px;border-radius:2px;background:var(--border2)}
/* Khung có ảnh máy thật (Apple CDN): bỏ nền/viền/notch giả, ảnh phủ đầy bằng object-fit:cover */
.ph-img{overflow:hidden;padding:0}
.ph-img::before,.ph-img::after{display:none}
.ph-img img{width:100%;height:100%;object-fit:cover;display:block}
.ph.ph-img{border:none;background:none;box-shadow:none}
.dev-drawer .modal-icon.ph-img{border:none;background:none}
.dev-name{font-weight:600;font-size:14px}
.dev-serial{color:var(--text2);font-family:'JetBrains Mono';font-size:12px}
.dev-model{color:var(--text3);font-size:12px;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.pill{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:20px;font-size:12.5px;font-weight:600}
.pill .pd{width:7px;height:7px;border-radius:50%}
.pill.on{background:var(--green-bg);color:#0a8e63}.pill.on .pd{background:var(--green);animation:pulseDot 2.4s infinite}
.pill.off{background:var(--bg4);color:#7a8199}.pill.off .pd{background:#9aa2ba}
.pill.warn{background:var(--amber-bg);color:#b07610}.pill.warn .pd{background:var(--amber)}
.pill.danger{background:var(--red-bg);color:#c02b3a}.pill.danger .pd{background:var(--red)}
.batt{display:flex;align-items:center;gap:8px}
.batt .track{width:46px;height:6px;border-radius:4px;background:var(--border);overflow:hidden;flex:none}
.batt .fill{height:100%;border-radius:4px;transform-origin:left;animation:growBar .7s var(--ease) backwards;animation-delay:.15s}

/* ── Thẻ thiết bị (mobile) — ẩn ở desktop, hiện <=768px thay cho bảng ── */
.dev-cards{display:none}
.dev-cards-empty{text-align:center;padding:36px 12px;color:var(--text3);font-size:14px;background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg)}
.dev-card{display:flex;align-items:flex-start;gap:12px;width:100%;text-align:left;background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg);padding:12px;cursor:pointer;font-family:inherit;color:var(--text);transition:border-color .15s,box-shadow .15s;-webkit-tap-highlight-color:transparent}
.dev-card:hover,.dev-card:active{border-color:var(--blue-border);box-shadow:0 4px 14px rgba(28,143,204,.12)}
.dev-card-ph{width:56px!important;height:56px!important;border-radius:12px!important;flex:none}
.dev-card-body{flex:1;min-width:0;display:flex;flex-direction:column;gap:3px}
.dev-card-line1{display:flex;align-items:center;justify-content:space-between;gap:8px}
.dev-card-name{font-weight:600;font-size:16px;font-family:var(--head);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0}
.dev-card-serial{font-size:12px;color:var(--text3)}
.dev-card-owner{font-size:13.5px;color:var(--text2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.dev-card-store{color:var(--text3)}
.dev-card-money{display:flex;flex-wrap:wrap;gap:8px;align-items:baseline;font-size:14px;font-weight:600;color:var(--text);margin-top:1px}
.dev-card-money i{font-style:normal;font-weight:400;font-size:12px;color:var(--text3)}
.dev-card-money .rem{color:var(--blue)}
.dev-card-money .day{font-weight:500;font-size:12px;color:var(--text3)}
.dev-card-badges{display:flex;flex-wrap:wrap;gap:5px;margin-top:4px}
.dev-card-badges:empty{display:none}
.dev-card-badges .pill{padding:2px 8px;font-size:11.5px}
.dev-card-chev{color:var(--text3);font-size:25px;line-height:1;align-self:center;flex:none}
/* Thẻ người dùng (mobile) — dùng trong .dev-cards của tab Người dùng */
.user-card{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius-lg);padding:12px;display:flex;flex-direction:column;gap:8px}
.user-card-head{display:flex;align-items:flex-start;justify-content:space-between;gap:8px}
.user-card-name{font-weight:600;font-size:14px;word-break:break-all}
.user-card-sub{font-size:12px;color:var(--text2)}
.user-card-meta{display:flex;flex-direction:column;gap:5px;font-size:13px;color:var(--text)}
.user-card-meta .ucl{display:inline-block;min-width:66px;color:var(--text3);font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.03em;margin-right:4px}
.user-card-actions{display:flex;flex-wrap:wrap;gap:6px;padding-top:8px;border-top:1px dashed var(--border)}
/* Lịch sử máy đã quản lý: danh sách thẻ hiển thị ở MỌI cỡ màn (tái dùng .dev-card) */
.fin-list{display:flex;flex-direction:column;gap:10px}
@media(max-width:560px){.fin-list .dev-card{flex-wrap:wrap}.fin-list .dev-card .btn{flex:1 1 100%;justify-content:center;margin-top:2px}}
/* Thẻ Kho (mobile): hàng nút "Kéo về cửa hàng" + chọn cửa hàng dưới thông tin máy */
.kho-card-actions{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
.kho-card-actions select{flex:1 1 120px;min-width:0;height:34px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg3);color:var(--text);font-size:13px;font-family:inherit;padding:0 8px;outline:none}
.kho-card-actions .btn{flex:1 1 auto;justify-content:center}
@media(max-width:768px){
  .dev-table{display:none}
  .dev-cards{display:flex;flex-direction:column;gap:10px}
  /* Bỏ khung card bao ngoài để mỗi thẻ thiết bị là 1 card độc lập */
  .table-card{background:transparent;border:none;border-radius:0;overflow:visible}
  .table-card .table-head{padding:2px 2px 12px;border-bottom:none}
}

.badge{transition:all .15s}
.dot-green{animation:pulseDot 2.4s infinite}
.overlay{animation:fadeIn .2s ease;backdrop-filter:blur(3px);background:rgba(18,21,43,.34)}
.modal{box-shadow:var(--shadow-lg);animation:scaleIn .28s var(--ease)}
/* Màn đăng nhập: ảnh /bg.jpg full (veil nhẹ hơn để khoe ảnh); thẻ login nổi ở giữa. */
.login-wrap{background:
  linear-gradient(rgba(255,255,255,.28),rgba(255,255,255,.42)),
  url('/bg.jpg') center center/cover no-repeat,
  var(--bg)}
.login-card{box-shadow:var(--shadow-lg);animation:scaleIn .4s var(--ease)}
.spinner{width:15px;height:15px;border:2px solid var(--border);border-top-color:var(--blue);border-radius:50%;display:inline-block;animation:spin .7s linear infinite;vertical-align:-2px}
.skeleton{background:linear-gradient(90deg,var(--bg3) 25%,var(--bg4) 37%,var(--bg3) 63%);background-size:200% 100%;animation:shimmer 1.3s infinite;border-radius:6px;color:transparent}
/* MOBILE OVERRIDES — đặt CUỐI để thắng các rule "enhanced" (.fleet/.stat-* ở trên
   set margin/size theo desktop, đứng sau media block giữa nên đè mất). Ở đây căn
   .fleet cùng lề 12px với .stats (hết lệch padding) + thu gọn stats/fleet. */
@media (max-width:768px){
.stats{padding:12px 12px 0;gap:8px}
.stat-card{padding:9px 11px}
.stat-top{font-size:12px}
.stat-ico{width:24px;height:24px;font-size:14px;border-radius:7px}
.stat-val{font-size:20px;margin-top:3px}
.stat-sub{font-size:11px;margin-top:1px}
.fleet{padding:12px 14px;margin:12px}
.fleet-title{font-size:14px}
.fleet-cap{font-size:12px;margin:2px 0 10px}
.fleet-bar{height:12px}
.fleet-legend{gap:14px;margin-top:10px}
.fleet-legend div{font-size:12.5px}
}
@media (max-width:400px){
.stat-card{padding:8px 10px}
.stat-val{font-size:19px}
.stat-ico{width:22px;height:22px;font-size:13px}
}

@media (prefers-reduced-motion:reduce){*,*::before,*::after{animation-duration:.001ms!important;animation-iteration-count:1!important;transition-duration:.001ms!important}}
`;
